import { isDtoolsSiQuoteLocked, isQuoteDraftEditable } from '@/lib/api/dtools-si'
import { clampUsedInventoryDesignation } from '@/lib/inventory-utils'
import { applyStoredChangeOrderTotals, applyStoredTotals, shouldUseStoredTotals } from '@/lib/stored-totals'
import { changeOrderSourceHasProduct } from '@/lib/api/summary-row-guard'
import { getLaborDiscountLineAmount, isLegacyDiscountChain } from '@/lib/discount-line-policy'
import { toast } from '@/lib/hooks/use-toast'
import { trackException } from '@/lib/telemetry'
import { useIsAuthenticated } from '@/lib/msal-config'
import { isTemporaryQuoteDetailId, replaceTemporaryQuoteDetailInCache } from '@/lib/product-create'
import { PREVIOUS_SNAPSHOT_QUERY_OPTIONS, queryClient } from '@/lib/query-client'
import { resolveInitialQuoteTaxCodeId, wasQuoteTaxCodeManuallyCleared } from '@/lib/quote-tax-policy'
import { isRoomVisibleUnderFilter } from '@/lib/quote-room-visibility'
import { useAddAccessoriesDialogStore, useViewState } from '@/lib/store'
import {
  FieldServiceProductType,
  ProductStatusReason,
  QuoteDetailFilters,
  QuoteSaleType,
  QuoteType,
  UpsertQuoteDetailBody,
} from '@/lib/types'
import {
  DEFAULT_SUPPLIES_COST_RATE,
  DEFAULT_SUPPLIES_PRICE_RATE,
  DEFAULT_WIRE_LOOP_LENGTH,
  DEFAULT_WIRE_RUN_LENGTH,
  getMarginPercentage,
  getQuoteId,
  getUniqueQueryKeys,
  hasInheritedBucket,
  isLaborSummaryDetail,
  isCatalogPackage,
  isLineItem,
  isPackageHeader,
  isProduct,
  isScopeOfWork,
  isSummaryDetail,
  isWireLoop,
  isWireRun,
  LABOR_SUMMARY_NAME_TO_KEY_MAP,
  LABOR_SUMMARY_PRODUCT_ID_MAP,
  LABOR_SUMMARY_PRODUCT_NAMES,
  PRODUCT_ID_MAP,
  PRODUCT_REQUIREMENT,
  sortQuoteDetails,
  SUMMARY_PRODUCT_ID_MAP,
} from '@/lib/utils'
import { isNoChargeQuoteDetail } from '@/lib/no-charge-line-policy'
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Decimal } from 'decimal.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { useAccountQuery } from './accounts'
import {
  deleteRecord,
  getMultipleRecords,
  getRecord,
  getTaxDecimal,
  GlobalOptionSetDefinition,
  updateRecord,
} from './helpers'
import { encodeODataLiteral, PriceListItemData, priceListItemSchema, productSchema, useProductsQuery } from './products'
import { QUERY_KEYS } from './query-keys'
import { createQuoteDetail, type CreateQuoteDetailBody } from './quote-detail-create'
import { assertInitializedQuoteProducts } from './quote-initialization'
import { isAlreadyDeletedError, pendingDeletedRows, withoutDeletedRows } from './quote-detail-delete'
import { applyQuoteDetailReorderInChunks, restoreAndRefetchQuoteDetailCaches } from './quote-detail-reorder'
import {
  QuoteData,
  useCPQVersion,
  useIsServiceQuoteOrChangeOrder,
  useOriginalQuoteQuery,
  useQuoteQuery,
} from './quotes'
import {
  AccountRoomData,
  getOtherScopeTagValueForSystem,
  getPhaseLabel,
  getRoomGroupIds,
  getRoomLabel,
  getSystemLabel,
  getSystemScopeTagLabel,
  resolveScopeTagForSystem,
  useAccountRoomsQuery,
  useSystemsQuery,
  useSystemScopeTagsQuery,
} from './rooms-and-systems'

export const cpqFieldsSchema = z.object({
  dsy_lineitemcpqfieldsid: z.string({ message: 'CPQ fields record ID is missing on line item.' }),
  dsy_globalid: z.string({ message: 'Global ID is missing on line item CPQ fields.' }),
  dsy_parentglobalid: z.string().nullable(),
  // Package membership -> the package header row's dsy_globalid. Orthogonal to
  // dsy_parentglobalid (accessory link); a line is one or the other, never both.
  // nullish, not nullable: fetch sites with explicit cpqfields $selects predate
  // this column, and a missing required key would fail the whole parse.
  dsy_packageglobalid: z.string().nullish(),
  dsy_phase: z.number().nullable(),
  _dsy_room_value: z.string().nullable(),
  dsy_system: z.number().nullable(),
  dsy_scopetag: z.number().nullable(),
  dsy_grouporder: z.number().nullable(),
  dsy_location: z.string().nullable(),
  dsy_engineeringlabor: z.number().nullable(),
  dsy_installationlabor: z.number().nullable(),
  dsy_roughinlabor: z.number().nullable(),
  dsy_programminglabor: z.number().nullable(),
  dsy_projectmanagementlabor: z.number().nullable(),
  dsy_servicelabor: z.number().nullable(),
  dsy_laborfactorpercent: z.number().nullable(),
  // Truck-stock designation (service quotes). nullish, not nullable: several
  // fetch sites use explicit cpqfields $selects that omit these fields, and a
  // missing required key would fail the whole parse.
  dsy_usedinventoryquantity: z.number().nullish(),
  dsy_usedinventorylocation: z.number().nullish(),
  dsy_usedinventorylocationname: z.string().nullish(),
  dsy_usedinventorybin: z.number().nullish(),
  dsy_usedinventorybinname: z.string().nullish(),
  dsy_usedinventoryfulfillmentid: z.string().nullish(),
})

export type CPQFieldsData = z.infer<typeof cpqFieldsSchema>

export type UpsertCPQFieldsBody = Partial<
  Omit<CPQFieldsData, 'dsy_lineitemcpqfieldsid' | '_dsy_room_value'> & {
    'dsy_room@odata.bind': string | null
  }
> & {
  // Production schema removed this quote lookup. Keep tombstones even while
  // dev metadata still exposes it so widened objects cannot reintroduce it.
  dsy_quote?: never
  'dsy_quote@odata.bind'?: never
  _dsy_quote_value?: never
}

export const quoteDetailSchema = z.object({
  quotedetailid: z.string({ message: 'Quote detail ID is missing.' }),
  quotedetailname: z.string().nullish().transform((name) => name ?? ''),
  description: z.string().nullable(),
  priceperunit: z.number().nullable(),
  msdyn_costpriceperunit: z.number().optional().nullable(),
  msdyn_costamount: z.number().optional().nullable(),
  quantity: z.number().catch(0),
  dsy_cpqfields: cpqFieldsSchema,
  productid: productSchema,
  tax: z.number().nullable(),
  msdyn_taxable: z.boolean().nullable(),
  manualdiscountamount: z.number().nullable(),
}).refine((detail) => !isPackageHeader(detail.productid) || detail.quotedetailname.trim().length > 0, {
  message: 'Package name is missing.', path: ['quotedetailname'],
})

export type QuoteDetailData = z.infer<typeof quoteDetailSchema>

const storedSuppliesDetailSchema = z.object({
  quotedetailid: z.string(),
  priceperunit: z.number().nullable(),
  msdyn_costpriceperunit: z.number().optional().nullable(),
  msdyn_costamount: z.number().optional().nullable(),
  quantity: z.number().catch(0),
})

export type StoredSuppliesDetailData = z.infer<typeof storedSuppliesDetailSchema>

export { createQuoteDetail, type CreateQuoteDetailBody } from './quote-detail-create'

const QUOTE_DETAIL_SELECT =
  '?$select=quotedetailid,quotedetailname,description,priceperunit,msdyn_costpriceperunit,msdyn_costamount,quantity,tax,msdyn_taxable,manualdiscountamount'

const QUOTE_DETAIL_EXPAND =
  '&$expand=dsy_cpqfields($select=dsy_lineitemcpqfieldsid,dsy_globalid,dsy_parentglobalid,dsy_packageglobalid,dsy_phase,_dsy_room_value,dsy_system,dsy_scopetag,dsy_grouporder,dsy_location,dsy_engineeringlabor,dsy_installationlabor,dsy_roughinlabor,dsy_programminglabor,dsy_projectmanagementlabor,dsy_servicelabor,dsy_laborfactorpercent,dsy_usedinventoryquantity,dsy_usedinventorylocation,dsy_usedinventorylocationname,dsy_usedinventorybin,dsy_usedinventorybinname,dsy_usedinventoryfulfillmentid),productid($select=productid,name,msdyn_fieldserviceproducttype,dsy_defaultphase,entityimage,_defaultuomid_value,dsy_partnumber,statuscode,dsy_weight,dsy_subcategory,dsy_vesapattern,dsy_ispackage)'

const QUOTE_DETAIL_OPTIONS = QUOTE_DETAIL_SELECT + QUOTE_DETAIL_EXPAND

// Real-time cost math derives from the unit rate — msdyn_costamount is a
// display/data-store field the server keeps in sync, never a calculation input.
function getExtendedCostDecimal(detail: QuoteDetailData, quantity: Decimal) {
  return new Decimal(detail.msdyn_costpriceperunit ?? 0).toDecimalPlaces(2).mul(quantity).toDecimalPlaces(2)
}

export async function getQuoteDetails(params: {
  quote: QuoteData
  filters?: QuoteDetailFilters
  signal?: AbortSignal
}): Promise<QuoteDetailData[]> {
  // $select
  let options = QUOTE_DETAIL_OPTIONS

  // $filter
  options += `&$filter=_quoteid_value eq '${params.quote.quoteid}'`

  if (params?.filters?.onlySummaryDetails) {
    const summaryDetailValues = Object.values(SUMMARY_PRODUCT_ID_MAP)
      .map((id) => `'${id}'`)
      .join(',')
    options += ` and Microsoft.Dynamics.CRM.In(PropertyName='productid',PropertyValues=[${summaryDetailValues}])`
  } else {
    const roomId =
      params?.filters?.roomId || params?.filters?.quoteDetail?.quoteDetail?.dsy_cpqfields._dsy_room_value || null
    if (roomId?.startsWith('temp-')) {
      return [] // Room not yet persisted — no details can exist
    }
    if (roomId) {
      options += ` and dsy_cpqfields/_dsy_room_value eq '${roomId}'`
    }

    const systemId = params?.filters?.systemId || params?.filters?.quoteDetail?.quoteDetail?.dsy_cpqfields.dsy_system
    if (systemId) {
      options += ` and dsy_cpqfields/dsy_system eq ${systemId}`
    } else if (params?.filters?.unassignedSystem) {
      options += ' and dsy_cpqfields/dsy_system eq null'
    }
  }

  // No $orderby — Dataverse rejects $orderby on expanded properties; client-side
  // sortQuoteDetails is the canonical sort source.

  // A refetch (the totals save's proposal-data-changed, a plugin invalidation)
  // can read rows a quote-line delete is still removing and put them back on
  // screen. Drop rows held by a delete at either end of the read; the last
  // delete's onSettled refetches server truth.
  const deletingAtStart = params.filters?.previousQuote ? [] : pendingDeletedRows(queryClient)
  const quoteDetails = await getMultipleRecords<QuoteDetailData>('quotedetails', options, { signal: params?.signal })

  // const invalidQuoteDetails = quoteDetails.filter((detail) => !quoteDetailSchema.safeParse(detail).success)
  // if (invalidQuoteDetails.length > 0 && !params?.filters?.previousQuote && isQuoteDraftEditable(quote)) {
  //   deleteQuoteDetails(invalidQuoteDetails)
  // }

  const results = quoteDetails.map((detail) => quoteDetailSchema.safeParse(detail))
  const valid = results.filter((r): r is z.SafeParseSuccess<QuoteDetailData> => r.success).map((r) => r.data)
  if (valid.length < quoteDetails.length) {
    trackException(
      new Error(`${quoteDetails.length - valid.length} quote detail(s) failed schema validation and were excluded`),
      {
        context: 'quote-details.fetchQuoteDetails',
        totalRecords: String(quoteDetails.length),
        droppedRecords: String(quoteDetails.length - valid.length),
      }
    )
  }
  if (params.filters?.previousQuote) return valid
  return withoutDeletedRows(valid, [...deletingAtStart, ...pendingDeletedRows(queryClient)])
}

/**
 * The rows a package's derived total sums: the header itself plus its
 * transitive member set (members, and those members' accessories). The header
 * is $0, so summing this set IS the package total — no stored roll-up, and
 * nothing else in the summary engine changes.
 *
 * Exported so the summary path and its tests share one predicate.
 */
export function selectPackageSummarySet(data: QuoteDetailData[], packageHeaderGlobalId: string) {
  const memberGlobalIds = new Set(
    data
      .filter(
        (detail) =>
          !detail.dsy_cpqfields.dsy_parentglobalid &&
          detail.dsy_cpqfields.dsy_packageglobalid === packageHeaderGlobalId
      )
      .map((detail) => detail.dsy_cpqfields.dsy_globalid)
      .filter(Boolean)
  )
  return data.filter((detail) => {
    const { dsy_globalid, dsy_parentglobalid, dsy_packageglobalid } = detail.dsy_cpqfields
    if (dsy_globalid === packageHeaderGlobalId) return true
    if (dsy_parentglobalid) return memberGlobalIds.has(dsy_parentglobalid)
    return dsy_packageglobalid === packageHeaderGlobalId
  })
}

/**
 * The filters `getQuoteDetails` cannot push to Dataverse — applied in the
 * query's `select` layer instead. Room-group reads (`useRoomGroupDetails`) run
 * the same function over the base cache entry so both paths narrow identically.
 */
export type QuoteDetailSelectFilters = {
  laborKey?: keyof typeof LABOR_SUMMARY_NAME_TO_KEY_MAP | null
  quoteDetail?: {
    quoteDetail: QuoteDetailData
    includeAccessories: boolean
    unitOnly?: boolean
    includeMembers?: boolean
  } | null
  search?: string | null
  onlySummaryDetails?: 'all' | 'labor' | null
}

export function applyQuoteDetailSelectFilters(
  data: QuoteDetailData[],
  filters: QuoteDetailSelectFilters
): QuoteDetailData[] {
  // Return all details if no non-api filters
  if (!filters.laborKey && !filters.quoteDetail && !filters.search && filters.onlySummaryDetails !== 'labor') {
    return data
  }

  // A package header's own summary widens to its transitive member set — the
  // single-detail find below would return the $0 header alone.
  const packageHeaderGlobalId = filters.quoteDetail?.includeMembers
    ? filters.quoteDetail.quoteDetail.dsy_cpqfields.dsy_globalid
    : null
  if (packageHeaderGlobalId) {
    return selectPackageSummarySet(data, packageHeaderGlobalId)
  }

  // Use find if filter for 1 quote detail
  // Must use global ID for change orders
  if (
    filters.quoteDetail &&
    filters.quoteDetail.quoteDetail.dsy_cpqfields.dsy_globalid &&
    !filters.quoteDetail.includeAccessories
  ) {
    const detail = data.find(
      (detail) => detail.dsy_cpqfields.dsy_globalid === filters.quoteDetail!.quoteDetail.dsy_cpqfields.dsy_globalid
    )
    if (detail) {
      return [detail]
    } else {
      return []
    }
  }

  // Combine rest of filters in a single loop
  return data.filter((detail) => {
    let matches = true

    if (filters.laborKey) {
      matches = matches && (detail.dsy_cpqfields[LABOR_SUMMARY_NAME_TO_KEY_MAP[filters.laborKey]] ?? 0) > 0
    }

    if (filters.quoteDetail?.includeAccessories) {
      const isMain = detail.dsy_cpqfields.dsy_globalid === filters.quoteDetail.quoteDetail.dsy_cpqfields.dsy_globalid
      const isAccessory =
        !!detail.dsy_cpqfields.dsy_parentglobalid &&
        detail.dsy_cpqfields.dsy_parentglobalid === filters.quoteDetail.quoteDetail.dsy_cpqfields.dsy_globalid

      matches = matches && (isMain || isAccessory)
    }

    if (filters.search) {
      const lowerSearch = filters.search.trim().toLowerCase()
      matches =
        matches &&
        // The name the row shows: a package header's is its own, every other
        // line's is its product's — a stored line name can be stale.
        ((isPackageHeader(detail.productid) ? detail.quotedetailname : detail.productid.name)
          .toLowerCase()
          .includes(lowerSearch) ||
          detail.description?.toLowerCase().includes(lowerSearch) ||
          detail.productid.dsy_partnumber?.toLowerCase().includes(lowerSearch) ||
          false)
    }

    if (filters.onlySummaryDetails === 'labor') {
      matches = matches && isLaborSummaryDetail(detail.productid.productid)
    }

    return matches
  })
}

/**
 * Client-side equivalent of `getQuoteDetails`' server `$filter` for a room
 * group: the room plus its direct children, then the same system /
 * unassigned-system narrowing the exact-room path pushes to Dataverse.
 * Summary details carry no room, so they fall out here exactly as they do
 * server-side.
 */
export function filterQuoteDetailsToRoomGroup(
  data: QuoteDetailData[],
  {
    roomIds,
    systemId,
    unassignedSystem,
  }: { roomIds: string[]; systemId?: number | null; unassignedSystem?: boolean }
): QuoteDetailData[] {
  const roomIdSet = new Set(roomIds)
  return data.filter((detail) => {
    const roomId = detail.dsy_cpqfields._dsy_room_value
    if (!roomId || !roomIdSet.has(roomId)) return false
    if (systemId) return detail.dsy_cpqfields.dsy_system === systemId
    if (unassignedSystem) return detail.dsy_cpqfields.dsy_system == null
    return true
  })
}

export function useQuoteDetailsQuery(filters?: QuoteDetailFilters) {
  const { data: quote } = useQuoteQuery({ previousQuote: filters?.previousQuote })

  return useQuery({
    queryKey: QUERY_KEYS.quoteDetails(filters),
    enabled: !!quote,
    ...(filters?.previousQuote ? PREVIOUS_SNAPSHOT_QUERY_OPTIONS : {}),
    queryFn: ({ signal }) => {
      if (!quote) {
        throw new Error('Quote not found')
      }
      return getQuoteDetails({ quote, filters, signal })
    },
    select: useCallback(
      (data: QuoteDetailData[]) =>
        applyQuoteDetailSelectFilters(data, {
          laborKey: filters?.laborKey,
          quoteDetail: filters?.quoteDetail,
          search: filters?.search,
          onlySummaryDetails: filters?.onlySummaryDetails,
        }),
      [filters?.laborKey, filters?.quoteDetail, filters?.search, filters?.onlySummaryDetails]
    ),
  })
}

/**
 * Room-group details: the exact-room read's data set widened to the room plus
 * its direct child rooms.
 *
 * Reads the **unfiltered base cache entry** (`QUERY_KEYS.quoteDetails({
 * previousQuote })`) — seeded for both the auth and unauth paths by
 * `initializeQuoteDetailsCache`, and invalidated by every line-item mutation's
 * `onSettled` — then narrows client-side. No new server filter, no seeding, no
 * per-child observer: the base entry's existing lifecycle is the freshness
 * contract, and `dataUpdatedAt` is reported straight off it so summary keys can
 * embed it.
 *
 * Inactive (returns `undefined` data) unless `filters.includeChildRooms` and
 * `filters.roomId` are both set, so mounting it on a leaf room costs nothing.
 *
 * `roomIds` is the resolved group (room + direct children) — callers embed it in
 * their own query keys so a re-parent re-derives whatever they computed from it.
 */
export function useRoomGroupDetails(filters?: QuoteDetailFilters) {
  const previousQuote = !!filters?.previousQuote
  const roomId = (filters?.includeChildRooms && filters.roomId) || null
  const systemId = filters?.systemId ?? null
  const unassignedSystem = !!filters?.unassignedSystem
  const laborKey = filters?.laborKey
  const search = filters?.search

  const { data: quote } = useQuoteQuery({ previousQuote })
  const { data: rooms } = useAccountRoomsQuery()

  const { data: baseDetails, dataUpdatedAt } = useQuery({
    queryKey: QUERY_KEYS.quoteDetails({ previousQuote }),
    enabled: !!quote && !!roomId,
    ...(previousQuote ? PREVIOUS_SNAPSHOT_QUERY_OPTIONS : {}),
    queryFn: ({ signal }) => {
      if (!quote) {
        throw new Error('Quote not found')
      }
      return getQuoteDetails({ quote, filters: { previousQuote }, signal })
    },
  })

  // Null until the rooms query has data: a `rooms ?? []` fallback would resolve
  // the group to the parent alone, so the surface would confidently render a
  // parent-only total under a key that claims to be the group's.
  const roomIds = useMemo(() => (roomId && rooms ? getRoomGroupIds(roomId, rooms) : null), [roomId, rooms])

  const data = useMemo(() => {
    if (!roomIds || !baseDetails) return undefined
    return applyQuoteDetailSelectFilters(
      filterQuoteDetailsToRoomGroup(baseDetails, { roomIds, systemId, unassignedSystem }),
      { laborKey, search }
    )
  }, [roomIds, baseDetails, systemId, unassignedSystem, laborKey, search])

  return { data, dataUpdatedAt, roomIds }
}

/**
 * The room read every group-aware surface uses: the exact-room query, widened to
 * the room group when `filters.includeChildRooms` is set on a `roomId`.
 *
 * In group mode the exact-room observer is pointed at the **base entry** — the
 * same key {@link useRoomGroupDetails} reads — so the two observers dedupe
 * instead of firing a redundant room-scoped fetch. `roomIds` is null outside
 * group mode; callers embed it in their own query keys only when it is set, so
 * existing key hashes are untouched.
 */
export function useRoomScopedQuoteDetails(filters?: QuoteDetailFilters | null) {
  const includeChildRooms = !!filters?.includeChildRooms && !!filters?.roomId
  const exactDetails = useQuoteDetailsQuery(
    includeChildRooms ? { previousQuote: filters?.previousQuote } : (filters ?? undefined)
  )
  const groupDetails = useRoomGroupDetails(filters ?? undefined)

  const data = includeChildRooms ? groupDetails.data : exactDetails.data
  const dataUpdatedAt = includeChildRooms ? groupDetails.dataUpdatedAt : exactDetails.dataUpdatedAt
  const roomIds = groupDetails.roomIds

  return useMemo(() => ({ data, dataUpdatedAt, roomIds }), [data, dataUpdatedAt, roomIds])
}

export function useStoredSuppliesDetailsQuery({
  quoteId,
  enabled = true,
}: {
  quoteId?: string | null
  enabled?: boolean
} = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.storedSuppliesDetails(quoteId),
    enabled: enabled && !!quoteId,
    ...PREVIOUS_SNAPSHOT_QUERY_OPTIONS,
    queryFn: async ({ signal }) => {
      if (!quoteId) throw new Error('Quote not found')

      const rows = await getMultipleRecords<StoredSuppliesDetailData>(
        'quotedetails',
        `?$select=quotedetailid,priceperunit,msdyn_costpriceperunit,msdyn_costamount,quantity` +
          `&$filter=_quoteid_value eq '${quoteId}' and _productid_value eq '${PRODUCT_ID_MAP['SUPPLIES']}'`,
        { signal }
      )

      return z.array(storedSuppliesDetailSchema).parse(rows)
    },
  })
}

export function initializeQuoteDetailsCache({
  allQuoteDetails,
  rooms,
  systems,
  previousQuote = false,
}: {
  allQuoteDetails: QuoteDetailData[]
  rooms: AccountRoomData[]
  systems: GlobalOptionSetDefinition
  previousQuote?: boolean
}) {
  queryClient.setQueryData(QUERY_KEYS.quoteDetails({ previousQuote }), allQuoteDetails)

  const roomIds = Array.from(new Set(rooms.map((room) => room.dsy_accountroomid)))
  const systemIds = Array.from(new Set(systems.Options.map((system) => system.Value)))

  const roomDetails = new Map<string, QuoteDetailData[]>(roomIds.map((roomId) => [roomId, []]))
  const systemDetails = new Map<number, QuoteDetailData[]>(systemIds.map((systemId) => [systemId, []]))

  const roomSystems = new Map<string, Set<number>>(roomIds.map((roomId) => [roomId, new Set()]))
  const roomSystemsDetails = new Map<string, QuoteDetailData[]>(roomIds.map((roomId) => [roomId, []]))

  const summaryQuoteDetails = []

  // Single pass through quoteDetails to build all collections efficiently
  for (const detail of allQuoteDetails) {
    if (isSummaryDetail(detail.productid.productid)) {
      summaryQuoteDetails.push(detail)
      continue
    }

    const roomId = detail.dsy_cpqfields._dsy_room_value
    const systemId = detail.dsy_cpqfields.dsy_system

    // Handle room-related collections
    if (roomId) {
      if (!roomDetails.has(roomId)) {
        roomDetails.set(roomId, [])
      }
      roomDetails.get(roomId)!.push(detail)
    }

    // Handle system-related collections
    if (systemId) {
      if (!systemDetails.has(systemId)) {
        systemDetails.set(systemId, [])
      }
      systemDetails.get(systemId)!.push(detail)
    }

    // Handle room+system combinations
    if (roomId && systemId) {
      if (!roomSystems.has(roomId)) {
        roomSystems.set(roomId, new Set())
      }
      roomSystems.get(roomId)!.add(systemId)

      const roomSystemKey = `${roomId}:${systemId}`
      if (!roomSystemsDetails.has(roomSystemKey)) {
        roomSystemsDetails.set(roomSystemKey, [])
      }
      roomSystemsDetails.get(roomSystemKey)!.push(detail)
    }
  }

  // First, prefetch details for each room
  roomIds.forEach((roomId) => {
    queryClient.setQueryData(QUERY_KEYS.quoteDetails({ roomId, previousQuote }), roomDetails.get(roomId))

    // Then for each room+system combination
    roomSystems.get(roomId)?.forEach((systemId: number) => {
      if (!systemId) return

      queryClient.setQueryData(
        QUERY_KEYS.quoteDetails({ roomId, systemId, previousQuote }),
        roomSystemsDetails.get(`${roomId}:${systemId}`)
      )
    })
  })

  systemIds.forEach((systemId) => {
    queryClient.setQueryData(QUERY_KEYS.quoteDetails({ systemId, previousQuote }), systemDetails.get(systemId))
  })

  queryClient.setQueryData(QUERY_KEYS.quoteDetails({ previousQuote, onlySummaryDetails: 'all' }), summaryQuoteDetails)
}

/**
 * Initializes the quote by creating all required summary items (supplies, labor summaries, discount summaries)
 * and setting the tax code if not already set. This should be called during the initial page load
 * to ensure all summary items exist before the plugins try to update them.
 */
export function useInitializeQuote() {
  const isAuthenticated = useIsAuthenticated()
  const queryClient = useQueryClient()

  const { data: quote } = useQuoteQuery()
  const { data: previousQuote } = useQuoteQuery({ previousQuote: true })
  const { data: originalQuote } = useOriginalQuoteQuery()
  const { data: account } = useAccountQuery()
  const { data: quoteDetails } = useQuoteDetailsQuery()
  const { data: previousQuoteDetails } = useQuoteDetailsQuery({ previousQuote: true })
  const { data: summaryProducts } = useProductsQuery({ search: 'SUMMARY:' })
  const { data: discountProducts } = useProductsQuery({ search: 'DISCOUNT:' })
  const { data: suppliesProducts } = useProductsQuery({ productId: PRODUCT_ID_MAP['SUPPLIES'] })
  const { data: rooms } = useAccountRoomsQuery()
  const { data: systems } = useSystemsQuery()

  const [initialized, setInitialized] = useState(false)

  const isDtoolsSiQuote = isDtoolsSiQuoteLocked(quote)
  const isService = useIsServiceQuoteOrChangeOrder()
  const isChangeOrder = quote?.dsy_type === QuoteType.ChangeOrder

  const {
    mutate: initializeQuote,
    isPending: isInitializingQuote,
    error,
  } = useMutation({
    mutationKey: ['initialize-quote'],
    mutationFn: async () => {
      if (
        !quote ||
        (isChangeOrder && !previousQuote) ||
        (isChangeOrder && !originalQuote) ||
        (isChangeOrder && !previousQuoteDetails) ||
        !quoteDetails ||
        !account ||
        !summaryProducts ||
        !discountProducts ||
        !suppliesProducts ||
        !rooms ||
        !systems
      ) {
        throw new Error('Missing required data for quote initialization')
      }

      let invalidateQuote = false
      let invalidateQuoteDetails = false
      const isDraft = isQuoteDraftEditable(quote)

      if (isDraft && !isDtoolsSiQuote) {
        // Apply all first-load quote defaults in one parent write, then fan
        // the independent child writes out together.
        const childWrites: (() => Promise<unknown>)[] = []
        const quoteHeaderPatch: Record<string, unknown> = {}
        const taxCodeToUse = resolveInitialQuoteTaxCodeId({
          quote,
          account,
          previousApprovedQuote: previousQuote,
          quoteTaxCodeManuallyCleared: wasQuoteTaxCodeManuallyCleared(quote.quoteid),
        })
        if (taxCodeToUse && quote.dsy_taxcode?.msdyn_taxcodeid !== taxCodeToUse) {
          quoteHeaderPatch['dsy_taxcode@odata.bind'] = `/msdyn_taxcodes(${taxCodeToUse})`
        }

        if (
          typeof quote.dsy_suppliespricerate !== 'number' ||
          typeof quote.dsy_suppliescostrate !== 'number'
        ) {
          const isTimeAndMaterial = quote.dsy_saletype === QuoteSaleType.TimeAndMaterial
          quoteHeaderPatch.dsy_suppliespricerate = isTimeAndMaterial ? 0 : DEFAULT_SUPPLIES_PRICE_RATE
          quoteHeaderPatch.dsy_suppliescostrate = isTimeAndMaterial ? 0 : DEFAULT_SUPPLIES_COST_RATE

          if (quote.dsy_type === QuoteType.Budget) {
            quoteHeaderPatch.dsy_proposaldisplay_showitemprice = false
            quoteHeaderPatch.dsy_proposaldisplay_defaultexpandedrooms = false
            quoteHeaderPatch.dsy_proposaldisplay_defaultexpandedsystems = false
          }
        }

        if (Object.keys(quoteHeaderPatch).length > 0) {
          invalidateQuote = true
          await updateRecord('quotes', quote.quoteid, quoteHeaderPatch)
        }

        const sourceQuoteHasProduct = (productId: string) =>
          changeOrderSourceHasProduct(productId, isChangeOrder, previousQuoteDetails)

        const suppliesProduct = suppliesProducts[0]
        const existingSuppliesDetail = quoteDetails.find(
          (detail) => detail.productid.productid === PRODUCT_ID_MAP['SUPPLIES']
        )
        if (!existingSuppliesDetail && suppliesProduct && !sourceQuoteHasProduct(PRODUCT_ID_MAP['SUPPLIES'])) {
          invalidateQuoteDetails = true
          childWrites.push(() =>
            createQuoteDetail({
              productName: suppliesProduct.dsy_product.name,
              cpqFieldsBody: {},
              quoteDetailBody: {
                priceperunit: 0,
                tax: 0,
                msdyn_taxable: true,
                quantity: 1,
                msdyn_costpriceperunit: 0,
                quotedetailname: suppliesProduct.dsy_product.name,
                description: suppliesProduct.dsy_description,
                'quoteid@odata.bind': `/quotes(${quote.quoteid})`,
                'productid@odata.bind': `/products(${suppliesProduct.dsy_product.productid})`,
                'uomid@odata.bind': `/uoms(${suppliesProduct.dsy_product._defaultuomid_value})`,
              },
            })
          )
        }

        const laborSummaryProductNames = LABOR_SUMMARY_PRODUCT_NAMES.filter((productName) =>
          !isService ? productName !== 'LABOR SUMMARY:SERVICE' : true
        )
        for (const productName of laborSummaryProductNames) {
          const productId = LABOR_SUMMARY_PRODUCT_ID_MAP[productName]
          const existingLaborDetail = quoteDetails.find((detail) => detail.productid.productid === productId)
          if (existingLaborDetail || sourceQuoteHasProduct(productId)) continue

          const laborProduct = summaryProducts.find((product) => product.dsy_product.productid === productId)
          if (!laborProduct) continue
          invalidateQuoteDetails = true
          childWrites.push(() =>
            createQuoteDetail({
              productName: laborProduct.dsy_product.name,
              cpqFieldsBody: { dsy_laborfactorpercent: 100 },
              quoteDetailBody: {
                quantity: 0,
                msdyn_costpriceperunit: new Decimal(laborProduct.dsy_unitcost ?? 0).toDecimalPlaces(2).toNumber(),
                msdyn_costamount: 0,
                priceperunit: new Decimal(laborProduct.dsy_unitprice ?? 0).toDecimalPlaces(2).toNumber(),
                baseamount: 0,
                quotedetailname: laborProduct.dsy_product.name,
                description: laborProduct.dsy_description,
                msdyn_taxable: laborProduct.dsy_taxable ?? false,
                tax: 0,
                'quoteid@odata.bind': `/quotes(${quote.quoteid})`,
                'productid@odata.bind': `/products(${laborProduct.dsy_product.productid})`,
                'uomid@odata.bind': `/uoms(${laborProduct.dsy_product._defaultuomid_value})`,
              },
            })
          )
        }

        const discountProductIds = [
          PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT'],
          PRODUCT_ID_MAP['DISCOUNT:LABOR'],
          PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT NOT TAXABLE'],
          PRODUCT_ID_MAP['DISCOUNT:LABOR NOT TAXABLE'],
        ]
        for (const productId of discountProductIds) {
          const existingDiscountDetail = quoteDetails.find((detail) => detail.productid.productid === productId)
          if (existingDiscountDetail || sourceQuoteHasProduct(productId)) continue

          const discountProduct = discountProducts.find((product) => product.dsy_product.productid === productId)
          if (!discountProduct) {
            console.warn(`[useInitializeQuote] missing pricelist item for discount product ${productId}`)
            continue
          }
          invalidateQuoteDetails = true
          childWrites.push(() =>
            createQuoteDetail({
              productName: discountProduct.dsy_product.name,
              cpqFieldsBody: {
                dsy_laborfactorpercent: 0,
                dsy_phase: null,
                dsy_system: null,
                'dsy_room@odata.bind': null,
                dsy_scopetag: null,
                dsy_location: null,
                dsy_parentglobalid: null,
                dsy_engineeringlabor: null,
                dsy_installationlabor: null,
                dsy_programminglabor: null,
                dsy_projectmanagementlabor: null,
                dsy_roughinlabor: null,
              },
              quoteDetailBody: {
                manualdiscountamount: 0,
                quantity: 1,
                msdyn_costpriceperunit: 0,
                msdyn_costamount: 0,
                msdyn_taxable: discountProduct.dsy_taxable ?? false,
                priceperunit: 0,
                baseamount: 0,
                tax: 0,
                extendedamount: 0,
                quotedetailname: discountProduct.dsy_product.name,
                description: discountProduct.dsy_description,
                'quoteid@odata.bind': `/quotes(${quote.quoteid})`,
                'productid@odata.bind': `/products(${discountProduct.dsy_product.productid})`,
                'uomid@odata.bind': `/uoms(${discountProduct.dsy_product._defaultuomid_value})`,
              },
            })
          )
        }

        if (!isService) {
          const serviceLaborQuoteDetail = quoteDetails.find(
            (detail) => detail.productid.productid === LABOR_SUMMARY_PRODUCT_ID_MAP['LABOR SUMMARY:SERVICE']
          )
          if (serviceLaborQuoteDetail) {
            invalidateQuoteDetails = true
            childWrites.push(() => deleteQuoteDetails([serviceLaborQuoteDetail]))
          }
          const quoteDetailsWithServiceLabor = quoteDetails.filter(
            (detail) => !!detail.dsy_cpqfields.dsy_servicelabor
          )
          for (const quoteDetail of quoteDetailsWithServiceLabor) {
            invalidateQuoteDetails = true
            childWrites.push(() =>
              updateQuoteDetail({
                quoteDetail,
                cpqFieldsBody: { dsy_servicelabor: 0 },
                quoteDetailBody: {},
              })
            )
          }
        }

        // Every planned write targets its own row — fan out together, wait
        // for all to settle, then surface the first failure so no write is
        // left racing the error path.
        const settled = await Promise.allSettled(childWrites.map((write) => write()))
        const failure = settled.find((result) => result.status === 'rejected')
        if (failure) throw failure.reason
      }

      // 9. Get updated quote details and initialize cache
      if (isChangeOrder && previousQuoteDetails) {
        initializeQuoteDetailsCache({ allQuoteDetails: previousQuoteDetails, rooms, systems, previousQuote: true })
      }

      const finalQuoteDetails = invalidateQuoteDetails
        ? await getQuoteDetails({ quote })
        : quoteDetails
      if (isDraft && !isDtoolsSiQuote) {
        const sourceQuoteHasProduct = (productId: string) =>
          changeOrderSourceHasProduct(productId, isChangeOrder, previousQuoteDetails)
        const expectedProductIds = [
          ...(suppliesProducts[0] ? [PRODUCT_ID_MAP['SUPPLIES']] : []),
          ...LABOR_SUMMARY_PRODUCT_NAMES.filter((productName) =>
            (!isService ? productName !== 'LABOR SUMMARY:SERVICE' : true) &&
            summaryProducts.some(
              (product) => product.dsy_product.productid === LABOR_SUMMARY_PRODUCT_ID_MAP[productName]
            )
          ).map((productName) => LABOR_SUMMARY_PRODUCT_ID_MAP[productName]),
          ...[
            PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT'],
            PRODUCT_ID_MAP['DISCOUNT:LABOR'],
            PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT NOT TAXABLE'],
            PRODUCT_ID_MAP['DISCOUNT:LABOR NOT TAXABLE'],
          ].filter((productId) =>
            discountProducts.some((product) => product.dsy_product.productid === productId)
          ),
        ].filter((productId) => !sourceQuoteHasProduct(productId))
        assertInitializedQuoteProducts(finalQuoteDetails, expectedProductIds)
      }
      initializeQuoteDetailsCache({ allQuoteDetails: finalQuoteDetails, rooms, systems })

      // Invalidate relevant queries to refresh data
      if (invalidateQuote) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quote() })
      }
    },
    onSuccess: () => {
      setInitialized(true)
    },
  })

  useEffect(() => {
    if (
      !isAuthenticated ||
      initialized ||
      isInitializingQuote ||
      error ||
      !quote ||
      (isChangeOrder && !previousQuote) ||
      (isChangeOrder && !originalQuote) ||
      (isChangeOrder && !previousQuoteDetails) ||
      !quoteDetails ||
      !account ||
      !summaryProducts ||
      !discountProducts ||
      !suppliesProducts ||
      !rooms ||
      !systems
    ) {
      return
    }

    if (isDtoolsSiQuote) {
      setInitialized(true)
      return
    }

    initializeQuote()
  }, [
    isAuthenticated,
    initialized,
    isInitializingQuote,
    error,
    quote,
    quoteDetails,
    account,
    summaryProducts,
    discountProducts,
    suppliesProducts,
    initializeQuote,
    rooms,
    systems,
    isChangeOrder,
    previousQuote,
    originalQuote,
    previousQuoteDetails,
    isDtoolsSiQuote,
  ])

  return useMemo(() => ({ initialized, error }), [initialized, error])
}

export function getChildQuoteDetails(
  quoteDetails: QuoteDetailData[] | undefined,
  parentGlobalId: string | null | undefined
) {
  if (!quoteDetails || !parentGlobalId) {
    return []
  }

  return quoteDetails.filter((quoteDetail) => quoteDetail.dsy_cpqfields.dsy_parentglobalid === parentGlobalId)
}

/**
 * A row's accessories, read back server-side. Used only when no cache can
 * answer the question — same reason deletePackage re-reads: a child missed
 * here is a child left behind in the room its parent just moved out of.
 */
export async function fetchChildQuoteDetails(parentGlobalId: string): Promise<QuoteDetailData[]> {
  const rows = await getMultipleRecords<QuoteDetailData>(
    'quotedetails',
    QUOTE_DETAIL_OPTIONS +
      `&$filter=_quoteid_value eq '${getQuoteId()}' and dsy_cpqfields/dsy_parentglobalid eq '${parentGlobalId}'`
  )
  return rows
    .map((row) => quoteDetailSchema.safeParse(row))
    .filter((r): r is z.SafeParseSuccess<QuoteDetailData> => r.success)
    .map((r) => r.data)
}

/**
 * The rows a package header holds, read back server-side. Same last resort as
 * fetchChildQuoteDetails: a member missed here is a member left at the old
 * package quantity. Callers narrow with packageMemberRows — the filter returns
 * the members' accessories too when they carry the package key illegally.
 */
async function fetchPackageQuoteDetails(headerGlobalId: string): Promise<QuoteDetailData[]> {
  const rows = await getMultipleRecords<QuoteDetailData>(
    'quotedetails',
    QUOTE_DETAIL_OPTIONS +
      `&$filter=_quoteid_value eq '${getQuoteId()}' and dsy_cpqfields/dsy_packageglobalid eq '${headerGlobalId}'`
  )
  return rows
    .map((row) => quoteDetailSchema.safeParse(row))
    .filter((r): r is z.SafeParseSuccess<QuoteDetailData> => r.success)
    .map((r) => r.data)
}

export function useChildQuoteDetails({ parentGlobalId }: { parentGlobalId: string | null | undefined }) {
  const { data: quoteDetails } = useQuoteDetailsQuery()
  return useMemo(() => getChildQuoteDetails(quoteDetails, parentGlobalId), [quoteDetails, parentGlobalId])
}

/**
 * Order `roomToSystems` entries by the account rooms list, which
 * `useAccountRoomsQuery` already hands back in canonical DFS pre-order
 * (`select: sortRoomsHierarchically`) — so a child section always follows its
 * parent. Rooms absent from the list (a `temp-` room mid-create, a row deleted
 * between the two reads) keep a stable tail position.
 *
 * The no-filter and room-filter paths seed `roomToSystems` by walking `rooms`,
 * so this is a no-op for them. It only bites where nothing seeds — changes mode
 * — where keys would otherwise land in line-item insertion order.
 *
 * Sort stability is spec-guaranteed (ES2019), which is what keeps equal-ranked
 * unknown rooms in their insertion order.
 */
export function orderRoomEntries<T>(entries: [string, T][], rooms: AccountRoomData[]): [string, T][] {
  const rankByRoomId = new Map(rooms.map((room, index) => [room.dsy_accountroomid, index]))
  const rank = (roomId: string) => rankByRoomId.get(roomId) ?? rooms.length
  return [...entries].sort((a, b) => rank(a[0]) - rank(b[0]))
}

export function useRoomsToSystems(filters?: {
  roomId?: string | null
  systemId?: number | null
  changes?: boolean | null
  search?: string | null
  /** Filter on a parent room selects its whole room group — see `useIsParentRoom`. */
  includeChildRooms?: boolean | null
}) {
  const temporaryRoomToSystems = useViewState((state) => state.temporaryRoomToSystems)
  const { data: quote } = useQuoteQuery()

  // `changes` is not a QuoteDetailFilters member — rebuild the detail filters
  // explicitly rather than leaning on it being ignored downstream.
  const detailFilters = useMemo<QuoteDetailFilters>(
    () => ({
      roomId: filters?.roomId,
      systemId: filters?.systemId,
      search: filters?.search,
      includeChildRooms: !!filters?.includeChildRooms,
    }),
    [filters?.roomId, filters?.systemId, filters?.search, filters?.includeChildRooms]
  )

  const { data: quoteDetails, roomIds: groupRoomIds } = useRoomScopedQuoteDetails(detailFilters)
  const { data: changes } = useQuoteDetailsChanges(detailFilters)
  const { data: rooms, dataUpdatedAt: roomsUpdatedAt } = useAccountRoomsQuery()
  const { data: systems, dataUpdatedAt: systemsUpdatedAt } = useSystemsQuery()

  const isDraft = isQuoteDraftEditable(quote)

  const visibleDetails = useMemo(() => {
    const details = filters?.changes
      ? (changes
          ?.map((change) => change.newQuoteDetail || change.previousQuoteDetail)
          .filter(Boolean) as QuoteDetailData[])
      : quoteDetails

    return details?.filter(
      (quoteDetail) => isLineItem(quoteDetail.productid) || isScopeOfWork(quoteDetail.productid.productid)
    )
  }, [filters?.changes, changes, quoteDetails])

  const detailsDiff = useMemo(() => {
    const uniqueRoomAndSystemIds = new Set<string>()

    for (const visibleDetail of visibleDetails ?? []) {
      if (visibleDetail.dsy_cpqfields._dsy_room_value) {
        uniqueRoomAndSystemIds.add(
          `${visibleDetail.dsy_cpqfields._dsy_room_value}:${visibleDetail.dsy_cpqfields.dsy_system}:${visibleDetail.dsy_cpqfields.dsy_grouporder}`
        )
      }
    }

    return Array.from(uniqueRoomAndSystemIds).sort().join()
  }, [visibleDetails])

  const { data: systemScopeTags } = useSystemScopeTagsQuery()

  const { data, dataUpdatedAt, isLoading } = useQuery<[string, number[]][]>({
    queryKey: [
      'roomsToSystems',
      {
        roomId: filters?.roomId,
        systemId: filters?.systemId,
        changes: filters?.changes,
        temporaryRoomToSystems,
        detailsDiff,
        isDraft,
        // Omitted unless the group read is active, so existing hashes are unchanged.
        ...(filters?.includeChildRooms ? { roomGroupIds: groupRoomIds ?? null } : {}),
      },
      {
        roomsUpdatedAt,
        systemsUpdatedAt,
      },
    ],
    queryFn: () => {
      const roomToSystems: {
        [roomId: string]: number[]
      } = {}

      if (!visibleDetails || !rooms || !systems) {
        return Object.entries(roomToSystems)
      }

      // Track first-occurrence index per room and per (room, system) in canonical sort
      // (non-draft only). Replaces the old min-lineitemnumber tiebreak.
      const roomFirstIdx: { [roomId: string]: number } = {}
      const systemFirstIdx: { [roomSystemKey: string]: number } = {}

      if (filters?.roomId) {
        // Initialize roomToSystems with room filter. A parent filter seeds the
        // whole group; seeding walks `rooms` (DFS order) rather than the group id
        // list so the emitted sections stay in rank order even when corrupt data
        // makes rank and membership disagree.
        const seededRoomIds = new Set(groupRoomIds ?? [filters.roomId])
        const seededSystemIds = filters.systemId ? [filters.systemId] : []
        for (const room of rooms) {
          if (seededRoomIds.has(room.dsy_accountroomid)) {
            roomToSystems[room.dsy_accountroomid] = [...seededSystemIds]
          }
        }
        // A `temp-` filter room is not in `rooms` yet.
        if (!roomToSystems[filters.roomId]) {
          roomToSystems[filters.roomId] = [...seededSystemIds]
        }
      } else if (!Object.values(filters ?? {}).some(Boolean)) {
        // Initialize roomToSystems with all rooms if no filters
        for (const room of rooms) {
          roomToSystems[room.dsy_accountroomid] = []
        }
      }

      // Sort once via canonical algorithm; positions feed both room and system order.
      const sortedForOrder =
        !isDraft && systemScopeTags ? sortQuoteDetails(visibleDetails, rooms, systems, systemScopeTags) : null

      const indexByDetailId = sortedForOrder ? new Map(sortedForOrder.map((d, i) => [d.quotedetailid, i])) : null

      // Add visible details rooms & systems
      for (const detail of visibleDetails) {
        const roomId = detail.dsy_cpqfields._dsy_room_value
        const systemId = detail.dsy_cpqfields.dsy_system
        if (!roomId) continue

        roomToSystems[roomId] = roomToSystems[roomId] || []
        if (systemId) {
          roomToSystems[roomId].push(systemId)
        }

        if (indexByDetailId) {
          const idx = indexByDetailId.get(detail.quotedetailid) ?? Infinity
          if (roomFirstIdx[roomId] === undefined || idx < roomFirstIdx[roomId]) {
            roomFirstIdx[roomId] = idx
          }
          if (systemId != null) {
            const key = `${roomId}:${systemId}`
            if (systemFirstIdx[key] === undefined || idx < systemFirstIdx[key]) {
              systemFirstIdx[key] = idx
            }
          }
        }
      }

      // Add visible temporary rooms & systems
      for (const [roomId, systemIds] of Object.entries(temporaryRoomToSystems)) {
        const visibleSystemIds = filters?.systemId ? [filters.systemId] : systemIds
        // Group-aware: under a parent filter a child room added moments ago must
        // not vanish before its first line item lands.
        if (isRoomVisibleUnderFilter({ roomId, filterRoomId: filters?.roomId, visibleRoomIds: groupRoomIds })) {
          if (roomToSystems[roomId]) {
            roomToSystems[roomId].push(...visibleSystemIds)
          } else {
            roomToSystems[roomId] = visibleSystemIds
          }
        }
      }

      // Sort systems within each room, then sort rooms
      if (isDraft || !sortedForOrder) {
        // Draft (or option sets unloaded): sort by rooms list order and systems options order
        return orderRoomEntries(
          Object.entries(roomToSystems).map(
            ([k, v]) =>
              [
                k,
                Array.from(new Set(v)).sort(
                  (a, b) =>
                    systems?.Options.findIndex((s) => s.Value === a) - systems?.Options.findIndex((s) => s.Value === b)
                ),
              ] as [string, number[]]
          ),
          rooms
        )
      }

      // Non-draft: sort by first-occurrence index in canonical sort
      return Object.entries(roomToSystems)
        .map(
          ([k, v]) =>
            [
              k,
              Array.from(new Set(v)).sort(
                (a, b) => (systemFirstIdx[`${k}:${a}`] ?? Infinity) - (systemFirstIdx[`${k}:${b}`] ?? Infinity)
              ),
            ] as [string, number[]]
        )
        .sort((a, b) => (roomFirstIdx[a[0]] ?? Infinity) - (roomFirstIdx[b[0]] ?? Infinity))
    },
  })

  return useMemo(() => ({ data: data ?? [], dataUpdatedAt, isLoading }), [data, dataUpdatedAt, isLoading])
}

export type InsertBucket = {
  roomId: string | null
  systemId: number | null
  scopeTag: number | null
  /**
   * Package header dsy_globalid for a package member; null for every other row.
   * When set it is the WHOLE bucket — room/system/scope are ignored.
   */
  packageGlobalId: string | null
  parentGlobalId: string | null
}

/**
 * Returns the next dsy_grouporder slot for a bucket:
 *   - member bucket (packageGlobalId set): the package key alone,
 *   - every other bucket: (room, system, scope, parent).
 * Reads the unfiltered cache (matches the previous getEndOfQuoteLineItemNumber pattern);
 * if empty, returns 1.
 *
 * A package header owns one slot space for all of its members, wherever they sit.
 * The sort splices members under the header ordered by their own grouporder, so
 * sharing the room's system/scope bucket would hand members and their unpackaged
 * neighbours the same slots — and members of one header that span systems or
 * scopes would collide with each other.
 */
export function getEndOfGroupOrder(cache: QuoteDetailData[] | undefined, bucket: InsertBucket): number {
  if (!cache?.length) return 1
  let max = 0
  for (const qd of cache) {
    // Skip optimistic rows — onMutate adds one before mutationFn re-reads cache,
    // which would otherwise inflate every newly-added row's grouporder by 1.
    if (qd.quotedetailid?.startsWith('temp-')) continue
    const f = qd.dsy_cpqfields
    const packageGlobalId = f.dsy_packageglobalid ?? null
    const parentGlobalId = f.dsy_parentglobalid ?? null
    // The accessory key wins on an illegal both-keys row, matching the sort.
    const matches = bucket.packageGlobalId
      ? !parentGlobalId && packageGlobalId === bucket.packageGlobalId
      : f._dsy_room_value === bucket.roomId &&
        f.dsy_system === bucket.systemId &&
        f.dsy_scopetag === bucket.scopeTag &&
        packageGlobalId === null &&
        parentGlobalId === bucket.parentGlobalId
    if (matches) {
      const n = f.dsy_grouporder ?? 0
      if (n > max) max = n
    }
  }
  return max + 1
}

export function useCreateQuoteDetail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['create', 'quotedetails'],
    mutationFn: createQuoteDetail,
    onError: (error) => {
      toast({
        title: 'Error adding product',
        error: error,
        variant: 'destructive',
      })
    },
    onSettled: async (_, __, { cpqFieldsBody }) => {
      const roomId = cpqFieldsBody['dsy_room@odata.bind']?.replace('/dsy_accountrooms(', '').replace(')', '')
      const systemId = cpqFieldsBody.dsy_system

      const queryKeys = [
        QUERY_KEYS.quoteDetails(),
        QUERY_KEYS.quoteDetails({ roomId }),
        QUERY_KEYS.quoteDetails({ systemId }),
      ]

      const uniqueQueryKeys = getUniqueQueryKeys(queryKeys)
      Promise.all(uniqueQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      return await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetails({ roomId, systemId }) })
    },
  })
}

export type CreateQuoteDetailFromPriceListItemVariables = {
  priceListItem: PriceListItemData
  roomId: string
  systemId: number
  phaseId?: number | null
  systemScopeTag?: number | null
  parentGlobalId?: string | null
  /**
   * Package header this row joins as a member. Mutually exclusive with
   * parentGlobalId — the server-side XOR plugin rejects a row carrying both.
   */
  packageGlobalId?: string | null
  quantity?: number | null
  /**
   * Explicit dsy_grouporder, bypassing the end-of-bucket computation.
   *
   * A bulk insert numbers its own rows: getEndOfGroupOrder reads the cache, and
   * concurrent creates all read the same snapshot, so every row of a package
   * insert otherwise lands on the same slot.
   */
  groupOrder?: number
  /**
   * Keep the quantity exactly as given, skipping the wire-run/wire-loop default.
   *
   * An accessory link carries no length, so a wire run added as one gets the
   * quote's default length. A package content carries the length its author
   * saved — including a deliberate 1 — and substituting the default there
   * inflates the line 100-250x.
   */
  isPackageContent?: boolean
}

/**
 * The two link keys a new line may carry, at most one of them set.
 *
 * A row is an accessory of another line OR a member of a package, never both:
 * the server-side XOR plugin rejects a row holding both keys, so the illegal
 * combination is refused here, where the message can name what asked for it.
 */
export function buildLineLinkKeys({
  parentGlobalId,
  packageGlobalId,
}: {
  parentGlobalId?: string | null
  packageGlobalId?: string | null
}): Pick<UpsertCPQFieldsBody, 'dsy_parentglobalid' | 'dsy_packageglobalid'> {
  if (parentGlobalId && packageGlobalId) {
    throw new Error('A line can belong to an accessory parent or a package, not both.')
  }
  return { dsy_parentglobalid: parentGlobalId, dsy_packageglobalid: packageGlobalId }
}

/**
 * Create one quote line from a price list item: the whole pricing, labor and
 * link-key computation, with no mutation wrapper around it.
 *
 * Exported so a bulk insert can drive it directly. Each call through
 * useCreateQuoteDetailFromPriceListItem takes its own optimistic cache snapshot
 * and restores that snapshot on failure, so N concurrent creates let one
 * rejection roll back rows its siblings had already committed — the reason a
 * package insert writes through this function and invalidates once at the end.
 */
export async function createQuoteDetailFromPriceListItem(
  {
    priceListItem,
    roomId,
    systemId,
    phaseId,
    systemScopeTag,
    parentGlobalId,
    packageGlobalId,
    quantity = 1,
    groupOrder,
    isPackageContent = false,
  }: CreateQuoteDetailFromPriceListItemVariables,
  client: QueryClient
): Promise<string> {
  const quote = client.getQueryData<QuoteData>(QUERY_KEYS.quote())

  if (!quote) {
    throw new Error('Quote not found')
  }

  const linkKeys = buildLineLinkKeys({ parentGlobalId, packageGlobalId })

  const phases = client.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.phases)

  if (!phases) {
    throw new Error('Phases not found')
  }

  const currentPhase = useViewState.getState().currentPhase

  const resolvedPhaseId =
    phaseId || currentPhase || priceListItem.dsy_product.dsy_defaultphase || phases.Options[0]?.Value

  if (!roomId || !systemId || !resolvedPhaseId) {
    throw new Error('Room, system, and phase are required')
  }

  const resolvedScopeTag = resolveScopeTagForSystem(systemId, systemScopeTag)

  const cpqFieldsBody: UpsertCPQFieldsBody = {
    dsy_system: systemId,
    dsy_phase: resolvedPhaseId,
    dsy_scopetag: resolvedScopeTag,
    dsy_engineeringlabor:
      quote.dsy_saletype === QuoteSaleType.TimeAndMaterial &&
      priceListItem.dsy_product.msdyn_fieldserviceproducttype !== FieldServiceProductType.Labor
        ? 0
        : priceListItem.dsy_engineeringlabor,
    dsy_installationlabor:
      quote.dsy_saletype === QuoteSaleType.TimeAndMaterial &&
      priceListItem.dsy_product.msdyn_fieldserviceproducttype !== FieldServiceProductType.Labor
        ? 0
        : priceListItem.dsy_installationlabor,
    dsy_roughinlabor:
      quote.dsy_saletype === QuoteSaleType.TimeAndMaterial &&
      priceListItem.dsy_product.msdyn_fieldserviceproducttype !== FieldServiceProductType.Labor
        ? 0
        : priceListItem.dsy_roughinlabor,
    dsy_programminglabor:
      quote.dsy_saletype === QuoteSaleType.TimeAndMaterial &&
      priceListItem.dsy_product.msdyn_fieldserviceproducttype !== FieldServiceProductType.Labor
        ? 0
        : priceListItem.dsy_programminglabor,
    dsy_projectmanagementlabor:
      quote.dsy_saletype === QuoteSaleType.TimeAndMaterial &&
      priceListItem.dsy_product.msdyn_fieldserviceproducttype !== FieldServiceProductType.Labor
        ? 0
        : priceListItem.dsy_projectmanagementlabor,
    dsy_servicelabor:
      quote.dsy_saletype === QuoteSaleType.TimeAndMaterial &&
      priceListItem.dsy_product.msdyn_fieldserviceproducttype !== FieldServiceProductType.Labor
        ? 0
        : priceListItem.dsy_servicelabor,
    ...linkKeys,
    'dsy_room@odata.bind': `/dsy_accountrooms(${roomId})`,
  }

  let newQuantity = quantity ?? 1

  // Never for a package content: its length is the one its author saved.
  if (!isPackageContent) {
    if (isWireRun(priceListItem.dsy_product) && newQuantity === 1) {
      newQuantity = quote.dsy_defaultwirerunlength ?? DEFAULT_WIRE_RUN_LENGTH
    }
    if (isWireLoop(priceListItem.dsy_product) && newQuantity === 1) {
      newQuantity = quote.dsy_defaultwirelooplength ?? DEFAULT_WIRE_LOOP_LENGTH
    }
  }

  const quantityDecimal = new Decimal(newQuantity)
  const unitPriceDecimal = new Decimal(priceListItem.dsy_unitprice ?? 0)
  const tax = priceListItem.dsy_taxable ? getTaxDecimal(unitPriceDecimal, quantityDecimal).toNumber() : 0
  const totalPriceDecimal = unitPriceDecimal.mul(quantityDecimal)
  const totalPriceWithTax = totalPriceDecimal.plus(tax)
  const unitCostDecimal = new Decimal(priceListItem.dsy_unitcost ?? 0)
  const totalCostDecimal = unitCostDecimal.mul(quantityDecimal)

  cpqFieldsBody.dsy_grouporder =
    groupOrder ??
    getEndOfGroupOrder(client.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails()), {
      roomId,
      systemId,
      scopeTag: resolvedScopeTag ?? null,
      // Set only when a package flow is materializing a member; a plain
      // catalog insert lands unpackaged.
      packageGlobalId: packageGlobalId ?? null,
      parentGlobalId: parentGlobalId ?? null,
    })

  const quoteDetailBody: UpsertQuoteDetailBody = {
    quantity: quantityDecimal.toDecimalPlaces(0).toNumber(),
    msdyn_costpriceperunit: unitCostDecimal.toDecimalPlaces(2).toNumber(),
    msdyn_costamount: totalCostDecimal.toDecimalPlaces(2).toNumber(),
    msdyn_costtotal: totalCostDecimal.toDecimalPlaces(2).toNumber(),
    msdyn_estimatedcost: totalCostDecimal.toDecimalPlaces(2).toNumber(),
    priceperunit: unitPriceDecimal.toDecimalPlaces(2).toNumber(),
    baseamount: totalPriceDecimal.toDecimalPlaces(2).toNumber(),
    extendedamount: totalPriceWithTax.toDecimalPlaces(2).toNumber(),
    quotedetailname: priceListItem.dsy_product.name,
    description: priceListItem.dsy_description,
    tax: tax,
    msdyn_taxable: !!priceListItem.dsy_taxable,
    'quoteid@odata.bind': `/quotes(${quote.quoteid})`,
    'productid@odata.bind': `/products(${priceListItem.dsy_product.productid})`,
    'uomid@odata.bind': `/uoms(${priceListItem.dsy_product._defaultuomid_value})`,
  }

  return await createQuoteDetail({ cpqFieldsBody, quoteDetailBody, productName: priceListItem.dsy_product.name })
}

export function useCreateQuoteDetailFromPriceListItem({ promptAccessories = true }: { promptAccessories?: boolean }) {
  const queryClient = useQueryClient()

  return useMutation<unknown[], Error, CreateQuoteDetailFromPriceListItemVariables, OptimisticContextWithTemp>({
    mutationKey: ['create', 'quotedetails', 'pricelistitem'],
    mutationFn: async (variables: CreateQuoteDetailFromPriceListItemVariables) => {
      const { priceListItem } = variables
      const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote())

      if (!quote) {
        throw new Error('Quote not found')
      }

      const promises: Promise<unknown>[] = [createQuoteDetailFromPriceListItem(variables, queryClient)]

      if (promptAccessories) {
        // A catalog package reads its own contents instead of accessories —
        // different source, different link key on the rows it creates, and the
        // contents go straight in rather than through the accessories dialog.
        promises.push(
          isCatalogPackage(priceListItem.dsy_product)
            ? queryClient.ensureQueryData({
                queryKey: QUERY_KEYS.packageContentPriceListItems({
                  productId: priceListItem.dsy_product.productid,
                  priceListId: quote?._pricelevelid_value,
                }),
                queryFn: () =>
                  getPackageContentPriceListItems({
                    productId: priceListItem.dsy_product.productid,
                    priceListId: quote._pricelevelid_value,
                  }),
              })
            : queryClient.ensureQueryData({
                queryKey: QUERY_KEYS.accessoryPriceListItems({
                  productId: priceListItem.dsy_product.productid,
                  priceListId: quote?._pricelevelid_value,
                }),
                queryFn: () =>
                  getAccessoryPriceListItems({
                    productId: priceListItem.dsy_product.productid,
                    priceListId: quote._pricelevelid_value,
                  }),
              })
        )
      }

      return await Promise.all(promises)
    },
    onMutate: async ({
      priceListItem,
      roomId,
      systemId,
      systemScopeTag,
      parentGlobalId,
      packageGlobalId,
      phaseId,
      quantity = 1,
      groupOrder,
      isPackageContent = false,
    }) => {
      await queryClient.cancelQueries({ queryKey: ['quotedetails'] })

      const previous: QuoteDetailCacheSnapshot = queryClient.getQueriesData<QuoteDetailData[]>({
        queryKey: ['quotedetails'],
      })
      const tempId = `temp-${crypto.randomUUID()}`

      systemScopeTag = resolveScopeTagForSystem(systemId, systemScopeTag)

      const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote())
      const phases = queryClient.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.phases)
      const resolvedPhaseId =
        phaseId ||
        useViewState.getState().currentPhase ||
        priceListItem.dsy_product.dsy_defaultphase ||
        phases?.Options[0]?.Value ||
        null

      const isTM =
        quote?.dsy_saletype === QuoteSaleType.TimeAndMaterial &&
        priceListItem.dsy_product.msdyn_fieldserviceproducttype !== FieldServiceProductType.Labor
      let newQuantity = quantity ?? 1
      if (!isPackageContent) {
        if (isWireRun(priceListItem.dsy_product) && newQuantity === 1) {
          newQuantity = quote?.dsy_defaultwirerunlength ?? DEFAULT_WIRE_RUN_LENGTH
        }
        if (isWireLoop(priceListItem.dsy_product) && newQuantity === 1) {
          newQuantity = quote?.dsy_defaultwirelooplength ?? DEFAULT_WIRE_LOOP_LENGTH
        }
      }

      const quantityDec = new Decimal(newQuantity)
      const unitPriceDec = new Decimal(priceListItem.dsy_unitprice ?? 0)
      const unitCostDec = new Decimal(priceListItem.dsy_unitcost ?? 0)
      const taxNum = priceListItem.dsy_taxable ? getTaxDecimal(unitPriceDec, quantityDec).toNumber() : 0
      const cachedProductImage = queryClient.getQueryData<string>(QUERY_KEYS.productImage(priceListItem.dsy_product.productid))

      const optimisticGroupOrder =
        groupOrder ??
        getEndOfGroupOrder(queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails()), {
          roomId,
          systemId,
          scopeTag: systemScopeTag ?? null,
          // Set only when a package flow is materializing a member; a plain
          // catalog insert lands unpackaged.
          packageGlobalId: packageGlobalId ?? null,
          parentGlobalId: parentGlobalId ?? null,
        })

      const optimisticDetail: QuoteDetailData = {
        quotedetailid: tempId,
        quotedetailname: priceListItem.dsy_product.name,
        description: priceListItem.dsy_description,
        priceperunit: unitPriceDec.toDecimalPlaces(2).toNumber(),
        msdyn_costpriceperunit: unitCostDec.toDecimalPlaces(2).toNumber(),
        quantity: quantityDec.toDecimalPlaces(0).toNumber(),
        tax: taxNum,
        msdyn_taxable: !!priceListItem.dsy_taxable,
        manualdiscountamount: null,
        productid: {
          ...priceListItem.dsy_product,
          entityimage: cachedProductImage ?? priceListItem.dsy_product.entityimage,
        },
        dsy_cpqfields: {
          dsy_lineitemcpqfieldsid: `${tempId}-cpq`,
          dsy_globalid: `${tempId}-global`,
          dsy_parentglobalid: parentGlobalId ?? null,
          dsy_packageglobalid: packageGlobalId ?? null,
          dsy_phase: resolvedPhaseId,
          _dsy_room_value: roomId,
          dsy_system: systemId,
          dsy_scopetag: systemScopeTag ?? null,
          dsy_grouporder: optimisticGroupOrder,
          dsy_location: null,
          dsy_engineeringlabor: isTM ? 0 : priceListItem.dsy_engineeringlabor,
          dsy_installationlabor: isTM ? 0 : priceListItem.dsy_installationlabor,
          dsy_roughinlabor: isTM ? 0 : priceListItem.dsy_roughinlabor,
          dsy_programminglabor: isTM ? 0 : priceListItem.dsy_programminglabor,
          dsy_projectmanagementlabor: isTM ? 0 : priceListItem.dsy_projectmanagementlabor,
          dsy_servicelabor: isTM ? 0 : priceListItem.dsy_servicelabor,
          dsy_laborfactorpercent: null,
        },
      }

      const entries = queryClient.getQueriesData<QuoteDetailData[]>({ queryKey: ['quotedetails'] })
      for (const [key, cached] of entries) {
        if (!cached) continue
        const filters = key[1] as
          | { previousQuote?: boolean; roomId?: string | null; systemId?: number | null; onlySummaryDetails?: boolean }
          | undefined
        if (filters?.previousQuote) continue
        if (filters?.onlySummaryDetails) continue
        if (filters?.roomId && filters.roomId !== roomId) continue
        if (filters?.systemId && filters.systemId !== systemId) continue
        queryClient.setQueryData<QuoteDetailData[]>(key, [...cached, optimisticDetail])
      }

      return { tempId, previous }
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        context.previous.forEach(([key, data]) => queryClient.setQueryData<QuoteDetailData[]>(key, data))
      }
      toast({
        title: 'Error adding product',
        error: error,
        variant: 'destructive',
      })
    },
    onSuccess: async ([quoteDetail, contentData], variables, context) => {
      let createdDetail: QuoteDetailData | null = null
      if (context?.tempId && typeof quoteDetail === 'string') {
        try {
          const createdRecord = await getRecord<QuoteDetailData>('quotedetails', quoteDetail, QUOTE_DETAIL_OPTIONS)
          createdDetail = quoteDetailSchema.parse(createdRecord)
          const entries = queryClient.getQueriesData<QuoteDetailData[]>({ queryKey: ['quotedetails'] })
          for (const [key, cached] of entries) {
            queryClient.setQueryData<QuoteDetailData[]>(
              key,
              replaceTemporaryQuoteDetailInCache(cached, context.tempId, createdDetail)
            )
          }
        } catch (error) {
          trackException(error instanceof Error ? error : new Error(String(error)), {
            context: 'quote-details.replaceOptimisticQuoteDetail',
            quoteDetailId: quoteDetail,
            tempId: context.tempId,
          })
        }
      }

      if (promptAccessories && isCatalogPackage(variables.priceListItem.dsy_product)) {
        await insertCatalogPackageContents({
          headerQuoteDetail: createdDetail,
          headerQuoteDetailId: typeof quoteDetail === 'string' ? quoteDetail : null,
          packageName: variables.priceListItem.dsy_product.name,
          contents: contentData as PackageContentPriceListItems | undefined,
          queryClient,
        })
        return
      }

      const accessories = contentData as AccessoryPriceListItemData[] | undefined
      if (accessories?.length) {
        const openAccessoriesDialog = useAddAccessoriesDialogStore.getState().openDialog
        openAccessoriesDialog(quoteDetail as string)
      }
    },
    onSettled: async (_data, _error, { roomId, systemId }) => {
      const queryKeys = [
        QUERY_KEYS.quoteDetails(),
        QUERY_KEYS.quoteDetails({ roomId }),
        QUERY_KEYS.quoteDetails({ systemId }),
      ]

      const uniqueQueryKeys = getUniqueQueryKeys(queryKeys)
      Promise.all(uniqueQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      return await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetails({ roomId, systemId }) })
    },
  })
}

export type UpdateQuoteDetailBody = {
  quoteDetail: QuoteDetailData
  quoteDetailBody: UpsertQuoteDetailBody
  cpqFieldsBody: UpsertCPQFieldsBody
  /**
   * Pre-edit quantities by quote detail id, stamped by useUpdateQuoteDetail's
   * onMutate BEFORE it scales the cache. The write path's cascades read their
   * base quantities from the cache, and onMutate runs first — without the
   * stamp, a header edit 1→2 reads members the optimistic patch already moved
   * to 2 and writes 4. Never set by callers.
   */
  cascadeBaseQuantities?: Map<string, number>
}

/**
 * A child row's quantity after its parent's moved from `previousQuantity` to
 * `nextQuantity`: proportional, rounded to a whole unit, floored at one.
 *
 * The one definition of "the parent got bigger, so its rows did too" — the
 * accessory cascade below and the package member cascade beside it both read
 * it, so a header scales its members exactly the way a product scales its
 * accessories. A child never disappears by shrinking its parent: the floor is
 * one, and removing a row is a delete, not a quantity edit.
 */
export function getScaledChildQuantity({
  quantity,
  previousQuantity,
  nextQuantity,
}: {
  quantity: number
  previousQuantity: number
  nextQuantity: number
}) {
  if (previousQuantity <= 0 || nextQuantity <= 0 || previousQuantity === nextQuantity) return quantity
  const ratio = new Decimal(nextQuantity).div(previousQuantity)
  return Decimal.max(new Decimal(quantity).mul(ratio).toDecimalPlaces(0), 1).toNumber()
}

export async function updateQuoteDetail({ quoteDetail, quoteDetailBody, cpqFieldsBody, cascadeBaseQuantities }: UpdateQuoteDetailBody) {
  const existingQuantityDecimal = new Decimal(quoteDetail.quantity)
  const existingUnitPriceDecimal = new Decimal(quoteDetail.priceperunit ?? 0)
  const existingUnitCostDecimal = new Decimal(quoteDetail.msdyn_costpriceperunit ?? 0)

  const updatedQuantityDecimal = new Decimal(quoteDetailBody.quantity ?? existingQuantityDecimal)
  // No-charge lines (LABOR:, OWNER FURNISHED:, DAISY:PREWIRE:) are never priced;
  // any touch re-asserts zero so a line born priced heals on its next edit.
  const isNoCharge = isNoChargeQuoteDetail(quoteDetail)
  const updatedUnitPriceDecimal = isNoCharge
    ? new Decimal(0)
    : new Decimal(quoteDetailBody.priceperunit ?? existingUnitPriceDecimal)
  const updatedUnitCostDecimal = isNoCharge
    ? new Decimal(0)
    : new Decimal(quoteDetailBody.msdyn_costpriceperunit ?? existingUnitCostDecimal)

  const isTaxable = quoteDetailBody.msdyn_taxable ?? quoteDetail.msdyn_taxable ?? false
  const existingTax = new Decimal(quoteDetail.tax ?? 0)
  const updatedTax = isTaxable ? getTaxDecimal(updatedUnitPriceDecimal, updatedQuantityDecimal) : new Decimal(0)
  const updatedTotalPriceDecimal = updatedUnitPriceDecimal.mul(updatedQuantityDecimal)
  const updatedTotalPriceWithTax = updatedTotalPriceDecimal.plus(updatedTax)
  const updatedTotalCostDecimal = updatedUnitCostDecimal.mul(updatedQuantityDecimal)

  if (!updatedQuantityDecimal.eq(existingQuantityDecimal)) {
    quoteDetailBody.quantity = updatedQuantityDecimal.toNumber()
  }
  if (!updatedUnitCostDecimal.eq(existingUnitCostDecimal)) {
    quoteDetailBody.msdyn_costpriceperunit = updatedUnitCostDecimal.toDecimalPlaces(2).toNumber()
  }
  if (!updatedUnitCostDecimal.eq(existingUnitCostDecimal) || !updatedQuantityDecimal.eq(existingQuantityDecimal)) {
    quoteDetailBody.msdyn_costamount = updatedTotalCostDecimal.toDecimalPlaces(2).toNumber()
    quoteDetailBody.msdyn_costtotal = updatedTotalCostDecimal.toDecimalPlaces(2).toNumber()
    quoteDetailBody.msdyn_estimatedcost = updatedTotalCostDecimal.toDecimalPlaces(2).toNumber()
  }
  if (!updatedUnitPriceDecimal.eq(existingUnitPriceDecimal)) {
    quoteDetailBody.priceperunit = updatedUnitPriceDecimal.toDecimalPlaces(2).toNumber()
  }
  if (!updatedUnitPriceDecimal.eq(existingUnitPriceDecimal) || !updatedQuantityDecimal.eq(existingQuantityDecimal)) {
    quoteDetailBody.baseamount = updatedTotalPriceDecimal.toDecimalPlaces(2).toNumber()
    quoteDetailBody.extendedamount = updatedTotalPriceWithTax.toDecimalPlaces(2).toNumber()
  }
  if (!updatedTax.eq(existingTax)) {
    quoteDetailBody.tax = updatedTax.toDecimalPlaces(2).toNumber()
    quoteDetailBody.extendedamount = updatedTotalPriceWithTax.toDecimalPlaces(2).toNumber()
  }

  // Truck-stock designation follows the line quantity (clamp on shrink, clear
  // when no whole unit remains). Skipped when the caller is explicitly writing
  // the designation itself.
  if (quoteDetailBody.quantity !== undefined && cpqFieldsBody.dsy_usedinventoryquantity === undefined) {
    const designationPatch = clampUsedInventoryDesignation(
      quoteDetail.dsy_cpqfields,
      updatedQuantityDecimal.toNumber()
    )
    if (designationPatch) Object.assign(cpqFieldsBody, designationPatch)
  }

  // Every write below targets its own row (quotedetail, cpqfields, children),
  // so they fan out together. Thunks fire only at the fan-out, so a throw
  // while planning leaves nothing in flight; all writes then settle before
  // the first failure propagates, so no write races the error path.
  const writes: (() => Promise<unknown>)[] = []

  const shouldUpdateQuoteDetail = Object.keys(quoteDetailBody).length > 0
  const shouldUpdateCPQFields = Object.keys(cpqFieldsBody).length > 0

  if (shouldUpdateQuoteDetail) {
    writes.push(() => updateRecord('quotedetails', quoteDetail.quotedetailid, quoteDetailBody))
  }

  if (shouldUpdateCPQFields) {
    writes.push(() =>
      updateRecord<UpsertCPQFieldsBody>(
        'dsy_lineitemcpqfieldses',
        quoteDetail.dsy_cpqfields.dsy_lineitemcpqfieldsid,
        cpqFieldsBody
      )
    )
  }

  // Update child quote details if they exist and the room, system, scope tag or
  // quantity is modified. Scope tag is in the list for the same reason room and
  // system are: it is a bucket dimension the builder renders a section per, and
  // an accessory left behind in the old scope is wrong in Dataverse and in the
  // CSV export while being invisible in both tables (accessories render under
  // their parent, never in their own scope section).
  if (
    !quoteDetail.dsy_cpqfields.dsy_parentglobalid &&
    (shouldUpdateQuoteDetail || shouldUpdateCPQFields) &&
    (cpqFieldsBody['dsy_room@odata.bind'] !== undefined ||
      cpqFieldsBody.dsy_system !== undefined ||
      cpqFieldsBody.dsy_scopetag !== undefined ||
      quoteDetailBody.quantity !== undefined)
  ) {
    // Children are resolved against the row's PRE-move bucket, and that slice is
    // frequently unpopulated: the compact table fills only the unfiltered root
    // key, so an exact-match read returns undefined and the accessories are left
    // behind in the old room — invisible in the builder and the proposal, still
    // counted in the totals. Prefer the slice, fall back to the unfiltered
    // cache, and only when neither cache exists read the children back
    // server-side (same reason deletePackage re-reads).
    const parentGlobalId = quoteDetail.dsy_cpqfields.dsy_globalid
    const bucketQuoteDetails = queryClient.getQueryData<QuoteDetailData[]>(
      QUERY_KEYS.quoteDetails({
        roomId: quoteDetail.dsy_cpqfields._dsy_room_value,
        systemId: quoteDetail.dsy_cpqfields.dsy_system,
      })
    )
    const allQuoteDetails = queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails())

    const bucketChildren = getChildQuoteDetails(bucketQuoteDetails, parentGlobalId)
    const resolvedChildren = bucketChildren.length
      ? bucketChildren
      : allQuoteDetails
        ? getChildQuoteDetails(allQuoteDetails, parentGlobalId)
        : parentGlobalId
          ? await fetchChildQuoteDetails(parentGlobalId)
          : []
    // Temp rows are skipped the way the member cascade skips them: an optimistic
    // accessory has no id to patch, and PATCHing it fails the whole mutation.
    const childQuoteDetails = resolvedChildren.filter((child) => !isTemporaryQuoteDetailId(child.quotedetailid))

    childQuoteDetails.forEach((child) => {
      const updateChildQuoteDetailBody: UpsertQuoteDetailBody = {}
      const updateChildCPQFieldsBody: UpsertCPQFieldsBody = {}

      if (cpqFieldsBody['dsy_room@odata.bind'] !== undefined) {
        updateChildCPQFieldsBody['dsy_room@odata.bind'] = cpqFieldsBody['dsy_room@odata.bind']
      }
      if (cpqFieldsBody.dsy_system !== undefined) {
        updateChildCPQFieldsBody.dsy_system = cpqFieldsBody.dsy_system
      }
      if (cpqFieldsBody.dsy_scopetag !== undefined) {
        updateChildCPQFieldsBody.dsy_scopetag = cpqFieldsBody.dsy_scopetag
      }
      if (quoteDetailBody.quantity) {
        const existingParentQuantityDecimal = new Decimal(quoteDetail.quantity)
        const updatedParentQuantityDecimal = new Decimal(quoteDetailBody.quantity)

        if (!updatedParentQuantityDecimal.eq(existingParentQuantityDecimal)) {
          const updatedChildQuantityDecimal = new Decimal(
            getScaledChildQuantity({
              // The cached quantity may already carry the optimistic scaling —
              // the stamped base is the pre-edit truth.
              quantity: cascadeBaseQuantities?.get(child.quotedetailid) ?? child.quantity,
              previousQuantity: existingParentQuantityDecimal.toNumber(),
              nextQuantity: updatedParentQuantityDecimal.toNumber(),
            })
          )

          const updatedChildPriceDecimal = new Decimal(child.priceperunit ?? 0)
          const updatedChildCostDecimal = new Decimal(child.msdyn_costpriceperunit ?? 0)

          const isChildTaxable = child.msdyn_taxable ?? false
          const updatedChildTax = isChildTaxable
            ? getTaxDecimal(updatedChildPriceDecimal, updatedChildQuantityDecimal)
            : new Decimal(0)
          const updatedChildTotalPriceDecimal = updatedChildPriceDecimal.mul(updatedChildQuantityDecimal)
          const updatedChildTotalPriceWithTax = updatedChildTotalPriceDecimal.plus(updatedChildTax)
          const updatedChildTotalCostDecimal = updatedChildCostDecimal.mul(updatedChildQuantityDecimal)

          updateChildQuoteDetailBody.quantity = updatedChildQuantityDecimal.toDecimalPlaces(0).toNumber()

          // Scaled children take the same designation clamp as the parent
          // (child quantity floors at 1, so only the shrink branch applies).
          const childDesignationPatch = clampUsedInventoryDesignation(
            child.dsy_cpqfields,
            updatedChildQuantityDecimal.toNumber()
          )
          if (childDesignationPatch) Object.assign(updateChildCPQFieldsBody, childDesignationPatch)

          updateChildQuoteDetailBody.msdyn_costpriceperunit = updatedChildCostDecimal.toDecimalPlaces(2).toNumber()
          updateChildQuoteDetailBody.msdyn_costamount = updatedChildTotalCostDecimal.toDecimalPlaces(2).toNumber()
          updateChildQuoteDetailBody.msdyn_costtotal = updatedChildTotalCostDecimal.toDecimalPlaces(2).toNumber()
          updateChildQuoteDetailBody.msdyn_estimatedcost = updatedChildTotalCostDecimal.toDecimalPlaces(2).toNumber()

          updateChildQuoteDetailBody.priceperunit = updatedChildPriceDecimal.toDecimalPlaces(2).toNumber()
          updateChildQuoteDetailBody.baseamount = updatedChildTotalPriceDecimal.toDecimalPlaces(2).toNumber()
          updateChildQuoteDetailBody.extendedamount = updatedChildTotalPriceWithTax.toDecimalPlaces(2).toNumber()

          updateChildQuoteDetailBody.tax = updatedChildTax.toDecimalPlaces(2).toNumber()
        }
      }

      const shouldUpdateChildQuoteDetail = Object.keys(updateChildQuoteDetailBody).length > 0
      const shouldUpdateChildCPQFields = Object.keys(updateChildCPQFieldsBody).length > 0

      if (shouldUpdateChildQuoteDetail) {
        writes.push(() => updateRecord('quotedetails', child.quotedetailid, updateChildQuoteDetailBody))
      }

      if (shouldUpdateChildCPQFields) {
        writes.push(() =>
          updateRecord<UpsertCPQFieldsBody>(
            'dsy_lineitemcpqfieldses',
            child.dsy_cpqfields.dsy_lineitemcpqfieldsid,
            updateChildCPQFieldsBody
          )
        )
      }
    })
  }

  // A package header carries a real quantity — how many copies of the package
  // the quote sells — so changing it scales every member by the same ratio, the
  // way a parent product scales its accessories. Its SCOPE TAG is the package's
  // bucket, which every member sits in by definition, so an edit to it moves
  // them too. Members hang on dsy_packageglobalid, which the accessory cascade
  // above never sees, hence the second pass.
  //
  // Room and system are deliberately NOT here: a relocation runs through
  // buildMovePackagePlan, which patches the header and every member itself and
  // deals each a fresh slot in the destination. Cascading them here as well
  // would write every member twice. The scope tag is excluded for the same
  // reason whenever it arrives alongside a room or system — that combination is
  // only ever the move plan's own cross-system remap.
  //
  // Each member goes back through updateQuoteDetail rather than being patched
  // here: that is the identical entry point a builder's edit on that row takes,
  // so the member's own accessories follow through the cascade above, its
  // price/cost/tax totals are recomputed once, and its truck-stock designation
  // is clamped. Members are never headers themselves (a package is not offered
  // "Add to package"), so the recursion is one level deep.
  const cascadeQuantity =
    quoteDetailBody.quantity !== undefined && !updatedQuantityDecimal.eq(existingQuantityDecimal)
  const cascadeScopeTag =
    cpqFieldsBody.dsy_scopetag !== undefined &&
    cpqFieldsBody['dsy_room@odata.bind'] === undefined &&
    cpqFieldsBody.dsy_system === undefined
  if (isPackageHeader(quoteDetail.productid) && (cascadeQuantity || cascadeScopeTag)) {
    const headerGlobalId = quoteDetail.dsy_cpqfields.dsy_globalid ?? ''
    // Same cache ladder as the accessory cascade, and for the same reason: the
    // compact table fills only the unfiltered root key, and a member missed here
    // is a member left at the old package's quantity.
    const bucketQuoteDetails = queryClient.getQueryData<QuoteDetailData[]>(
      QUERY_KEYS.quoteDetails({
        roomId: quoteDetail.dsy_cpqfields._dsy_room_value,
        systemId: quoteDetail.dsy_cpqfields.dsy_system,
      })
    )
    const allQuoteDetails = queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails())

    const bucketMembers = packageMemberRows(bucketQuoteDetails, headerGlobalId)
    const cachedMembers = bucketMembers.length
      ? bucketMembers
      : allQuoteDetails
        ? packageMemberRows(allQuoteDetails, headerGlobalId)
        : headerGlobalId
          ? packageMemberRows(await fetchPackageQuoteDetails(headerGlobalId), headerGlobalId)
          : []
    // Rebase to the stamped pre-edit quantities: the cache these rows came from
    // was already scaled by onMutate, and the member's quantity is both the
    // scaling base here and the previousQuantity of its own accessory cascade.
    const members = cachedMembers.map((member) => {
      const base = cascadeBaseQuantities?.get(member.quotedetailid)
      return base === undefined || base === member.quantity ? member : { ...member, quantity: base }
    })

    const scaled = cascadeQuantity
      ? planPackageMemberQuantityScaling({
          members,
          previousQuantity: existingQuantityDecimal.toNumber(),
          nextQuantity: updatedQuantityDecimal.toNumber(),
        })
      : []
    const scaledQuantities = new Map(scaled.map(({ quoteDetail, quantity }) => [quoteDetail.quotedetailid, quantity]))

    // One write per member even when both cascades fire: a scope edit that also
    // rescales would otherwise patch the same row twice.
    const targets = cascadeScopeTag
      ? members.filter((member) => !isTemporaryQuoteDetailId(member.quotedetailid))
      : scaled.map(({ quoteDetail }) => quoteDetail)

    for (const member of targets) {
      const quantity = scaledQuantities.get(member.quotedetailid)
      const memberQuoteDetailBody: UpsertQuoteDetailBody = quantity === undefined ? {} : { quantity }
      const memberCPQFieldsBody: UpsertCPQFieldsBody = cascadeScopeTag
        ? { dsy_scopetag: cpqFieldsBody.dsy_scopetag ?? null }
        : {}
      writes.push(() =>
        updateQuoteDetail({
          quoteDetail: member,
          quoteDetailBody: memberQuoteDetailBody,
          cpqFieldsBody: memberCPQFieldsBody,
          cascadeBaseQuantities,
        })
      )
    }
  }

  const settled = await Promise.allSettled(writes.map((write) => write()))
  const failure = settled.find((result) => result.status === 'rejected')
  if (failure) throw failure.reason
  return settled.map((result) => (result as PromiseFulfilledResult<unknown>).value)
}

type QuoteDetailCacheSnapshot = [readonly unknown[], QuoteDetailData[] | undefined][]

type OptimisticContext = { previous: QuoteDetailCacheSnapshot }
type OptimisticContextWithTemp = OptimisticContext & { tempId: string }

/**
 * The rows one optimistic patch touched, per cache key, as they were before it.
 *
 * Restoring a whole ['quotedetails'] snapshot on error also reverts every other
 * edit that succeeded while this one was in flight (each row is its own PATCH,
 * and the builder lets you edit the next row immediately), so the rollback puts
 * back only the rows this mutation wrote.
 */
type PatchedRowsSnapshot = [readonly unknown[], Map<string, QuoteDetailData>][]
type PatchedRowsContext = { previousRows: PatchedRowsSnapshot }

function parseRoomIdFromBind(bind?: string | null): string | null | undefined {
  if (bind === undefined) return undefined
  if (bind === null) return null
  return bind.replace('/dsy_accountrooms(', '').replace(')', '')
}

function applyOptimisticPatch(
  quoteDetail: QuoteDetailData,
  quoteDetailBody: UpsertQuoteDetailBody,
  cpqFieldsBody: UpsertCPQFieldsBody
): QuoteDetailData {
  const { ['dsy_room@odata.bind']: roomBind, ...cpqRest } = cpqFieldsBody
  const roomId = parseRoomIdFromBind(roomBind)
  return {
    ...quoteDetail,
    ...(quoteDetailBody as Partial<QuoteDetailData>),
    dsy_cpqfields: {
      ...quoteDetail.dsy_cpqfields,
      ...(cpqRest as Partial<CPQFieldsData>),
      ...(roomId !== undefined ? { _dsy_room_value: roomId } : {}),
    },
  }
}

function applyChildOptimisticPatch(child: QuoteDetailData, cpqFieldsBody: UpsertCPQFieldsBody): QuoteDetailData {
  const roomBind = cpqFieldsBody['dsy_room@odata.bind']
  const systemChanging = cpqFieldsBody.dsy_system !== undefined
  if (roomBind === undefined && !systemChanging) return child
  const roomId = parseRoomIdFromBind(roomBind)
  return {
    ...child,
    dsy_cpqfields: {
      ...child.dsy_cpqfields,
      ...(systemChanging ? { dsy_system: cpqFieldsBody.dsy_system as number | null } : {}),
      ...(roomId !== undefined ? { _dsy_room_value: roomId } : {}),
    },
  }
}

export function useUpdateQuoteDetail(
  options?: UseMutationOptions<unknown, Error, UpdateQuoteDetailBody, PatchedRowsContext>
) {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, UpdateQuoteDetailBody, PatchedRowsContext>({
    mutationKey: ['update', 'quotedetails'],
    mutationFn: updateQuoteDetail,
    onMutate: async (variables) => {
      const { quoteDetail, quoteDetailBody, cpqFieldsBody } = variables
      // Child global IDs also exist on the previous approved quote. Cascade
      // edits and their rollback snapshots must stay on the current side.
      const currentQuoteDetails = { queryKey: ['quotedetails', { previousQuote: false }] }
      await queryClient.cancelQueries(currentQuoteDetails)

      const patched = applyOptimisticPatch(quoteDetail, quoteDetailBody, cpqFieldsBody)
      const previousRows: PatchedRowsSnapshot = []
      // Pre-edit quantities, captured before any slice is patched: the write
      // path (which runs AFTER this) resolves its cascade bases from the cache,
      // and without the stamp it would scale quantities this patch already
      // scaled — 1→2 writing members to 4.
      const cascadeBaseQuantities = new Map<string, number>()

      // Each slice is patched on its own so the rollback can name the rows it
      // wrote (setQueriesData's updater never sees the key it is updating).
      for (const [queryKey, cached] of queryClient.getQueriesData<QuoteDetailData[]>(currentQuoteDetails)) {
        if (!cached) continue

        for (const d of cached) {
          if (!cascadeBaseQuantities.has(d.quotedetailid)) cascadeBaseQuantities.set(d.quotedetailid, d.quantity)
        }

        // The cascaded quantities the mutation is about to write, mirrored into
        // the cache so the roll-up columns divide a new quantity by a member
        // total that already moved with it.
        const scaledQuantities =
          quoteDetailBody.quantity === undefined
            ? new Map<string, number>()
            : planCascadedQuantityScaling({
                details: cached,
                quoteDetail,
                previousQuantity: quoteDetail.quantity,
                nextQuantity: quoteDetailBody.quantity,
              })

        const replaced = new Map<string, QuoteDetailData>()
        const next = cached.map((d) => {
          if (d.quotedetailid === quoteDetail.quotedetailid) {
            replaced.set(d.quotedetailid, d)
            return patched
          }

          let row = d
          if (
            !quoteDetail.dsy_cpqfields.dsy_parentglobalid &&
            d.dsy_cpqfields.dsy_parentglobalid === quoteDetail.dsy_cpqfields.dsy_globalid
          ) {
            row = applyChildOptimisticPatch(row, cpqFieldsBody)
          }

          const quantity = scaledQuantities.get(d.quotedetailid)
          if (quantity !== undefined && quantity !== row.quantity) {
            row = { ...row, quantity }
          }

          if (row !== d) replaced.set(d.quotedetailid, d)
          return row
        })

        if (!replaced.size) continue
        previousRows.push([queryKey, replaced])
        queryClient.setQueryData<QuoteDetailData[]>(queryKey, next)
      }

      // TanStack hands this same variables object to mutationFn next.
      if (quoteDetailBody.quantity !== undefined) {
        variables.cascadeBaseQuantities = cascadeBaseQuantities
      }

      return { previousRows }
    },
    onError: (error, _vars, context) => {
      // Only this mutation's rows go back: a neighbouring row's edit that landed
      // while this one was in flight keeps its value.
      context?.previousRows.forEach(([key, rows]) => {
        queryClient.setQueryData<QuoteDetailData[]>(key, (cached) => {
          if (!cached) return cached
          let changed = false
          const next = cached.map((d) => {
            const previous = rows.get(d.quotedetailid)
            if (!previous || previous === d) return d
            changed = true
            return previous
          })
          return changed ? next : cached
        })
      })
      toast({
        title: 'Error updating item',
        error: error,
        variant: 'destructive',
      })
    },
    onSettled: async (_data, _error, { quoteDetail, cpqFieldsBody }) => {
      // The error path invalidates too: the writes fan out with allSettled, so a
      // failed member PATCH can leave the header's already committed on the
      // server while the optimistic rollback shows the old value.
      const queryKeys = []
      const awaitQueryKeys = []

      // A cascading edit is only finished once the rows it scaled are back, and
      // the compact table reads them from the unfiltered root key — awaiting it
      // is what keeps the spinner up until the member quantities land.
      const cascades =
        isPackageHeader(quoteDetail.productid) ||
        getChildQuoteDetails(
          queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails()),
          quoteDetail.dsy_cpqfields.dsy_globalid
        ).length > 0
      if (cascades) {
        awaitQueryKeys.push(QUERY_KEYS.quoteDetails())
      } else {
        queryKeys.push(QUERY_KEYS.quoteDetails())
      }

      const cpqBodyRoomId = cpqFieldsBody['dsy_room@odata.bind']?.replace('/dsy_accountrooms(', '').replace(')', '')
      const roomIds = new Set([quoteDetail.dsy_cpqfields._dsy_room_value, cpqBodyRoomId])
      const systemIds = new Set([quoteDetail.dsy_cpqfields.dsy_system, cpqFieldsBody.dsy_system])

      for (const roomId of roomIds) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ roomId }))

        for (const systemId of systemIds) {
          awaitQueryKeys.push(QUERY_KEYS.quoteDetails({ roomId, systemId }))
        }
      }

      for (const systemId of systemIds) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ systemId }))
      }

      if (isSummaryDetail(quoteDetail.productid.productid)) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' }))
      }

      const uniqueQueryKeys = getUniqueQueryKeys(queryKeys)
      Promise.all(uniqueQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      const uniqueAwaitQueryKeys = getUniqueQueryKeys(awaitQueryKeys)
      return await Promise.all(uniqueAwaitQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
    },
    ...options,
  })
}

export type ReorderQuoteDetailChange = { dsy_lineitemcpqfieldsid: string; dsy_grouporder: number }

export type ReorderQuoteDetailsVariables = {
  changes: ReorderQuoteDetailChange[]
  roomId: string
  systemId: number
}

function patchGroupOrderIntoCaches(queryClient: QueryClient, changes: ReorderQuoteDetailChange[]) {
  const changeByCpqId = new Map(changes.map((c) => [c.dsy_lineitemcpqfieldsid, c.dsy_grouporder]))

  // undefined = bail out of the write so untouched entries keep their
  // dataUpdatedAt and the summary query keys (which embed it) don't rotate.
  queryClient.setQueriesData<QuoteDetailData[]>({ queryKey: ['quotedetails'] }, (cached) => {
    if (!cached) return undefined
    let changed = false
    const next = cached.map((d) => {
      const newOrder = changeByCpqId.get(d.dsy_cpqfields.dsy_lineitemcpqfieldsid)
      if (newOrder === undefined || newOrder === d.dsy_cpqfields.dsy_grouporder) return d
      changed = true
      return { ...d, dsy_cpqfields: { ...d.dsy_cpqfields, dsy_grouporder: newOrder } }
    })
    return changed ? next : undefined
  })
}

export function useReorderQuoteDetails() {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, ReorderQuoteDetailsVariables, OptimisticContext>({
    mutationKey: ['quotedetails', 'reorder'],
    mutationFn: async ({ changes }: ReorderQuoteDetailsVariables) => {
      if (!changes.length) return
      // Throttle to 12 in flight. DnD on a large bucket can issue N PATCHes in
      // one burst; avoid Dataverse 429s. The old limit of 5 also worked around
      // the serializing plugins that queued every child write on the parent
      // quote row — those are retired, so the cap is purely rate-limit headroom.
      return await applyQuoteDetailReorderInChunks(changes, (change) =>
        updateRecord<UpsertCPQFieldsBody>('dsy_lineitemcpqfieldses', change.dsy_lineitemcpqfieldsid, {
          dsy_grouporder: change.dsy_grouporder,
        })
      )
    },
    onMutate: async ({ changes }) => {
      if (!changes.length) return { previous: [] as QuoteDetailCacheSnapshot }

      await queryClient.cancelQueries({ queryKey: ['quotedetails'] })

      const previous: QuoteDetailCacheSnapshot = queryClient.getQueriesData<QuoteDetailData[]>({
        queryKey: ['quotedetails'],
      })

      patchGroupOrderIntoCaches(queryClient, changes)

      return { previous }
    },
    onSuccess: async (_data, { changes }) => {
      // The PATCHes go out in chunks while CPQ plugins fire their own
      // invalidations; a refetch landing mid-flight carries pre-reorder values
      // and overwrites the optimistic patch. Re-assert it now that the server
      // has the final order.
      await queryClient.cancelQueries({ queryKey: ['quotedetails'] })
      patchGroupOrderIntoCaches(queryClient, changes)
    },
    onError: async (error, _variables, context) => {
      if (context?.previous) {
        await restoreAndRefetchQuoteDetailCaches(queryClient, context.previous)
      }
      toast({
        title: 'Error reordering items',
        error: error,
        variant: 'destructive',
      })
    },
    onSettled: async (_data, _error, { roomId, systemId }) => {
      // Optimistic patch already matches what the server was written with — don't refetch on settle,
      // which would cascade re-renders and cause dnd-kit to re-run positioning. Mark stale so
      // the next natural access (page focus, mount) refreshes from server.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetails(), refetchType: 'none' })
      return await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.quoteDetails({ roomId, systemId }),
        refetchType: 'none',
      })
    },
  })
}

/** Direct members of a package header, in cache order. */
function packageMemberRows(details: QuoteDetailData[] | undefined, headerGlobalId: string): QuoteDetailData[] {
  if (!details?.length || !headerGlobalId) return []
  return details.filter(
    (d) =>
      // The accessory key wins on an illegal both-keys row, matching the sort.
      !d.dsy_cpqfields.dsy_parentglobalid && d.dsy_cpqfields.dsy_packageglobalid === headerGlobalId
  )
}

/**
 * Whether an in-flight update on `updating` also rewrites `detail` — the rows a
 * cascade owns, whose own inputs have to lock while it runs.
 *
 * An accessory is owned by the row above it (`parentDetail`), exactly as before.
 * A package member is NOT an accessory — it is a top-level row carrying the
 * header's global id — so the parent check alone leaves member inputs live
 * during a header cascade, and an edit there races the scaling. A member's own
 * accessories follow their member through the same cascade, hence the hop up.
 */
export function isQuantityCascadeTarget({
  detail,
  parentDetail,
  updating,
}: {
  detail: QuoteDetailData
  parentDetail?: QuoteDetailData | null
  updating: QuoteDetailData | undefined
}): boolean {
  if (!updating) return false
  if (parentDetail && parentDetail.quotedetailid === updating.quotedetailid) return true

  const headerGlobalId = updating.dsy_cpqfields.dsy_globalid
  if (!headerGlobalId) return false

  const memberDetail = detail.dsy_cpqfields.dsy_parentglobalid ? parentDetail : detail
  if (!memberDetail || memberDetail.dsy_cpqfields.dsy_parentglobalid) return false
  return memberDetail.dsy_cpqfields.dsy_packageglobalid === headerGlobalId
}

/**
 * The quantity a package header's input commits: whole packages, at least one.
 *
 * A product row deletes itself when its quantity falls below one. A header must
 * not — it would strand its members on the quote with nothing to group them —
 * so its input floors instead of falling into the delete branch.
 */
export function clampPackageHeaderQuantity(quantity: number) {
  return Math.max(1, Math.round(quantity))
}

export type PackageMemberQuantityScale = { quoteDetail: QuoteDetailData; quantity: number }

/**
 * What a package header's quantity edit does to its members: each one's total
 * scaled by the header's ratio, floored at one whole unit.
 *
 * Member quantities stay TOTALS on their own rows — three speakers in a package
 * sold twice is a row reading six, not a row reading three with a multiplier
 * hidden on the header. Everything downstream (the proposal, the summary
 * engine, the Won conversion) sums real rows, and none of them learn about
 * packages.
 *
 * Members only: an accessory is scaled by its own member's cascade, one level
 * down, so scaling it here as well would square the ratio. Temp rows are
 * skipped like everywhere else — an optimistic row has no id to patch.
 */
export function planPackageMemberQuantityScaling({
  members,
  previousQuantity,
  nextQuantity,
}: {
  members: QuoteDetailData[]
  previousQuantity: number
  nextQuantity: number
}): PackageMemberQuantityScale[] {
  if (previousQuantity <= 0 || nextQuantity <= 0 || previousQuantity === nextQuantity) return []

  const scaled: PackageMemberQuantityScale[] = []
  for (const member of members) {
    if (member.dsy_cpqfields.dsy_parentglobalid) continue
    if (isTemporaryQuoteDetailId(member.quotedetailid)) continue

    const quantity = getScaledChildQuantity({ quantity: member.quantity, previousQuantity, nextQuantity })
    if (quantity === member.quantity) continue

    scaled.push({ quoteDetail: member, quantity })
  }
  return scaled
}

/**
 * Every quantity a header's (or a parent product's) quantity edit moves, keyed
 * by quote detail id — the cache-side mirror of what updateQuoteDetail writes.
 *
 * The optimistic patch has to scale these too, not just the edited row: the
 * roll-up columns divide the member/accessory total by the edited row's own
 * cached quantity, so a cache holding a new header quantity beside unscaled
 * members reads "old total ÷ new quantity" for the whole in-flight window. It
 * also keeps two quick edits in a row honest — the second one scales from an
 * already-scaled base instead of the stale one.
 *
 * Same two passes, same order as the write path: a row's accessories scale by
 * its own ratio, and a package header additionally scales its members, whose
 * accessories then scale by their member's ratio (one level deep — a member is
 * never a header). Temp rows are skipped everywhere, since nothing is written
 * for them either.
 */
export function planCascadedQuantityScaling({
  details,
  quoteDetail,
  previousQuantity,
  nextQuantity,
}: {
  details: QuoteDetailData[]
  quoteDetail: QuoteDetailData
  previousQuantity: number
  nextQuantity: number
}): Map<string, number> {
  const scaled = new Map<string, number>()
  if (previousQuantity <= 0 || nextQuantity <= 0 || previousQuantity === nextQuantity) return scaled

  const scaleAccessories = (parent: QuoteDetailData, parentPrevious: number, parentNext: number) => {
    if (parent.dsy_cpqfields.dsy_parentglobalid) return
    for (const accessory of getChildQuoteDetails(details, parent.dsy_cpqfields.dsy_globalid)) {
      if (isTemporaryQuoteDetailId(accessory.quotedetailid)) continue
      const quantity = getScaledChildQuantity({
        quantity: accessory.quantity,
        previousQuantity: parentPrevious,
        nextQuantity: parentNext,
      })
      if (quantity !== accessory.quantity) scaled.set(accessory.quotedetailid, quantity)
    }
  }

  scaleAccessories(quoteDetail, previousQuantity, nextQuantity)

  if (isPackageHeader(quoteDetail.productid)) {
    const members = planPackageMemberQuantityScaling({
      members: packageMemberRows(details, quoteDetail.dsy_cpqfields.dsy_globalid ?? ''),
      previousQuantity,
      nextQuantity,
    })
    for (const { quoteDetail: member, quantity } of members) {
      scaled.set(member.quotedetailid, quantity)
      scaleAccessories(member, member.quantity, quantity)
    }
  }

  return scaled
}

const EMPTY_PACKAGE_MEMBER_IDS: { memberIds: string[]; accessoryIds: string[] } = {
  memberIds: [],
  accessoryIds: [],
}

/**
 * Everything hanging off a package header: its members (dsy_packageglobalid = the
 * header's dsy_globalid) and those members' accessories (dsy_parentglobalid = a
 * member's dsy_globalid). The header itself is never included; the transitive set
 * is `memberIds.concat(accessoryIds)`.
 *
 * Pure: it only ever sees what the caller passes, so it is only correct for callers
 * holding the complete, unfiltered detail set. deletePackage re-reads server-side
 * rather than relying on it.
 */
export function getPackageMemberIds(
  details: QuoteDetailData[] | undefined,
  headerGlobalId: string
): { memberIds: string[]; accessoryIds: string[] } {
  // Every builder row calls this through usePackageMemberIds, and only header
  // rows pass an id — without this the non-header rows each walk the whole
  // detail set, making the table O(N²).
  if (!headerGlobalId || !details?.length) return EMPTY_PACKAGE_MEMBER_IDS
  const members = packageMemberRows(details, headerGlobalId)
  const memberGlobalIds = new Set(members.map((d) => d.dsy_cpqfields.dsy_globalid).filter(Boolean))
  const accessoryIds = (details ?? [])
    .filter((d) => {
      const parentGlobalId = d.dsy_cpqfields.dsy_parentglobalid
      return !!parentGlobalId && memberGlobalIds.has(parentGlobalId)
    })
    .map((d) => d.quotedetailid)
  return { memberIds: members.map((d) => d.quotedetailid), accessoryIds }
}

const EMPTY_PACKAGE_ROWS: QuoteDetailData[] = []

/**
 * Every row a package header holds — its members AND the accessories under them
 * — read from the unfiltered cache, where usePackageMemberIds only needs ids.
 *
 * Transitive, never the direct members alone: "is this content already in the
 * package?" is a question about the whole tree. Asked of the members it answers
 * backwards for a content sitting there as a nested accessory, which is how the
 * add-items checklist came to hide the contents a package held and offer the
 * ones it already had.
 */
export function usePackageRows(headerGlobalId: string | null | undefined) {
  const { data: quoteDetails } = useQuoteDetailsQuery()
  return useMemo(() => {
    const { memberIds, accessoryIds } = getPackageMemberIds(quoteDetails, headerGlobalId ?? '')
    if (!memberIds.length && !accessoryIds.length) return EMPTY_PACKAGE_ROWS
    const packageIds = new Set([...memberIds, ...accessoryIds])
    return (quoteDetails ?? []).filter((detail) => packageIds.has(detail.quotedetailid))
  }, [quoteDetails, headerGlobalId])
}

/**
 * Transitive member set of a package header, read from the unfiltered cache so a
 * count is right even while a table is filtered to one room or system.
 */
export function usePackageMemberIds(headerGlobalId: string | null | undefined) {
  const { data: quoteDetails } = useQuoteDetailsQuery()
  return useMemo(
    () => getPackageMemberIds(quoteDetails, headerGlobalId ?? ''),
    [quoteDetails, headerGlobalId]
  )
}

/**
 * Derived package total: the header row (always $0) plus its transitive member
 * set, run through the same summary engine as every other roll-up. Nothing is
 * stored on the header — call this wherever a package price is shown.
 */
export function usePackageSummary(header: QuoteDetailData) {
  return useQuoteDetailsSummary(getRowSummaryFilters(header, { isExpanded: false }))
}

/**
 * The summary set a builder row's roll-up columns read — quantity, unit price,
 * margin, unit labor hours, unit labor price.
 *
 * A parent product rolls its accessories in while they are hidden and drops back
 * to its own numbers once they are on screen. A package header rolls in its
 * transitive member set either way: unlike a product it has no economics of its
 * own — its price is an enforced $0 — so "its own numbers" is an empty row, and
 * blanking the columns on expand is the defect this roll-up exists to fix.
 *
 * unitOnly divides by the row's own quantity, which for a header is how many
 * copies of the package the quote sells — so its unit price is the price of one
 * package. At quantity 1 that equals the Total beside it, exactly as it does on
 * a parent product selling one.
 */
export function getRowSummaryFilters(
  quoteDetail: QuoteDetailData,
  { isExpanded, unitOnly = false }: { isExpanded: boolean; unitOnly?: boolean }
): QuoteDetailFilters {
  const isHeader = isPackageHeader(quoteDetail.productid)
  return {
    quoteDetail: {
      quoteDetail,
      includeAccessories: isHeader || !isExpanded,
      includeMembers: isHeader,
      unitOnly,
    },
  }
}

/**
 * Delete a whole package: members first, then the header.
 *
 * Order matters. Each member delete is its own depth-1 request, so the
 * CascadeDeleteQuoteDetailChildren plugin removes that member's accessories.
 * Deleting the header first would have the plugin cascade the members at depth 2,
 * where its own depth guard stops — leaving their accessories orphaned.
 *
 * The header and its members are read back server-side, once, for the same reason
 * replaceQuoteDetails re-fetches its children: the client cache may be filtered
 * (the room/system tables each hold a slice) or stale, and a member missed here is
 * a member the plugin then cascades at depth 2 with its accessories left orphaned.
 */
export async function deletePackage(headerGlobalId: string) {
  if (!headerGlobalId) return
  const quoteId = getQuoteId()
  const rows = await getMultipleRecords<{
    quotedetailid: string
    dsy_cpqfields: { dsy_globalid: string | null; dsy_parentglobalid: string | null; dsy_packageglobalid: string | null }
  }>(
    'quotedetails',
    `?$filter=_quoteid_value eq ${quoteId} and (dsy_cpqfields/dsy_globalid eq '${headerGlobalId}'` +
      ` or dsy_cpqfields/dsy_packageglobalid eq '${headerGlobalId}')` +
      `&$select=quotedetailid&$expand=dsy_cpqfields($select=dsy_globalid,dsy_parentglobalid,dsy_packageglobalid)`
  )

  const headerIds: string[] = []
  const memberIds: string[] = []
  for (const row of rows) {
    const cpq = row.dsy_cpqfields
    if (cpq?.dsy_globalid === headerGlobalId) {
      headerIds.push(row.quotedetailid)
      continue
    }
    // The accessory key wins on an illegal both-keys row, matching the sort.
    if (!cpq?.dsy_parentglobalid && cpq?.dsy_packageglobalid === headerGlobalId) {
      memberIds.push(row.quotedetailid)
    }
  }

  await Promise.all(memberIds.map((id) => deleteRecord('quotedetails', id)))
  await Promise.all(headerIds.map((id) => deleteRecord('quotedetails', id)))
}

/**
 * Ungroup a package: drop every member's membership and leave the rows in place.
 * The header row is untouched — callers decide whether to delete it afterwards.
 */
export async function ungroupPackage(details: QuoteDetailData[] | undefined, headerGlobalId: string) {
  const members = packageMemberRows(details, headerGlobalId)
  await Promise.all(
    members.map((member) =>
      updateRecord<UpsertCPQFieldsBody>('dsy_lineitemcpqfieldses', member.dsy_cpqfields.dsy_lineitemcpqfieldsid, {
        dsy_packageglobalid: null,
      })
    )
  )
}

// Package names are labels on a header row, not product names: normalize so two
// authors typing the same words land on the same string.
export function normalizePackageName(name: string) {
  return name.trim().replace(/\s+/g, ' ').toUpperCase()
}

/**
 * Rows a selection is allowed to group. Top-level builder lines only:
 * accessories follow their parent, headers never nest, an existing member
 * already belongs to a package, and scope/summary rows are not products.
 */
export function canGroupIntoPackage(detail: QuoteDetailData) {
  return (
    isLineItem(detail.productid) &&
    !isPackageHeader(detail.productid) &&
    !detail.dsy_cpqfields.dsy_parentglobalid &&
    !detail.dsy_cpqfields.dsy_packageglobalid
  )
}

/**
 * Package headers an item's kebab offers it: only the ones sitting in the item's
 * OWN room AND system, in cache order.
 *
 * Nothing cross-bucket is listed. Joining a package in another room or system
 * would MOVE the line, and a move is an explicit form in this app
 * (edit-quote-detail-dialog) — never something a hover-click does silently.
 *
 * Only top-level product lines can join: accessories follow their parent and
 * headers never nest. A member is never offered the package it is already in.
 *
 * Optimistic rows are excluded on both sides. A temp row's cpqfields id is a
 * minted string no server record answers to, so joining one — or joining a
 * header that is still in flight — is a guaranteed rejected write.
 */
export function getAddToPackageOptions(
  details: QuoteDetailData[] | undefined,
  quoteDetail: QuoteDetailData
): QuoteDetailData[] {
  const fields = quoteDetail.dsy_cpqfields
  if (fields.dsy_parentglobalid) return []
  if (isTemporaryQuoteDetailId(quoteDetail.quotedetailid)) return []
  if (!isLineItem(quoteDetail.productid) || isPackageHeader(quoteDetail.productid)) return []
  if (!fields._dsy_room_value) return []

  return (details ?? []).filter((header) => {
    if (!isPackageHeader(header.productid)) return false
    if (isTemporaryQuoteDetailId(header.quotedetailid)) return false
    const headerFields = header.dsy_cpqfields
    // A header without a global id owns nothing.
    if (!headerFields.dsy_globalid) return false
    if (headerFields.dsy_globalid === fields.dsy_packageglobalid) return false
    return headerFields._dsy_room_value === fields._dsy_room_value && headerFields.dsy_system === fields.dsy_system
  })
}

/**
 * Top-level product lines a new package built in this section could take, in
 * the order the table shows them.
 *
 * A room-level create passes systemId null and gets the whole room — the dialog
 * groups those by system and lets the first checked item fix the package's own.
 *
 * Optimistic rows are excluded: adoption PATCHes a member's cpqfields row by
 * id, and a temp row's id is minted client-side, so checking one guarantees a
 * rejected write. Both kebabs already disable themselves for temp rows.
 *
 * The caller picks items by eye against the table behind the dialog, so the
 * checklist is run through sortQuoteDetails — the canonical sort source — when
 * the option sets it needs are loaded. Without them the raw cache order stands,
 * exactly as the tables fall back before their option sets resolve.
 */
export function getPackageCandidates(
  details: QuoteDetailData[] | undefined,
  scope: { roomId: string; systemId: number | null },
  sortContext?: {
    rooms: AccountRoomData[] | undefined
    systems: GlobalOptionSetDefinition | undefined
    systemScopeTags: GlobalOptionSetDefinition | undefined
  }
): QuoteDetailData[] {
  const candidates = (details ?? []).filter(
    (detail) =>
      canGroupIntoPackage(detail) &&
      !isTemporaryQuoteDetailId(detail.quotedetailid) &&
      detail.dsy_cpqfields._dsy_room_value === scope.roomId &&
      (scope.systemId === null || detail.dsy_cpqfields.dsy_system === scope.systemId)
  )

  const { rooms, systems, systemScopeTags } = sortContext ?? {}
  if (!rooms || !systems || !systemScopeTags) return candidates
  return sortQuoteDetails(candidates, rooms, systems, systemScopeTags)
}

/**
 * The system a half-built package has committed to — the first checked item's.
 * Null while nothing is checked. A package belongs to one room and system, so
 * everything outside the lock is off the table until the checks are cleared.
 *
 * Returned wrapped because dsy_system is itself nullable: "locked to Unassigned"
 * and "not locked yet" are different states.
 */
export function getPackageSystemLock(
  candidates: QuoteDetailData[],
  checkedIds: ReadonlySet<string>
): { systemId: number | null } | null {
  const first = candidates.find((candidate) => checkedIds.has(candidate.quotedetailid))
  return first ? { systemId: first.dsy_cpqfields.dsy_system } : null
}

/** Membership write for "Add to package": the item takes the header's bucket. */
export function getAddToPackageVariables(
  quoteDetail: QuoteDetailData,
  header: QuoteDetailData
): SetPackageMembershipVariables | null {
  const fields = header.dsy_cpqfields
  if (!fields._dsy_room_value) return null
  return {
    quoteDetail,
    header,
    bucket: { roomId: fields._dsy_room_value, systemId: fields.dsy_system, scopeTag: fields.dsy_scopetag },
  }
}

/**
 * Membership write for "Remove from package": the key is cleared and the row
 * stays exactly where it sits — members already live in the header's bucket.
 */
export function getRemoveFromPackageVariables(member: QuoteDetailData): SetPackageMembershipVariables | null {
  const fields = member.dsy_cpqfields
  if (!fields.dsy_packageglobalid || fields.dsy_parentglobalid || !fields._dsy_room_value) return null
  return {
    quoteDetail: member,
    header: null,
    bucket: { roomId: fields._dsy_room_value, systemId: fields.dsy_system, scopeTag: fields.dsy_scopetag },
  }
}

export type GroupIntoPackageVariables = {
  name: string
  description: string | null
  members: QuoteDetailData[]
}

/** The bucket a package lives in — taken from its first member. */
export type PackageAnchor = {
  roomId: string
  systemId: number | null
  phase: number | null
  scopeTag: number | null
}

export function getPackageAnchor(detail: QuoteDetailData): PackageAnchor | null {
  const fields = detail.dsy_cpqfields
  if (!fields._dsy_room_value) return null
  return {
    roomId: fields._dsy_room_value,
    systemId: fields.dsy_system,
    phase: fields.dsy_phase,
    scopeTag: fields.dsy_scopetag,
  }
}

export type PackageHeaderProduct = { productid: string; name: string; _defaultuomid_value: string | null }

const PACKAGE_HEADER_PRODUCT_QUERY_KEY = ['package-header-product']

/**
 * The reserved header product, read straight from the catalog.
 *
 * Deliberately NOT via useProductsQuery: that reads price-list items, which
 * would make creating a package depend on someone having added a $0 item for
 * the reserved product to every price list, in every org. The header is priced
 * at $0 by the caller, so the catalog row is all it needs.
 */
async function getPackageHeaderProduct(): Promise<PackageHeaderProduct> {
  const product = await getRecord<PackageHeaderProduct>(
    'products',
    PRODUCT_ID_MAP.PACKAGE,
    '?$select=productid,name,_defaultuomid_value'
  )
  if (!product?.productid || !product._defaultuomid_value) {
    throw new Error('The reserved package product is missing or has no default unit.')
  }
  return product
}

/**
 * The header line: an ad-hoc $0 row on the reserved PACKAGE product, carrying
 * the package name and description. It never gets a package or parent key of
 * its own — headers do not nest.
 */
export function buildPackageHeaderBody({
  name,
  description,
  anchor,
  quoteId,
  packageProduct,
  groupOrder,
}: {
  name: string
  description: string | null
  anchor: PackageAnchor
  quoteId: string
  packageProduct: PackageHeaderProduct
  groupOrder: number
}): CreateQuoteDetailBody {
  return {
    productName: packageProduct.name,
    cpqFieldsBody: {
      dsy_system: anchor.systemId,
      dsy_phase: anchor.phase,
      dsy_scopetag: anchor.scopeTag,
      dsy_grouporder: groupOrder,
      'dsy_room@odata.bind': `/dsy_accountrooms(${anchor.roomId})`,
    },
    quoteDetailBody: {
      quotedetailname: normalizePackageName(name),
      description,
      quantity: 1,
      msdyn_costpriceperunit: 0,
      msdyn_costamount: 0,
      priceperunit: 0,
      baseamount: 0,
      extendedamount: 0,
      tax: 0,
      msdyn_taxable: false,
      'quoteid@odata.bind': `/quotes(${quoteId})`,
      'productid@odata.bind': `/products(${packageProduct.productid})`,
      'uomid@odata.bind': `/uoms(${packageProduct._defaultuomid_value})`,
    },
  }
}

/**
 * Point an authoring header at the catalog product just saved out of it.
 *
 * ONLY the product bind: quotedetailname, the description and every cpqfield are
 * the author's, and a save must not rewrite what is on the quote. The bind is
 * the same shape the insert path writes, and it is the whole difference between
 * the two header kinds — isCatalogPackage is dsy_ispackage plus "not the
 * reserved GUID", so re-binding is what turns "Save to catalog" into
 * "Update catalog package".
 */
export function buildCatalogHeaderRebindBody(productId: string): UpsertQuoteDetailBody {
  return { 'productid@odata.bind': `/products(${productId})` }
}

/**
 * One member's cpqfields patch: the package key plus its slot inside the
 * header's own space. Room, system and scope are written only when they
 * actually change — updateQuoteDetail cascades room and system to the member's
 * accessories, and an unchanged value would issue a pointless write per
 * accessory. dsy_parentglobalid is never touched: writing both link keys is
 * rejected by the server-side XOR plugin.
 */
export function buildPackageMemberPatch({
  member,
  headerGlobalId,
  groupOrder,
  anchor,
}: {
  member: QuoteDetailData
  headerGlobalId: string
  groupOrder: number
  anchor: PackageAnchor
}): UpsertCPQFieldsBody {
  const fields = member.dsy_cpqfields
  const cpqFieldsBody: UpsertCPQFieldsBody = {
    dsy_packageglobalid: headerGlobalId,
    dsy_grouporder: groupOrder,
  }
  if (fields._dsy_room_value !== anchor.roomId) {
    cpqFieldsBody['dsy_room@odata.bind'] = `/dsy_accountrooms(${anchor.roomId})`
  }
  if (fields.dsy_system !== anchor.systemId) cpqFieldsBody.dsy_system = anchor.systemId
  if (fields.dsy_scopetag !== anchor.scopeTag) cpqFieldsBody.dsy_scopetag = anchor.scopeTag
  return cpqFieldsBody
}

/**
 * Adopt the selected rows into a freshly created header, numbered 1..N in the
 * header's own slot space.
 *
 * allSettled, not all: one rejected member must not leave a half-built package
 * behind. Nothing adopted means there is no package worth keeping, so the
 * just-created header is removed and the error surfaces; a partial adoption
 * keeps the package and says how much of it landed.
 */
export async function adoptPackageMembers({
  members,
  headerQuoteDetailId,
  headerGlobalId,
  anchor,
}: {
  members: QuoteDetailData[]
  headerQuoteDetailId: string
  headerGlobalId: string
  anchor: PackageAnchor
}) {
  const settled = await Promise.allSettled(
    members.map((member, index) =>
      updateQuoteDetail({
        quoteDetail: member,
        quoteDetailBody: {},
        cpqFieldsBody: buildPackageMemberPatch({ member, headerGlobalId, groupOrder: index + 1, anchor }),
      })
    )
  )

  const failures = settled.filter((result) => result.status === 'rejected')
  if (failures.length && failures.length === members.length) {
    // The header is unreachable from the UI without members, so take it back
    // out. A failed cleanup must not mask the write error that caused it.
    await deleteRecord('quotedetails', headerQuoteDetailId).catch(() => undefined)
    throw failures[0].reason
  }
  if (failures.length) {
    toast({
      title: 'Package created without every item',
      description:
        `Added ${members.length - failures.length} of ${members.length} items.` +
        ' Use the item menu → Add to package for the rest.',
      variant: 'destructive',
    })
  }
  return { adopted: members.length - failures.length, failed: failures.length }
}

/**
 * Package writes touch quotedetail and cpqfields rows, so every one of them is a
 * quote EDIT: none carries NON_QUOTE_EDIT_MUTATION_KEY_ROOT, which is exactly
 * what keeps the activation gate waiting on them.
 */
export const PACKAGE_MUTATION_KEYS = {
  group: ['package', 'group'],
  membership: ['package', 'membership'],
  move: ['package', 'move'],
  ungroup: ['package', 'ungroup'],
  delete: ['package', 'delete'],
  bindCatalog: ['package', 'bind-catalog'],
} as const

// A package spans whatever buckets its members came from, so every package write
// invalidates the whole quotedetails root rather than guessing at room/system keys.
async function invalidateAllQuoteDetails(client: QueryClient) {
  await client.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailsRoot })
}

/**
 * Create a package header out of the items the create dialog checked.
 *
 * The header is an ad-hoc $0 line bound to the reserved PACKAGE product; its
 * dsy_globalid (server-assigned, read back once) becomes every member's
 * dsy_packageglobalid. Members are moved into the header's room/system/scope so
 * the sort keeps them under it, and dsy_grouporder is renumbered 1..N inside the
 * header's own slot space.
 *
 * At least one member is required. A header with nothing under it is a
 * customer-visible defect — the proposal renders it as "0 items — $0.00" and it
 * still materializes into salesorderdetails at Won.
 */
export function useGroupIntoPackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...PACKAGE_MUTATION_KEYS.group],
    mutationFn: async ({ name, description, members }: GroupIntoPackageVariables) => {
      const groupable = members.filter(canGroupIntoPackage)
      if (!groupable.length) throw new Error('Pick at least one item for the package')

      if (!normalizePackageName(name)) throw new Error('Package name is required')

      // The first member anchors the package: its room, system and scope become
      // the header's bucket and every member is moved into it.
      const anchor = getPackageAnchor(groupable[0])
      if (!anchor) throw new Error('A package must belong to a room')

      const cache = queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails())
      const packageProduct = await queryClient.ensureQueryData({
        queryKey: PACKAGE_HEADER_PRODUCT_QUERY_KEY,
        queryFn: getPackageHeaderProduct,
      })

      const headerQuoteDetailId = await createQuoteDetail(
        buildPackageHeaderBody({
          name,
          description,
          anchor,
          quoteId: getQuoteId(),
          packageProduct,
          groupOrder: getEndOfGroupOrder(cache, {
            roomId: anchor.roomId,
            systemId: anchor.systemId,
            scopeTag: anchor.scopeTag,
            packageGlobalId: null,
            parentGlobalId: null,
          }),
        })
      )

      const headerGlobalId = (await getCreatedGlobalIdsByQuoteDetailId([headerQuoteDetailId])).get(
        headerQuoteDetailId
      )
      if (!headerGlobalId) {
        throw new Error('The package header was created without a global id. Refresh the quote and try again.')
      }

      // The platform stamps quotedetailname from the bound product on create
      // (the row is not product-overridden), so a header created with a package
      // name comes back reading "PACKAGE". The name only sticks on a PATCH.
      await updateRecord('quotedetails', headerQuoteDetailId, {
        quotedetailname: normalizePackageName(name),
      })

      await adoptPackageMembers({ members: groupable, headerQuoteDetailId, headerGlobalId, anchor })

      return headerGlobalId
    },
    onError: (error) => {
      toast({ title: 'Error creating package', error, variant: 'destructive' })
    },
    onSettled: async () => {
      await invalidateAllQuoteDetails(queryClient)
    },
  })
}

export type SetPackageMembershipVariables = {
  quoteDetail: QuoteDetailData
  /** Header to join, or null to leave the package the row is currently in. */
  header: QuoteDetailData | null
  /** Where the row lands: the header's bucket when joining, the drop target's when leaving. */
  bucket: { roomId: string; systemId: number | null; scopeTag: number | null }
}

/** Where a row lands — "Room · System · Scope", the three a membership write moves it into. */
export function getBucketLabel(bucket: { roomId: string | null; systemId: number | null; scopeTag: number | null }) {
  return [
    bucket.roomId ? getRoomLabel(bucket.roomId) : null,
    getSystemLabel(bucket.systemId),
    getSystemScopeTagLabel(bucket.scopeTag),
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Where a package sits — "Room · System · Scope". Joining it moves the line into
 * all three, so the destination is spelled out wherever the move is offered or
 * reported.
 */
export function getPackageBucketLabel(header: QuoteDetailData): string {
  const fields = header.dsy_cpqfields
  return getBucketLabel({
    roomId: fields._dsy_room_value,
    systemId: fields.dsy_system,
    scopeTag: fields.dsy_scopetag,
  })
}

/**
 * What a finished membership write has to say, or null for the one case that
 * says nothing.
 *
 * Joining a package always names it: the row takes the package's room, system
 * AND scope tag. Leaving one takes the DROP TARGET's bucket, which rewrites the
 * same three fields just as silently — so it is reported whenever any of them
 * actually changed, and stays quiet when the row only left the package.
 */
export function getPackageMembershipToastKind({
  quoteDetail,
  header,
  bucket,
}: SetPackageMembershipVariables): 'join' | 'bucket' | null {
  if (header) return 'join'
  const fields = quoteDetail.dsy_cpqfields
  const movedBuckets =
    fields._dsy_room_value !== bucket.roomId ||
    fields.dsy_system !== bucket.systemId ||
    fields.dsy_scopetag !== bucket.scopeTag
  return movedBuckets ? 'bucket' : null
}

/**
 * Move one top-level row into or out of a package (drag across a package
 * boundary). The row takes the target bucket and the next free slot in it —
 * a member numbers inside its header's slot space, an unpackaged row inside
 * (room, system, scope).
 */
export function useSetPackageMembership() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...PACKAGE_MUTATION_KEYS.membership],
    mutationFn: async ({ quoteDetail, header, bucket }: SetPackageMembershipVariables) => {
      if (quoteDetail.dsy_cpqfields.dsy_parentglobalid) {
        throw new Error('Accessories follow their parent and cannot join a package on their own')
      }
      const headerGlobalId = header?.dsy_cpqfields.dsy_globalid ?? null
      if (header && !headerGlobalId) throw new Error('Package header is missing its global id')

      const cache = queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails())
      const cpqFieldsBody: UpsertCPQFieldsBody = {
        dsy_packageglobalid: headerGlobalId,
        dsy_grouporder: getEndOfGroupOrder(cache, {
          roomId: bucket.roomId,
          systemId: bucket.systemId,
          scopeTag: bucket.scopeTag,
          packageGlobalId: headerGlobalId,
          parentGlobalId: null,
        }),
      }
      const fields = quoteDetail.dsy_cpqfields
      if (fields._dsy_room_value !== bucket.roomId) {
        cpqFieldsBody['dsy_room@odata.bind'] = `/dsy_accountrooms(${bucket.roomId})`
      }
      if (fields.dsy_system !== bucket.systemId) cpqFieldsBody.dsy_system = bucket.systemId
      if (fields.dsy_scopetag !== bucket.scopeTag) cpqFieldsBody.dsy_scopetag = bucket.scopeTag

      await updateQuoteDetail({ quoteDetail, quoteDetailBody: {}, cpqFieldsBody })
    },
    // Here rather than at the call sites: the kebab, both tables' drags and both
    // directions all land in this one mutation, and the call-site version of
    // this toast could only see the joining half.
    onSuccess: (_data, variables) => {
      const kind = getPackageMembershipToastKind(variables)
      if (!kind) return
      if (kind === 'join' && variables.header) {
        toast({ title: `Moved to ${variables.header.quotedetailname} — ${getPackageBucketLabel(variables.header)}` })
        return
      }
      // Naming the bucket needs the option sets; without them there is still a
      // move to report, just not where to.
      const bucketLabel = getBucketLabel(variables.bucket)
      toast({ title: bucketLabel ? `Moved to ${bucketLabel}` : 'Moved out of the package' })
    },
    onError: (error) => {
      toast({ title: 'Error moving item', error, variant: 'destructive' })
    },
    onSettled: async () => {
      await invalidateAllQuoteDetails(queryClient)
    },
  })
}

export type MovePackageDestination = { roomId: string; systemId: number | null }

/** One row of a move: the row and the cpqfields patch that relocates it. */
export type MovePackagePatch = { quoteDetail: QuoteDetailData; cpqFieldsBody: UpsertCPQFieldsBody }

/**
 * The writes that move a whole package into another room and system: the header
 * first, then its members in the order they read on screen.
 *
 * A ROOM change leaves scope tags alone. A package's scope is the work it
 * belongs to, not where it sits, and rewriting it here would move every member's
 * line in the scope-of-work export as a side effect of a room change.
 *
 * A SYSTEM change has no such choice: a scope tag belongs to exactly one system
 * (its label leads with the system's), so the old tag names a scope the
 * destination does not have — the proposal and the scope-of-work CSV would group
 * those lines under a foreign scope, and the row's own picker could not
 * re-select the value it shows. Every patched row takes the destination's Other
 * scope, which is the rule the per-row system picker already applies.
 *
 * Link keys are untouched too — a move never regroups anything, so no row's
 * package/parent XOR pair is written.
 *
 * Slots are dealt out from the end of the DESTINATION bucket, one running
 * counter over header-then-members. The header needs it or it lands in the new
 * room holding its old room's slot; the members' slot space is the package key
 * alone (see getEndOfGroupOrder), so numbering them from the same counter
 * preserves their order without colliding with anything.
 *
 * Accessories are not patched and must not be: updateQuoteDetail cascades room
 * and system to each member's children.
 */
export function buildMovePackagePlan({
  header,
  memberRows,
  destination,
  cache,
}: {
  header: QuoteDetailData
  /** The header's rows — accessories may be included; they are filtered out. */
  memberRows: QuoteDetailData[]
  destination: MovePackageDestination
  cache: QuoteDetailData[] | undefined
}): MovePackagePatch[] {
  const headerFields = header.dsy_cpqfields
  const headerGlobalId = headerFields.dsy_globalid
  if (!headerGlobalId) throw new Error('Package header is missing its global id')

  const members = memberRows
    .filter(
      (row) =>
        row.dsy_cpqfields.dsy_packageglobalid === headerGlobalId && !row.dsy_cpqfields.dsy_parentglobalid
    )
    .sort((a, b) => (a.dsy_cpqfields.dsy_grouporder ?? 0) - (b.dsy_cpqfields.dsy_grouporder ?? 0))

  const isSystemChange = destination.systemId !== headerFields.dsy_system
  const scopeTag = isSystemChange
    ? getOtherScopeTagValueForSystem(destination.systemId)
    : (headerFields.dsy_scopetag ?? null)

  let slot = getEndOfGroupOrder(cache, {
    roomId: destination.roomId,
    systemId: destination.systemId,
    scopeTag,
    packageGlobalId: null,
    parentGlobalId: null,
  })

  // Room and system go on EVERY patch, unchanged or not: updateQuoteDetail
  // cascades to a member's accessories only when room, system, scope or quantity
  // is in the body, so a member already sitting in the destination bucket would
  // otherwise leave its accessories in the room the package left.
  return [header, ...members].map((quoteDetail) => {
    const cpqFieldsBody: UpsertCPQFieldsBody = {
      dsy_grouporder: slot++,
      'dsy_room@odata.bind': `/dsy_accountrooms(${destination.roomId})`,
      dsy_system: destination.systemId,
      ...(isSystemChange ? { dsy_scopetag: scopeTag } : {}),
    }
    return { quoteDetail, cpqFieldsBody }
  })
}

/**
 * Move a package — header, members and, through the update cascade, their
 * accessories — into another room and system.
 *
 * allSettled: a package half in each room is bad, but a package whose move was
 * abandoned mid-way because one member rejected is the same thing with no
 * report. Only a total failure throws; a partial one names how much moved, and
 * the retry is the same action again.
 */
export function useMovePackage({ header, memberRows }: { header: QuoteDetailData; memberRows: QuoteDetailData[] }) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...PACKAGE_MUTATION_KEYS.move],
    mutationFn: async (destination: MovePackageDestination) => {
      const patches = buildMovePackagePlan({
        header,
        memberRows,
        destination,
        cache: queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails()),
      })

      const settled = await Promise.allSettled(
        patches.map(({ quoteDetail, cpqFieldsBody }) =>
          updateQuoteDetail({ quoteDetail, quoteDetailBody: {}, cpqFieldsBody })
        )
      )
      const failures = settled.filter((result) => result.status === 'rejected')
      if (failures.length === patches.length) throw failures[0].reason
      return { moved: patches.length - failures.length, failed: failures.length }
    },
    onSuccess: ({ moved, failed }) => {
      if (!failed) {
        toast({ title: 'Package moved', variant: 'success' })
        return
      }
      toast({
        title: 'Package moved without every item',
        description: `Moved ${moved} of ${moved + failed} rows. The rest are still where they were.`,
        variant: 'destructive',
      })
    },
    onError: (error) => {
      toast({ title: 'Error moving package', error, variant: 'destructive' })
    },
    // The root key covers both the room and system it left and the ones it
    // joined, which is what every other package write invalidates.
    onSettled: async () => {
      await invalidateAllQuoteDetails(queryClient)
    },
  })
}

/** What the item form submits for a package header. */
export type PackageHeaderEditValues = {
  name: string
  /** Rich-text HTML, or null when the author cleared it. */
  description: string | null
  roomId: string
  systemId: number | null
  scopeTag: number | null
  quantity: number
}

export type PackageHeaderEditPlan = {
  /** The header's own patch, through updateQuoteDetail. */
  quoteDetailBody: UpsertQuoteDetailBody
  cpqFieldsBody: UpsertCPQFieldsBody
  /** Where the whole package relocates to, through useMovePackage — or null. */
  move: MovePackageDestination | null
}

/**
 * How one submit of a package header's item form is routed.
 *
 * A header is a parent product: the same form edits it, and each field goes to
 * the machinery that already owns that field for a whole package rather than
 * being PATCHed onto the header row.
 *
 * - Room and system are NEVER in the cpqfields patch. A package moves as a unit,
 *   so they become a `move` and run through buildMovePackagePlan, which carries
 *   the members (and, through their own cascades, their accessories) and deals
 *   fresh slots in the destination.
 * - The scope tag is a plain header patch, because updateQuoteDetail cascades a
 *   header's scope onto its members — EXCEPT across a system change, where the
 *   move plan already remaps every row to the destination's Other scope. Writing
 *   it here as well would patch the same value onto the same rows twice.
 * - Quantity is a plain header patch too, and that is the point: it re-enters
 *   updateQuoteDetail, which scales the members. A raw PATCH would leave them.
 * - Name and description write exactly as the old package edit dialog wrote
 *   them — normalized uppercase, and null rather than an empty rich-text shell.
 *
 * Pricing and labor are absent by construction: the form does not show them on
 * a header, and nothing here would carry them if it did.
 */
export function buildPackageHeaderEditPlan({
  header,
  values,
}: {
  header: QuoteDetailData
  values: PackageHeaderEditValues
}): PackageHeaderEditPlan {
  const fields = header.dsy_cpqfields

  const isRoomChange = !!values.roomId && values.roomId !== fields._dsy_room_value
  const isSystemChange = values.systemId !== fields.dsy_system
  const move = isRoomChange || isSystemChange ? { roomId: values.roomId, systemId: values.systemId } : null

  const quoteDetailBody: UpsertQuoteDetailBody = {}
  const name = normalizePackageName(values.name)
  if (name && name !== header.quotedetailname) quoteDetailBody.quotedetailname = name
  if (values.description !== (header.description ?? null)) quoteDetailBody.description = values.description

  const quantity = clampPackageHeaderQuantity(values.quantity)
  if (quantity !== header.quantity) quoteDetailBody.quantity = quantity

  const cpqFieldsBody: UpsertCPQFieldsBody = {}
  if (!isSystemChange && values.scopeTag !== fields.dsy_scopetag) {
    cpqFieldsBody.dsy_scopetag = values.scopeTag ?? null
  }

  return { quoteDetailBody, cpqFieldsBody, move }
}

/** Drop every member's membership, then delete the now-empty header row. */
export function useUngroupPackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...PACKAGE_MUTATION_KEYS.ungroup],
    mutationFn: async (header: QuoteDetailData) => {
      const headerGlobalId = header.dsy_cpqfields.dsy_globalid
      if (!headerGlobalId) throw new Error('Package header is missing its global id')
      await ungroupPackage(queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails()), headerGlobalId)
      await deleteRecord('quotedetails', header.quotedetailid)
    },
    onError: (error) => {
      toast({ title: 'Error ungrouping package', error, variant: 'destructive' })
    },
    onSettled: async () => {
      await invalidateAllQuoteDetails(queryClient)
    },
  })
}

/**
 * Adopt the catalog product a package was just saved as, on the header it was
 * saved from.
 *
 * Without it the author's own header stays bound to the reserved ad-hoc PACKAGE
 * product, so its menu keeps offering "Save to catalog" — which the catalog's
 * name guard then rejects, leaving delete-and-reinsert as the only way to push a
 * tweak back. Re-bound, the header reads as the catalog package it now is and
 * the menu offers "Update catalog package".
 *
 * Silent on both ends: the save already toasted, and the catalog entry exists
 * whatever happens here. A failed rebind leaves the author exactly where this
 * fix found them rather than reporting the save as broken.
 */
export function useBindHeaderToCatalogPackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...PACKAGE_MUTATION_KEYS.bindCatalog],
    mutationFn: async ({ header, productId }: { header: QuoteDetailData; productId: string }) => {
      await updateRecord('quotedetails', header.quotedetailid, buildCatalogHeaderRebindBody(productId))
    },
    onSettled: async () => {
      await invalidateAllQuoteDetails(queryClient)
    },
  })
}

/** Delete the header and everything under it (members, then their accessories). */
export function useDeletePackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [...PACKAGE_MUTATION_KEYS.delete],
    mutationFn: async (header: QuoteDetailData) => {
      const headerGlobalId = header.dsy_cpqfields.dsy_globalid
      if (!headerGlobalId) throw new Error('Package header is missing its global id')
      await deletePackage(headerGlobalId)
    },
    onError: (error) => {
      toast({ title: 'Error deleting package', error, variant: 'destructive' })
    },
    onSettled: async () => {
      await invalidateAllQuoteDetails(queryClient)
    },
  })
}

async function deleteQuoteDetails(quoteDetails: QuoteDetailData[]) {
  // The CascadeDeleteQuoteDetailChildren plugin (Dataverse, registered on quotedetail
  // Delete) handles cascading child accessories AND cleanup of each deleted row's own
  // dsy_lineitemcpqfields. Frontend only needs to delete the quotedetails.
  //
  // Drop children whose parent is also in the deletion set — the plugin's pre-op cascade
  // will already have removed them by the time the frontend's request lands, returning
  // 404 and rejecting the Promise.all. Many call sites still bundle [parent, ...subRows];
  // filtering here keeps them working without a coordinated component refactor.
  // Holds every deleted row's own dsy_globalid — accessory parents AND package
  // headers, since both keys point at one.
  const deletedGlobalIds = new Set(
    quoteDetails.map((qd) => qd.dsy_cpqfields.dsy_globalid).filter(Boolean) as string[]
  )
  const toDelete = quoteDetails.filter((qd) => {
    const parentGlobalId = qd.dsy_cpqfields.dsy_parentglobalid
    return !parentGlobalId || !deletedGlobalIds.has(parentGlobalId)
  })
  // Package members can't be dropped the same way: the plugin cascades them from
  // the header at depth 2, where its own depth guard stops their accessories from
  // being cleaned up. Delete them first instead — same ordering as deletePackage —
  // so each member's accessories cascade from its own depth-1 request.
  //
  // Throttled to 12 in flight either way. Each DELETE fires the cascade plugin; an
  // unbounded burst risks Dataverse 429s and lets a sibling request commit after
  // the error handler has already refetched server truth.
  //
  // A row already gone (404, or the 400 a double-click's second DELETE gets) is
  // deleted: tolerating it keeps the rest of the batch going and skips the
  // false error toast and rollback.
  const deleteInChunks = (details: QuoteDetailData[]) =>
    applyQuoteDetailReorderInChunks(details, (quoteDetail) =>
      deleteRecord('quotedetails', quoteDetail.quotedetailid).catch((error: unknown) => {
        if (!isAlreadyDeletedError(error)) throw error
      })
    )
  const membersFirst = toDelete.filter((qd) => {
    const packageGlobalId = qd.dsy_cpqfields.dsy_packageglobalid
    return !!packageGlobalId && deletedGlobalIds.has(packageGlobalId)
  })
  if (membersFirst.length) {
    const memberIds = new Set(membersFirst.map((qd) => qd.quotedetailid))
    await deleteInChunks(membersFirst)
    await deleteInChunks(toDelete.filter((quoteDetail) => !memberIds.has(quoteDetail.quotedetailid)))
    return
  }
  await deleteInChunks(toDelete)
}

export function useDeleteQuoteDetails(
  options?: UseMutationOptions<unknown, Error, QuoteDetailData[], OptimisticContext>
) {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, QuoteDetailData[], OptimisticContext>({
    mutationKey: ['delete', 'quotedetails'],
    mutationFn: deleteQuoteDetails,
    onMutate: async (quoteDetails) => {
      if (!quoteDetails.length) return { previous: [] as QuoteDetailCacheSnapshot }

      // Global IDs are shared across quote versions. Never cascade cache edits
      // into the previous approved quote used to calculate change-order deltas.
      const currentQuoteDetails = { queryKey: ['quotedetails', { previousQuote: false }] }
      await queryClient.cancelQueries(currentQuoteDetails)

      const previous: QuoteDetailCacheSnapshot = queryClient.getQueriesData<QuoteDetailData[]>(currentQuoteDetails)

      // undefined = bail out of the write so untouched entries keep their
      // dataUpdatedAt and the summary query keys (which embed it) don't rotate.
      // Accessories and package members go too, or they linger as ghost rows
      // until the refetch lands.
      queryClient.setQueriesData<QuoteDetailData[]>(currentQuoteDetails, (cached) => {
        if (!cached) return undefined
        const next = withoutDeletedRows(cached, quoteDetails)
        return next.length === cached.length ? undefined : next
      })

      return { previous }
    },
    onError: async (error, _quoteDetails, context) => {
      if (context?.previous) {
        await restoreAndRefetchQuoteDetailCaches(queryClient, context.previous)
      }
      toast({
        title: 'Error deleting item',
        error: error,
        variant: 'destructive',
      })
    },
    onSettled: async (_, __, quoteDetails) => {
      // While another quote-line delete runs, a refetch can land before its
      // DELETE commits and put its hidden row back. The last one (this one
      // still counts until onSettled returns) refetches.
      if (queryClient.isMutating({ mutationKey: ['delete', 'quotedetails'] }) > 1) return

      const uniqueRooms = new Set<string>()
      const uniqueSystems = new Set<number>()

      quoteDetails.forEach((quoteDetail) => {
        if (quoteDetail.dsy_cpqfields._dsy_room_value) {
          uniqueRooms.add(quoteDetail.dsy_cpqfields._dsy_room_value)
        }
        if (quoteDetail.dsy_cpqfields.dsy_system) {
          uniqueSystems.add(quoteDetail.dsy_cpqfields.dsy_system)
        }
      })

      const queryKeys = [QUERY_KEYS.quoteDetails()]
      const awaitQueryKeys = []

      if (quoteDetails.some((quoteDetail) => isSummaryDetail(quoteDetail.productid.productid))) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' }))
      }

      for (const roomId of uniqueRooms) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ roomId }))

        for (const systemId of uniqueSystems) {
          awaitQueryKeys.push(QUERY_KEYS.quoteDetails({ roomId, systemId }))
        }
      }

      for (const systemId of uniqueSystems) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ systemId }))
      }

      const uniqueQueryKeys = getUniqueQueryKeys(queryKeys)
      Promise.all(uniqueQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      const uniqueAwaitQueryKeys = getUniqueQueryKeys(awaitQueryKeys)
      return await Promise.all(uniqueAwaitQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
    },
    ...options,
  })
}

export type ReplaceQuoteDetailsBody = {
  quoteDetails: QuoteDetailData[]
  replacementProduct: PriceListItemData
  updateFields: {
    description: boolean
    price: boolean
    cost: boolean
    taxable: boolean
    labor: boolean
  }
}

async function replaceQuoteDetails({ quoteDetails, replacementProduct, updateFields }: ReplaceQuoteDetailsBody) {
  const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote())

  if (!quote) {
    throw new Error('Quote not found')
  }

  // Fetch every existing child accessory AND package member of the rows being
  // replaced once, server-side. Client cache may be filtered or stale; we MUST
  // re-point both link keys to the new row's globalid before deleting the old
  // row — otherwise the cascade-delete plugin on quotedetail Delete (which now
  // matches dsy_parentglobalid OR dsy_packageglobalid) removes them as orphans.
  const oldGlobalIds = quoteDetails.map((qd) => qd.dsy_cpqfields.dsy_globalid).filter(Boolean) as string[]
  const childrenByOldGlobalId = new Map<string, { dsy_lineitemcpqfieldsid: string }[]>()
  const membersByOldGlobalId = new Map<string, { dsy_lineitemcpqfieldsid: string }[]>()
  if (oldGlobalIds.length > 0) {
    const filter = oldGlobalIds
      .map((g) => `dsy_cpqfields/dsy_parentglobalid eq '${g}' or dsy_cpqfields/dsy_packageglobalid eq '${g}'`)
      .join(' or ')
    const allChildren = await getMultipleRecords<{
      quotedetailid: string
      dsy_cpqfields: {
        dsy_lineitemcpqfieldsid: string
        dsy_parentglobalid: string | null
        dsy_packageglobalid: string | null
      }
    }>(
      'quotedetails',
      `?$filter=_quoteid_value eq ${quote.quoteid} and (${filter})` +
        `&$select=quotedetailid&$expand=dsy_cpqfields($select=dsy_lineitemcpqfieldsid,dsy_parentglobalid,dsy_packageglobalid)`
    )
    for (const child of allChildren) {
      const row = { dsy_lineitemcpqfieldsid: child.dsy_cpqfields.dsy_lineitemcpqfieldsid }
      // Keys are mutually exclusive (server-side XOR); check the accessory key
      // first so an illegal both-keys row keeps pre-package behaviour.
      const parentGlobalId = child.dsy_cpqfields.dsy_parentglobalid
      if (parentGlobalId) {
        childrenByOldGlobalId.set(parentGlobalId, [...(childrenByOldGlobalId.get(parentGlobalId) ?? []), row])
        continue
      }
      const packageGlobalId = child.dsy_cpqfields.dsy_packageglobalid
      if (packageGlobalId) {
        membersByOldGlobalId.set(packageGlobalId, [...(membersByOldGlobalId.get(packageGlobalId) ?? []), row])
      }
    }
  }

  const promises = []

  for (const quoteDetail of quoteDetails) {
    // Get existing values from the old quote detail
    const existingCpqFields = quoteDetail.dsy_cpqfields
    const roomId = existingCpqFields._dsy_room_value
    const systemId = existingCpqFields.dsy_system
    const phaseId = existingCpqFields.dsy_phase
    const scopeTag = existingCpqFields.dsy_scopetag
    const parentGlobalId = existingCpqFields.dsy_parentglobalid
    const location = existingCpqFields.dsy_location
    const laborFactorPercent = existingCpqFields.dsy_laborfactorpercent

    // Build CPQ fields for the new quote detail. Preserve existing dsy_grouporder
    // so the replaced row keeps its slot within the (room, system, scope, parent) bucket.
    const cpqFieldsBody: UpsertCPQFieldsBody = {
      dsy_system: systemId,
      dsy_phase: phaseId,
      dsy_scopetag: scopeTag,
      dsy_parentglobalid: parentGlobalId,
      // The replaced row keeps its own package membership. Never set alongside
      // dsy_parentglobalid — stored rows satisfy the server-side XOR.
      dsy_packageglobalid: existingCpqFields.dsy_packageglobalid ?? null,
      dsy_grouporder: existingCpqFields.dsy_grouporder,
      dsy_location: location,
      dsy_laborfactorpercent: laborFactorPercent,
      'dsy_room@odata.bind': roomId ? `/dsy_accountrooms(${roomId})` : null,
    }

    // Set labor values based on updateFields.labor flag
    if (updateFields.labor) {
      // Use labor from the new product
      cpqFieldsBody.dsy_engineeringlabor = replacementProduct.dsy_engineeringlabor ?? 0
      cpqFieldsBody.dsy_installationlabor = replacementProduct.dsy_installationlabor ?? 0
      cpqFieldsBody.dsy_roughinlabor = replacementProduct.dsy_roughinlabor ?? 0
      cpqFieldsBody.dsy_programminglabor = replacementProduct.dsy_programminglabor ?? 0
      cpqFieldsBody.dsy_projectmanagementlabor = replacementProduct.dsy_projectmanagementlabor ?? 0
      cpqFieldsBody.dsy_servicelabor = replacementProduct.dsy_servicelabor ?? 0
    } else {
      // Keep existing labor values
      cpqFieldsBody.dsy_engineeringlabor = existingCpqFields.dsy_engineeringlabor ?? 0
      cpqFieldsBody.dsy_installationlabor = existingCpqFields.dsy_installationlabor ?? 0
      cpqFieldsBody.dsy_roughinlabor = existingCpqFields.dsy_roughinlabor ?? 0
      cpqFieldsBody.dsy_programminglabor = existingCpqFields.dsy_programminglabor ?? 0
      cpqFieldsBody.dsy_projectmanagementlabor = existingCpqFields.dsy_projectmanagementlabor ?? 0
      cpqFieldsBody.dsy_servicelabor = existingCpqFields.dsy_servicelabor ?? 0
    }

    // Calculate price, cost, tax values
    const quantityDecimal = new Decimal(quoteDetail.quantity)
    const unitPriceDecimal = updateFields.price
      ? new Decimal(replacementProduct.dsy_unitprice ?? 0)
      : new Decimal(quoteDetail.priceperunit ?? 0)
    const unitCostDecimal = updateFields.cost
      ? new Decimal(replacementProduct.dsy_unitcost ?? 0)
      : new Decimal(quoteDetail.msdyn_costpriceperunit ?? 0)
    const isTaxable = updateFields.taxable ? !!replacementProduct.dsy_taxable : !!quoteDetail.msdyn_taxable
    const tax = isTaxable ? getTaxDecimal(unitPriceDecimal, quantityDecimal).toNumber() : 0
    const totalPriceDecimal = unitPriceDecimal.mul(quantityDecimal)
    const totalPriceWithTax = totalPriceDecimal.plus(tax)
    const totalCostDecimal = unitCostDecimal.mul(quantityDecimal)

    // Build quote detail body for the new quote detail
    const quoteDetailBody: UpsertQuoteDetailBody = {
      quotedetailname: replacementProduct.dsy_product.name,
      description: updateFields.description ? replacementProduct.dsy_description : quoteDetail.description,
      quantity: quantityDecimal.toNumber(),
      priceperunit: unitPriceDecimal.toDecimalPlaces(2).toNumber(),
      msdyn_costpriceperunit: unitCostDecimal.toDecimalPlaces(2).toNumber(),
      msdyn_costamount: totalCostDecimal.toDecimalPlaces(2).toNumber(),
      msdyn_costtotal: totalCostDecimal.toDecimalPlaces(2).toNumber(),
      msdyn_estimatedcost: totalCostDecimal.toDecimalPlaces(2).toNumber(),
      baseamount: totalPriceDecimal.toDecimalPlaces(2).toNumber(),
      extendedamount: totalPriceWithTax.toDecimalPlaces(2).toNumber(),
      tax: tax,
      msdyn_taxable: isTaxable,
      manualdiscountamount: quoteDetail.manualdiscountamount,
      'quoteid@odata.bind': `/quotes(${quote.quoteid})`,
      'productid@odata.bind': `/products(${replacementProduct.dsy_product.productid})`,
    }

    if (replacementProduct.dsy_product._defaultuomid_value) {
      quoteDetailBody['uomid@odata.bind'] = `/uoms(${replacementProduct.dsy_product._defaultuomid_value})`
    }

    promises.push(
      (async () => {
        const oldGlobalId = quoteDetail.dsy_cpqfields.dsy_globalid

        // 1. Create the new quotedetail. Server auto-generates a fresh dsy_globalid.
        const newQuoteDetailId = await createQuoteDetail({ cpqFieldsBody, quoteDetailBody, productName: replacementProduct.dsy_product.name })

        // 2. Read back the new globalid (auto-number `{RANDSTRING:6}-{RANDSTRING:6}-{RANDSTRING:6}`).
        const newQuoteDetail = (await getRecord(
          'quotedetails',
          newQuoteDetailId,
          '?$select=quotedetailid&$expand=dsy_cpqfields($select=dsy_globalid)'
        )) as { quotedetailid: string; dsy_cpqfields: { dsy_globalid: string } }
        const newGlobalId = newQuoteDetail.dsy_cpqfields.dsy_globalid

        // 3. Re-point every existing child accessory's parentglobalid — and, when the
        // replaced row is a package header, every member's packageglobalid — to the new
        // row BEFORE deleting the old one. The cascade-delete plugin queries both keys by
        // old globalid at pre-op; with them re-pointed, the cascade no-ops and the
        // accessories/members survive on the new row.
        const children = childrenByOldGlobalId.get(oldGlobalId) ?? []
        const members = membersByOldGlobalId.get(oldGlobalId) ?? []
        await Promise.all([
          ...children.map((child) =>
            updateRecord<UpsertCPQFieldsBody>('dsy_lineitemcpqfieldses', child.dsy_lineitemcpqfieldsid, {
              dsy_parentglobalid: newGlobalId,
            })
          ),
          ...members.map((member) =>
            updateRecord<UpsertCPQFieldsBody>('dsy_lineitemcpqfieldses', member.dsy_lineitemcpqfieldsid, {
              dsy_packageglobalid: newGlobalId,
            })
          ),
        ])

        // 4. Delete the old parent. Plugin cascades nothing (re-pointed) and post-op
        // cleans up the old cpqfields row.
        await deleteRecord('quotedetails', quoteDetail.quotedetailid)

        return newQuoteDetailId
      })()
    )
  }

  return await Promise.all(promises)
}

export type OutdatedProduct = {
  productId: string
  productName: string
  currentPrice: number | null
  priceListPrice: number | null
  quoteDetailIds: string[]
  uniqueKey: string // Combination of productId and currentPrice for unique identification
  isDiscontinued?: boolean
}

export async function checkOutdatedProducts() {
  const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote())
  const quoteDetails = queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails())

  if (!quoteDetails || !quote || !quote._pricelevelid_value) {
    throw new Error('Quote details, quote, or price list not found')
  }

  // Filter out quote details that are not real products (exclude scope of work and summary details)
  const productQuoteDetails = quoteDetails.filter((quoteDetail) => isProduct(quoteDetail.productid))

  // Get unique product ids
  const uniqueProductIdsSet = new Set<string>()

  // Group quote details by product ID + price combination
  const uniqueProductPriceGroups = new Map<
    string,
    {
      productId: string
      productName: string
      currentPrice: number | null
      quoteDetailIds: string[]
      isDiscontinued: boolean
    }
  >()

  for (const quoteDetail of productQuoteDetails) {
    const productId = quoteDetail.productid.productid
    uniqueProductIdsSet.add(productId)
    const currentPrice = quoteDetail.priceperunit ?? 0
    const isDiscontinued = quoteDetail.productid.statuscode === ProductStatusReason.Discontinued
    // Create a unique key combining product ID and current price
    const priceKey = new Decimal(currentPrice).toDecimalPlaces(2).toString()
    const uniqueKey = `${productId}-${priceKey}`

    if (!uniqueProductPriceGroups.has(uniqueKey)) {
      uniqueProductPriceGroups.set(uniqueKey, {
        productId,
        productName: quoteDetail.productid.name,
        currentPrice,
        quoteDetailIds: [],
        isDiscontinued,
      })
    }
    uniqueProductPriceGroups.get(uniqueKey)!.quoteDetailIds.push(quoteDetail.quotedetailid)
  }

  const outdatedProducts: OutdatedProduct[] = []

  // Convert map to array for batching
  const productGroupsArray = Array.from(uniqueProductPriceGroups.entries())

  // Convert set to array for batching
  const uniqueProductIdsArray = Array.from(uniqueProductIdsSet)

  // Create a map to store price list items for all products
  const productToPriceListItemMap = new Map<string, PriceListItemData>()

  // Process products in batches of 10
  const batchSize = 10
  for (let i = 0; i < uniqueProductIdsArray.length; i += batchSize) {
    const batch = uniqueProductIdsArray.slice(i, i + batchSize)
    try {
      // Create filter for batch: (_dsy_product_value eq 'id1' or _dsy_product_value eq 'id2' ...)
      const productFilters = batch.map((productId) => `_dsy_product_value eq '${productId}'`).join(' or ')
      const filter = `(${productFilters}) and _dsy_pricelist_value eq '${quote._pricelevelid_value}'`

      // Query price list items for this batch
      const priceListItems = await getMultipleRecords<PriceListItemData>(
        'dsy_pricelistitems',
        `?$select=_dsy_product_value,dsy_unitprice,dsy_description&$filter=${filter}`
      )

      // Store items in the map
      for (const priceListItem of priceListItems) {
        const productId = priceListItem._dsy_product_value
        if (!productToPriceListItemMap.has(productId)) {
          productToPriceListItemMap.set(productId, priceListItem)
        }
      }
    } catch (error) {
      trackException(error, { context: 'quote-details.checkPricing', detail: `batch index ${i}` })
      // Continue with other batches even if one fails
    }
  }

  // Check each product group using the collected price data
  for (const [uniqueKey, productInfo] of productGroupsArray) {
    const priceListItem = productToPriceListItemMap.get(productInfo.productId)

    // Skip Allowance products. Raw read, so description can be null (not an allowance).
    if (priceListItem?.dsy_description?.startsWith('Allowance')) {
      continue
    }

    // Always flag discontinued products, regardless of if found in price list
    if (productInfo.isDiscontinued) {
      outdatedProducts.push({
        productId: productInfo.productId,
        productName: productInfo.productName,
        currentPrice: productInfo.currentPrice,
        priceListPrice: priceListItem?.dsy_unitprice ?? null,
        quoteDetailIds: productInfo.quoteDetailIds,
        uniqueKey,
        isDiscontinued: true,
      })
      continue
    }

    // Skip if price list item not found or price is $0
    if (!priceListItem?.dsy_unitprice) {
      continue
    }

    // Check if price is outdated (different from price list)
    const currentPriceDecimal = new Decimal(productInfo.currentPrice ?? 0)
    const priceListPriceDecimal = new Decimal(priceListItem.dsy_unitprice ?? 0)

    // Consider prices different if they differ by more than 0.01 (to account for floating point precision)
    if (!currentPriceDecimal.equals(priceListPriceDecimal)) {
      outdatedProducts.push({
        productId: productInfo.productId,
        productName: productInfo.productName,
        currentPrice: productInfo.currentPrice,
        priceListPrice: priceListItem.dsy_unitprice,
        quoteDetailIds: productInfo.quoteDetailIds,
        uniqueKey,
      })
    }
  }

  return outdatedProducts.sort((a, b) => a.productName.localeCompare(b.productName))
}

export function useOutdatedProductsQuery() {
  return useQuery({
    queryKey: QUERY_KEYS.outdatedProducts,
    queryFn: checkOutdatedProducts,
  })
}

export type UpdatePricingBody = {
  quoteDetails: QuoteDetailData[]
  outdatedProducts: OutdatedProduct[]
}

async function updatePricing({ quoteDetails, outdatedProducts }: UpdatePricingBody) {
  const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote())

  if (!quote || !quote._pricelevelid_value) {
    throw new Error('Quote or price list not found')
  }

  // Create a map of product ID to price list price for quick lookup
  const outdatedProductsNewPriceMap = new Map<string, number | null>()
  for (const product of outdatedProducts) {
    outdatedProductsNewPriceMap.set(product.productId, product.priceListPrice)
  }

  const promises = []

  // Update each quote detail that has an outdated product
  const productQuoteDetails = quoteDetails.filter((quoteDetail) => isProduct(quoteDetail.productid))
  for (const quoteDetail of productQuoteDetails) {
    const productId = quoteDetail.productid.productid
    const newPrice = outdatedProductsNewPriceMap.get(productId)

    if (typeof newPrice !== 'number') {
      continue
    }

    promises.push(
      updateQuoteDetail({
        quoteDetail,
        quoteDetailBody: { priceperunit: newPrice },
        cpqFieldsBody: {},
      })
    )
  }

  return await Promise.all(promises)
}

export function useUpdatePricing(options?: UseMutationOptions<unknown, Error, UpdatePricingBody>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['update', 'pricing'],
    mutationFn: updatePricing,
    onError: (error) => {
      toast({
        title: 'Error updating pricing',
        error: error,
        variant: 'destructive',
      })
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Pricing updated successfully',
      })
    },
    onSettled: async (_, __, { quoteDetails }) => {
      const uniqueRooms = new Set<string>()
      const uniqueSystems = new Set<number>()

      quoteDetails.forEach((quoteDetail) => {
        if (quoteDetail.dsy_cpqfields._dsy_room_value) {
          uniqueRooms.add(quoteDetail.dsy_cpqfields._dsy_room_value)
        }
        if (quoteDetail.dsy_cpqfields.dsy_system) {
          uniqueSystems.add(quoteDetail.dsy_cpqfields.dsy_system)
        }
      })

      const queryKeys = []
      const awaitQueryKeys = [QUERY_KEYS.quoteDetails()]

      if (quoteDetails.some((quoteDetail) => isSummaryDetail(quoteDetail.productid.productid))) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' }))
      }

      for (const roomId of uniqueRooms) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ roomId }))

        for (const systemId of uniqueSystems) {
          awaitQueryKeys.push(QUERY_KEYS.quoteDetails({ roomId, systemId }))
        }
      }

      for (const systemId of uniqueSystems) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ systemId }))
      }

      const uniqueQueryKeys = getUniqueQueryKeys(queryKeys)
      Promise.all(uniqueQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      const uniqueAwaitQueryKeys = getUniqueQueryKeys(awaitQueryKeys)
      await Promise.all(uniqueAwaitQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      return await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.outdatedProducts })
    },
    ...options,
  })
}

export type EditQuoteDetailsBody = {
  quoteDetails: QuoteDetailData[]
  quoteDetailBody: UpsertQuoteDetailBody
  cpqFieldsBody: UpsertCPQFieldsBody
}

/**
 * A row with an inherited bucket (package member or accessory) takes room,
 * system and scope from its parent, never from a bulk edit — writing them here
 * would strand it outside its group the way a direct member move used to.
 * Every other field in the shared body still applies.
 */
export function stripInheritedBucketFields(
  quoteDetail: Parameters<typeof hasInheritedBucket>[0],
  cpqFieldsBody: UpsertCPQFieldsBody
): UpsertCPQFieldsBody {
  if (!hasInheritedBucket(quoteDetail)) return cpqFieldsBody
  const rest = { ...cpqFieldsBody }
  delete rest['dsy_room@odata.bind']
  delete rest.dsy_system
  delete rest.dsy_scopetag
  return rest
}

async function editQuoteDetails({ quoteDetails, quoteDetailBody, cpqFieldsBody }: EditQuoteDetailsBody) {
  return await Promise.all(
    quoteDetails.map((quoteDetail) =>
      updateQuoteDetail({ quoteDetail, quoteDetailBody, cpqFieldsBody: stripInheritedBucketFields(quoteDetail, cpqFieldsBody) })
    )
  )
}

export function useEditQuoteDetails(options?: UseMutationOptions<unknown, Error, EditQuoteDetailsBody>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['edit', 'quotedetails'],
    mutationFn: editQuoteDetails,
    onError: (error) => {
      toast({
        title: 'Error editing items',
        error: error,
        variant: 'destructive',
      })
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Items edited successfully',
      })
    },
    onSettled: async (_, __, { quoteDetails }) => {
      const uniqueRooms = new Set<string>()
      const uniqueSystems = new Set<number>()

      quoteDetails.forEach((quoteDetail) => {
        if (quoteDetail.dsy_cpqfields._dsy_room_value) {
          uniqueRooms.add(quoteDetail.dsy_cpqfields._dsy_room_value)
        }
        if (quoteDetail.dsy_cpqfields.dsy_system) {
          uniqueSystems.add(quoteDetail.dsy_cpqfields.dsy_system)
        }
      })

      const queryKeys = [QUERY_KEYS.quoteDetails()]
      const awaitQueryKeys = []

      if (quoteDetails.some((quoteDetail) => isSummaryDetail(quoteDetail.productid.productid))) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' }))
      }

      for (const roomId of uniqueRooms) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ roomId }))

        for (const systemId of uniqueSystems) {
          awaitQueryKeys.push(QUERY_KEYS.quoteDetails({ roomId, systemId }))
        }
      }

      for (const systemId of uniqueSystems) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ systemId }))
      }

      const uniqueQueryKeys = getUniqueQueryKeys(queryKeys)
      Promise.all(uniqueQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      const uniqueAwaitQueryKeys = getUniqueQueryKeys(awaitQueryKeys)
      return await Promise.all(uniqueAwaitQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
    },
    ...options,
  })
}

export function useReplaceQuoteDetails(options?: UseMutationOptions<unknown, Error, ReplaceQuoteDetailsBody>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['replace', 'quotedetails', 'all'],
    mutationFn: replaceQuoteDetails,
    onError: (error) => {
      toast({
        title: 'Error replacing products',
        error: error,
        variant: 'destructive',
      })
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Products replaced successfully',
      })
    },
    onSettled: async (_, __, { quoteDetails }) => {
      const uniqueRooms = new Set<string>()
      const uniqueSystems = new Set<number>()

      quoteDetails.forEach((quoteDetail) => {
        if (quoteDetail.dsy_cpqfields._dsy_room_value) {
          uniqueRooms.add(quoteDetail.dsy_cpqfields._dsy_room_value)
        }
        if (quoteDetail.dsy_cpqfields.dsy_system) {
          uniqueSystems.add(quoteDetail.dsy_cpqfields.dsy_system)
        }
      })

      const queryKeys = [QUERY_KEYS.quoteDetails()]
      const awaitQueryKeys = []

      if (quoteDetails.some((quoteDetail) => isSummaryDetail(quoteDetail.productid.productid))) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' }))
      }

      for (const roomId of uniqueRooms) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ roomId }))

        for (const systemId of uniqueSystems) {
          awaitQueryKeys.push(QUERY_KEYS.quoteDetails({ roomId, systemId }))
        }
      }

      for (const systemId of uniqueSystems) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ systemId }))
      }

      const uniqueQueryKeys = getUniqueQueryKeys(queryKeys)
      Promise.all(uniqueQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))

      const uniqueAwaitQueryKeys = getUniqueQueryKeys(awaitQueryKeys)
      return await Promise.all(uniqueAwaitQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
    },
    ...options,
  })
}

export type QuoteDetailsSummary = {
  product: {
    quantity: number
    cost: number
    price: number
    priceWithDiscounts: number
    margin: number
    subtotalPercentage: number
    tax: number
  }
  labor: {
    hours: number
    price: number
    priceWithDiscounts: number
    cost: number
    margin: number
    subtotalPercentage: number
    tax: number
  }
  supplies: {
    price: number
    cost: number
    margin: number
    priceRate: number
    costRate: number
    subtotalPercentage: number
    tax: number
  }
  tax: {
    price: number
    rate: number
  }
  discounts: {
    products: number
    productsTax: number
    productsTaxable: number
    productsNonTax: number
    labor: number
    laborTax: number
    laborTaxable: number
    laborNonTax: number
    customerLoyalty: number
    tax: number
    total: number
    /** Flat-dollar portions (manual amounts), excluding percentage discounts. */
    productsFixed: number
    laborFixed: number
    totalFixed: number
  }
  subtotal: number
  subtotalWithSupplies: number
  subtotalWithSuppliesAndDiscounts: number
  storedSubtotalAdjustment: number
  total: number
  totalCost: number
  totalMargin: number
  grossProfitPerHour: number
}

const INITIAL_SUMMARY: QuoteDetailsSummary = {
  product: {
    quantity: 0,
    cost: 0,
    price: 0,
    priceWithDiscounts: 0,
    margin: 0,
    subtotalPercentage: 0,
    tax: 0,
  },
  labor: {
    hours: 0,
    price: 0,
    priceWithDiscounts: 0,
    cost: 0,
    margin: 0,
    subtotalPercentage: 0,
    tax: 0,
  },
  supplies: {
    price: 0,
    cost: 0,
    margin: 0,
    priceRate: 0,
    costRate: 0,
    subtotalPercentage: 0,
    tax: 0,
  },
  tax: {
    price: 0,
    rate: 0,
  },
  discounts: {
    products: 0,
    productsTax: 0,
    productsTaxable: 0,
    productsNonTax: 0,
    labor: 0,
    laborTax: 0,
    laborTaxable: 0,
    laborNonTax: 0,
    customerLoyalty: 0,
    tax: 0,
    total: 0,
    productsFixed: 0,
    laborFixed: 0,
    totalFixed: 0,
  },
  subtotal: 0,
  subtotalWithSupplies: 0,
  subtotalWithSuppliesAndDiscounts: 0,
  storedSubtotalAdjustment: 0,
  total: 0,
  totalCost: 0,
  totalMargin: 0,
  grossProfitPerHour: 0,
}

const hasActiveSummaryFilter = (filters?: QuoteDetailFilters | null) =>
  !!filters?.previousQuote ||
  !!filters?.quoteDetail ||
  !!filters?.onlySummaryDetails ||
  !!filters?.roomId ||
  !!filters?.systemId ||
  !!filters?.unassignedSystem ||
  !!filters?.laborKey ||
  !!filters?.search

const applyLockedQuoteStoredTotals = ({
  filters,
  quote,
  summary,
}: {
  filters: QuoteDetailFilters | undefined
  quote: QuoteData
  summary: QuoteDetailsSummary
}): QuoteDetailsSummary => {
  // CO headers freeze an incremental amount, while both diff operands stay cumulative.
  if (quote.dsy_type === QuoteType.ChangeOrder || !shouldUseStoredTotals(quote) || hasActiveSummaryFilter(filters)) return summary
  return applyStoredTotals({ quote, summary })

}

export function calculateSummaryV1({
      filters,
      quote,
      quoteDetails,
      summaryDetails,
    }: {
      filters: QuoteDetailFilters | undefined
      quote: QuoteData
      quoteDetails: QuoteDetailData[]
      summaryDetails: QuoteDetailData[]
}) {
      // Pre-process summary details for faster lookup during calculations
      const laborSummaryMap = new Map<
        string,
        {
          detail: QuoteDetailData
          field: keyof CPQFieldsData
        }
      >()

      for (const [key, field] of Object.entries(LABOR_SUMMARY_NAME_TO_KEY_MAP)) {
        const detail = summaryDetails.find(
          (d) =>
            d.productid.productid === LABOR_SUMMARY_PRODUCT_ID_MAP[key as keyof typeof LABOR_SUMMARY_NAME_TO_KEY_MAP]
        )
        if (detail) {
          laborSummaryMap.set(key, { detail, field })
        }
      }

      // Calculate tax rate once
      const taxRate = new Decimal(quote.dsy_taxcode?.msdyn_taxrate ?? 0).div(100)

      // Initialize accumulators with Decimal
      const totals = {
        product: {
          quantity: new Decimal(0),
          cost: new Decimal(0),
          price: new Decimal(0),
          taxableAmount: new Decimal(0),
          tax: new Decimal(0),
        },
        labor: {
          hours: new Decimal(0),
          price: new Decimal(0),
          cost: new Decimal(0),
          taxableAmount: new Decimal(0),
          tax: new Decimal(0),
        },
        tax: {
          price: new Decimal(0),
        },
      }

      // Filter products only once before processing
      const productDetails = quoteDetails.filter((detail) => isProduct(detail.productid))

      // Get specific labor key for filtering if present
      const filteredLaborData = filters?.laborKey ? laborSummaryMap.get(filters.laborKey) : null

      // Process all product details
      for (const detail of productDetails) {
        // Calculate product totals
        const productQuantity = new Decimal(detail.quantity)
        const productUnitPrice = new Decimal(detail.priceperunit ?? 0).toDecimalPlaces(2)
        const productTotalCost = getExtendedCostDecimal(detail, productQuantity)
        const productTotalPrice = productUnitPrice.mul(productQuantity).toDecimalPlaces(2)

        // Add to product totals
        totals.product.quantity = totals.product.quantity.plus(productQuantity)
        totals.product.cost = totals.product.cost.plus(productTotalCost)
        totals.product.price = totals.product.price.plus(productTotalPrice)

        // Add taxable amount and tax, since NetSuite calculates tax per line item
        if (detail.msdyn_taxable) {
          totals.product.taxableAmount = totals.product.taxableAmount.plus(productTotalPrice)
          totals.product.tax = totals.product.tax.plus(getTaxDecimal(productTotalPrice, 1, filters?.previousQuote))
        }

        // Calculate labor totals
        if (filteredLaborData) {
          // Process only the specified labor type
          const { detail: laborSummaryDetail, field: laborFieldKey } = filteredLaborData
          const laborHours = detail.dsy_cpqfields[laborFieldKey] as number | null

          if (laborHours) {
            // Calculate labor totals
            const laborFactor = new Decimal(laborSummaryDetail.dsy_cpqfields.dsy_laborfactorpercent ?? 100).div(100)
            const totalFactoredLaborHours = new Decimal(laborHours).mul(productQuantity).mul(laborFactor)

            const laborUnitPrice = new Decimal(laborSummaryDetail.priceperunit ?? 0)
            const laborUnitCost = new Decimal(laborSummaryDetail.msdyn_costpriceperunit ?? 0)

            const laborTotalPrice = laborUnitPrice.mul(totalFactoredLaborHours)
            const laborTotalCost = laborUnitCost.mul(totalFactoredLaborHours)

            // Add to labor totals
            totals.labor.hours = totals.labor.hours.plus(totalFactoredLaborHours)
            totals.labor.price = totals.labor.price.plus(laborTotalPrice)
            totals.labor.cost = totals.labor.cost.plus(laborTotalCost)

            // Add taxable amount ONLY, since NetSuite calculates tax per SUMMARY line item - calculate tax on total labor price
            if (laborSummaryDetail.msdyn_taxable) {
              totals.labor.taxableAmount = totals.labor.taxableAmount.plus(laborTotalPrice)
            }
          }
        } else {
          // Process all labor types
          for (const [, { detail: laborSummaryDetail, field: laborFieldKey }] of laborSummaryMap.entries()) {
            const laborHours = detail.dsy_cpqfields[laborFieldKey] as number | null

            if (laborHours) {
              // Calculate labor totals
              const laborFactor = new Decimal(laborSummaryDetail.dsy_cpqfields.dsy_laborfactorpercent ?? 100).div(100)
              const totalFactoredLaborHours = new Decimal(laborHours).mul(productQuantity).mul(laborFactor)

              const laborUnitPrice = new Decimal(laborSummaryDetail.priceperunit ?? 0)
              const laborUnitCost = new Decimal(laborSummaryDetail.msdyn_costpriceperunit ?? 0)

              const laborTotalPrice = laborUnitPrice.mul(totalFactoredLaborHours)
              const laborTotalCost = laborUnitCost.mul(totalFactoredLaborHours)

              // Add to labor totals
              totals.labor.hours = totals.labor.hours.plus(totalFactoredLaborHours)
              totals.labor.price = totals.labor.price.plus(laborTotalPrice)
              totals.labor.cost = totals.labor.cost.plus(laborTotalCost)

              // Add taxable amount ONLY, since NetSuite calculates tax per SUMMARY line item - calculate tax on total taxable amount
              if (laborSummaryDetail.msdyn_taxable) {
                totals.labor.taxableAmount = totals.labor.taxableAmount.plus(laborTotalPrice)
              }
            }
          }
        }
      }

      // Handle rounding and tax calculations
      totals.product.price = totals.product.price.toDecimalPlaces(2)
      totals.product.cost = totals.product.cost.toDecimalPlaces(2)
      totals.product.taxableAmount = totals.product.taxableAmount.toDecimalPlaces(2)

      totals.labor.hours = totals.labor.hours.toDecimalPlaces(5)
      totals.labor.price = totals.labor.price.toDecimalPlaces(2)
      totals.labor.cost = totals.labor.cost.toDecimalPlaces(2)
      totals.labor.taxableAmount = totals.labor.taxableAmount.toDecimalPlaces(2)

      // Calculate labor tax and total tax now
      totals.labor.tax = getTaxDecimal(totals.labor.taxableAmount, 1, filters?.previousQuote)
      totals.tax.price = totals.product.tax.plus(totals.labor.tax)

      // Handle quantity = 1 filter
      if (filters?.quoteDetail?.unitOnly) {
        const parentQuoteDetail = quoteDetails.find(
          (d) => d.dsy_cpqfields.dsy_globalid === filters.quoteDetail!.quoteDetail.dsy_cpqfields.dsy_globalid
        )!

        totals.product.price = totals.product.price.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.product.cost = totals.product.cost.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.product.quantity = totals.product.quantity.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.product.taxableAmount = totals.product.taxableAmount.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.product.tax = totals.product.tax.div(parentQuoteDetail.quantity).toDecimalPlaces(2)

        totals.labor.price = totals.labor.price.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.labor.cost = totals.labor.cost.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.labor.hours = totals.labor.hours.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.labor.taxableAmount = totals.labor.taxableAmount.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
        totals.labor.tax = totals.labor.tax.div(parentQuoteDetail.quantity).toDecimalPlaces(2)

        totals.tax.price = totals.tax.price.div(parentQuoteDetail.quantity).toDecimalPlaces(2)
      }

      // Calculate final totals efficiently
      const subtotal = totals.product.price.plus(totals.labor.price)

      // Disable discounts when filters are applied
      const shouldApplyDiscounts =
        !filters?.laborKey &&
        !filters?.onlySummaryDetails &&
        !filters?.quoteDetail &&
        !filters?.roomId &&
        !filters?.systemId &&
        !filters?.unassignedSystem &&
        !filters?.search

      // Calculate product discounts
      const {
        amount: productsDiscountAmount,
        tax: productsDiscountTax,
        fixed: productsDiscountFixedAmount,
      } = summaryDetails
        .filter(
          (d) =>
            shouldApplyDiscounts &&
            (d.productid.productid === SUMMARY_PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT'] ||
              d.productid.productid === SUMMARY_PRODUCT_ID_MAP['DISCOUNT:PRODUCTS'])
        )
        .reduce(
          (acc, curr) => {
            const equipmentManualDiscountAmount = new Decimal(curr.manualdiscountamount ?? curr.priceperunit ?? 0)
              .mul(curr.quantity)
              .toDecimalPlaces(2)
              .abs()

            const equipmentPercentageDiscountAmount = totals.product.price
              .mul(new Decimal(curr.dsy_cpqfields.dsy_laborfactorpercent ?? 0).div(100))
              .toDecimalPlaces(2)
              .abs()

            const equipmentDiscountAmount = equipmentManualDiscountAmount.plus(equipmentPercentageDiscountAmount)

            const taxableRatio = !totals.product.price.isZero()
              ? totals.product.taxableAmount.div(totals.product.price)
              : new Decimal(0)
            const taxableDiscountAmount = equipmentDiscountAmount.mul(taxableRatio)

            const equipmentDiscountTax = getTaxDecimal(
              taxableDiscountAmount,
              curr.quantity,
              filters?.previousQuote
            ).abs()

            return {
              amount: acc.amount.plus(equipmentDiscountAmount),
              tax: acc.tax.plus(equipmentDiscountTax),
              fixed: acc.fixed.plus(equipmentManualDiscountAmount),
            }
          },
          { amount: new Decimal(0), tax: new Decimal(0), fixed: new Decimal(0) }
        )

      // Calculate labor discounts
      const {
        amount: laborDiscountAmount,
        tax: laborDiscountTax,
        fixed: laborDiscountFixedAmount,
      } = summaryDetails
        .filter((d) => shouldApplyDiscounts && d.productid.productid === SUMMARY_PRODUCT_ID_MAP['DISCOUNT:LABOR'])
        .reduce(
          (acc, curr) => {
            const laborManualDiscountAmount = new Decimal(curr.manualdiscountamount ?? curr.priceperunit ?? 0)
              .mul(curr.quantity)
              .toDecimalPlaces(2)
              .abs()

            const laborPercentageDiscountAmount = totals.labor.price
              .mul(new Decimal(curr.dsy_cpqfields.dsy_laborfactorpercent ?? 0).div(100))
              .toDecimalPlaces(2)
              .abs()

            const laborDiscountAmount = laborManualDiscountAmount.plus(laborPercentageDiscountAmount)

            const taxableRatio = !totals.labor.price.isZero()
              ? totals.labor.taxableAmount.div(totals.labor.price)
              : new Decimal(0)
            const taxableDiscountAmount = laborDiscountAmount.mul(taxableRatio)

            const laborDiscountTax = getTaxDecimal(taxableDiscountAmount, curr.quantity, filters?.previousQuote).abs()

            return {
              amount: acc.amount.plus(laborDiscountAmount),
              tax: acc.tax.plus(laborDiscountTax),
              fixed: acc.fixed.plus(laborManualDiscountAmount),
            }
          },
          { amount: new Decimal(0), tax: new Decimal(0), fixed: new Decimal(0) }
        )

      // Calculate supplies data
      const subtotalWithProductAndLaborDiscounts = subtotal.sub(productsDiscountAmount).sub(laborDiscountAmount)

      const suppliesPriceRate = new Decimal(quote.dsy_suppliespricerate ?? 0).div(100)
      const suppliesPrice = suppliesPriceRate
        .mul(subtotalWithProductAndLaborDiscounts)
        .div(new Decimal(1).sub(suppliesPriceRate))

      const suppliesCostRate = new Decimal(quote.dsy_suppliescostrate ?? 0).div(100)
      const suppliesCost = suppliesCostRate
        .mul(subtotalWithProductAndLaborDiscounts)
        .div(new Decimal(1).sub(suppliesCostRate))

      const suppliesMargin = getMarginPercentage(suppliesPrice, suppliesCost)

      const suppliesTax = getTaxDecimal(suppliesPrice, 1, filters?.previousQuote)

      const subtotalWithSupplies = subtotal.plus(suppliesPrice)

      // Calculate client loyalty discounts
      const clientLoyaltyDiscountPercentage = new Decimal(quote.discountpercentage ?? 0)
      const clientLoyaltyPercentageDiscountAmount = subtotalWithSupplies
        .mul(clientLoyaltyDiscountPercentage)
        .div(100)
        .abs()
      const clientLoyaltyDiscountAmount = new Decimal(quote.discountamount ?? 0).abs()
      const clientLoyaltyTotalDiscountAmount = shouldApplyDiscounts
        ? clientLoyaltyPercentageDiscountAmount.plus(clientLoyaltyDiscountAmount)
        : new Decimal(0)

      // Total discount is the sum of all discount amounts (all negative)
      const totalDiscountAmount = clientLoyaltyTotalDiscountAmount
        .plus(productsDiscountAmount)
        .plus(laborDiscountAmount)
      const subtotalWithSuppliesAndDiscounts = subtotalWithSupplies.sub(totalDiscountAmount)
      const totalDiscountTax = productsDiscountTax.plus(laborDiscountTax)

      // Final calculations - subtracting negative values means adding their absolute values
      totals.product.tax = totals.product.tax.sub(productsDiscountTax)
      totals.labor.tax = totals.labor.tax.sub(laborDiscountTax)
      const tax = totals.tax.price.plus(suppliesTax).sub(totalDiscountTax)
      const total = subtotalWithSuppliesAndDiscounts.plus(tax)
      const totalCost = totals.product.cost.plus(totals.labor.cost).plus(suppliesCost)
      const grossProfitPerHour = !totals.labor.hours.isZero()
        ? subtotalWithSuppliesAndDiscounts.sub(totalCost).div(totals.labor.hours)
        : new Decimal(0)

      // Create an object with all calculated values to avoid duplicate calculations
      const totalProductPriceWithDiscounts = totals.product.price.sub(productsDiscountAmount)
      const totalLaborPriceWithDiscounts = totals.labor.price.sub(laborDiscountAmount)

      // Convert Decimal objects to numbers with appropriate rounding in one pass
      return {
        product: {
          quantity: totals.product.quantity.toDecimalPlaces(0).toNumber(),
          cost: totals.product.cost.toDecimalPlaces(2).toNumber(),
          price: totals.product.price.toDecimalPlaces(2).toNumber(),
          priceWithDiscounts: totalProductPriceWithDiscounts.toDecimalPlaces(2).toNumber(),
          margin: getMarginPercentage(totalProductPriceWithDiscounts, totals.product.cost)
            .toDecimalPlaces(2)
            .toNumber(),
          subtotalPercentage: !subtotalWithSuppliesAndDiscounts.isZero()
            ? totalProductPriceWithDiscounts
                .div(subtotalWithSuppliesAndDiscounts)
                .mul(100)
                .toDecimalPlaces(2)
                .toNumber()
            : 0,
          tax: totals.product.tax.toDecimalPlaces(2).toNumber(),
        },
        labor: {
          hours: totals.labor.hours.toDecimalPlaces(5).toNumber(),
          cost: totals.labor.cost.toDecimalPlaces(2).toNumber(),
          price: totals.labor.price.toDecimalPlaces(2).toNumber(),
          priceWithDiscounts: totalLaborPriceWithDiscounts.toDecimalPlaces(2).toNumber(),
          margin: getMarginPercentage(totalLaborPriceWithDiscounts, totals.labor.cost).toDecimalPlaces(2).toNumber(),
          subtotalPercentage: !subtotalWithSuppliesAndDiscounts.isZero()
            ? totalLaborPriceWithDiscounts.div(subtotalWithSuppliesAndDiscounts).mul(100).toDecimalPlaces(2).toNumber()
            : 0,
          tax: totals.labor.tax.toDecimalPlaces(2).toNumber(),
        },
        supplies: {
          price: suppliesPrice.toDecimalPlaces(2).toNumber(),
          cost: suppliesCost.toDecimalPlaces(2).toNumber(),
          margin: suppliesMargin.toDecimalPlaces(2).toNumber(),
          priceRate: suppliesPriceRate.mul(100).toDecimalPlaces(8).toNumber(),
          costRate: suppliesCostRate.mul(100).toDecimalPlaces(8).toNumber(),
          subtotalPercentage: !subtotalWithSuppliesAndDiscounts.isZero()
            ? suppliesPrice.div(subtotalWithSuppliesAndDiscounts).mul(100).toDecimalPlaces(2).toNumber()
            : 0,
          tax: suppliesTax.toDecimalPlaces(2).toNumber(),
        },
        tax: {
          price: tax.toDecimalPlaces(2).toNumber(),
          rate: taxRate.toDecimalPlaces(5).toNumber(),
        },
        discounts: {
          products: productsDiscountAmount.toDecimalPlaces(2).toNumber(),
          productsTax: productsDiscountTax.toDecimalPlaces(2).toNumber(),
          productsTaxable: productsDiscountAmount.toDecimalPlaces(2).toNumber(),
          productsNonTax: 0,
          labor: laborDiscountAmount.toDecimalPlaces(2).toNumber(),
          laborTax: laborDiscountTax.toDecimalPlaces(2).toNumber(),
          laborTaxable: laborDiscountAmount.toDecimalPlaces(2).toNumber(),
          laborNonTax: 0,
          customerLoyalty: clientLoyaltyTotalDiscountAmount.toDecimalPlaces(2).toNumber(),
          tax: totalDiscountTax.toDecimalPlaces(2).toNumber(),
          total: totalDiscountAmount.toDecimalPlaces(2).toNumber(),
          productsFixed: productsDiscountFixedAmount.toDecimalPlaces(2).toNumber(),
          laborFixed: laborDiscountFixedAmount.toDecimalPlaces(2).toNumber(),
          totalFixed: productsDiscountFixedAmount
            .plus(laborDiscountFixedAmount)
            .plus(shouldApplyDiscounts ? clientLoyaltyDiscountAmount : 0)
            .toDecimalPlaces(2)
            .toNumber(),
        },
        subtotal: subtotal.toDecimalPlaces(2).toNumber(),
        subtotalWithSupplies: subtotalWithSupplies.toDecimalPlaces(2).toNumber(),
        subtotalWithSuppliesAndDiscounts: subtotalWithSuppliesAndDiscounts.toDecimalPlaces(2).toNumber(),
        storedSubtotalAdjustment: 0,
        totalMargin: getMarginPercentage(subtotalWithSuppliesAndDiscounts, totalCost).toDecimalPlaces(2).toNumber(),
        total: total.toDecimalPlaces(2).toNumber(),
        totalCost: totalCost.toDecimalPlaces(2).toNumber(),
        grossProfitPerHour: grossProfitPerHour.toDecimalPlaces(2).toNumber(),
      }
}

export function calculateSummaryV2({
      filters,
      quote,
      quoteDetails,
      summaryDetails,
      isLegacyChain,
    }: {
      filters: QuoteDetailFilters | undefined
      quote: QuoteData
      quoteDetails: QuoteDetailData[]
      summaryDetails: QuoteDetailData[]
      isLegacyChain: boolean
}) {
      // Pre-process summary details for faster lookup during calculations
      const laborSummaryData = []

      for (const [key, field] of Object.entries(LABOR_SUMMARY_NAME_TO_KEY_MAP)) {
        const detail = summaryDetails.find(
          (d) =>
            d.productid.productid === LABOR_SUMMARY_PRODUCT_ID_MAP[key as keyof typeof LABOR_SUMMARY_NAME_TO_KEY_MAP]
        )
        if (!detail) continue
        if (filters?.laborKey && key !== filters.laborKey) continue
        laborSummaryData.push({ detail, field })
      }

      // Calculate tax rate once
      const taxRate = new Decimal(quote.dsy_taxcode?.msdyn_taxrate ?? 0).div(100)

      // Initialize accumulators with Decimal
      const totals = {
        product: {
          quantity: new Decimal(0),
          cost: new Decimal(0),
          price: new Decimal(0),
          taxableAmount: new Decimal(0),
          tax: new Decimal(0),
        },
        labor: {
          hours: new Decimal(0),
          price: new Decimal(0),
          cost: new Decimal(0),
          taxableAmount: new Decimal(0),
          tax: new Decimal(0),
          dsy_installationlabor: new Decimal(0),
          dsy_programminglabor: new Decimal(0),
          dsy_roughinlabor: new Decimal(0),
          dsy_engineeringlabor: new Decimal(0),
          dsy_projectmanagementlabor: new Decimal(0),
          dsy_servicelabor: new Decimal(0),
        },
      }

      // Filter products only once before processing
      const productDetails = quoteDetails.filter((detail) => isProduct(detail.productid))

      // Process all product details
      for (const detail of productDetails) {
        // Calculate product totals
        const productQuantity = new Decimal(detail.quantity)
        const productUnitPrice = new Decimal(detail.priceperunit ?? 0).toDecimalPlaces(2)
        const productTotalCost = getExtendedCostDecimal(detail, productQuantity)
        const productTotalPrice = productUnitPrice.mul(productQuantity).toDecimalPlaces(2)

        // Add to product totals
        totals.product.quantity = totals.product.quantity.plus(productQuantity)
        totals.product.cost = totals.product.cost.plus(productTotalCost)
        totals.product.price = totals.product.price.plus(productTotalPrice)

        // Add taxable amount since NetSuite calculates tax per total taxable amount
        if (detail.msdyn_taxable) {
          totals.product.taxableAmount = totals.product.taxableAmount.plus(productTotalPrice)
        }

        // Calculate labor hours totals
        // Do not calculate $$ values here since NetSuite calculates on each labor type summary line item
        for (const { detail: laborSummaryDetail, field: laborFieldKey } of laborSummaryData) {
          const laborHours = detail.dsy_cpqfields[laborFieldKey] ?? 0
          if (!laborHours) continue
          const laborFactor = new Decimal(laborSummaryDetail.dsy_cpqfields.dsy_laborfactorpercent ?? 100).div(100)
          const totalFactoredLaborHours = new Decimal(laborHours).mul(productQuantity).mul(laborFactor)
          totals.labor[laborFieldKey] = totals.labor[laborFieldKey].plus(totalFactoredLaborHours)
        }
      }

      // Handle quantity = 1 filter
      if (filters?.quoteDetail?.unitOnly) {
        const parentQuoteDetail = quoteDetails.find(
          (d) => d.dsy_cpqfields.dsy_globalid === filters.quoteDetail!.quoteDetail.dsy_cpqfields.dsy_globalid
        )!

        totals.product.price = totals.product.price.div(parentQuoteDetail.quantity)
        totals.product.cost = totals.product.cost.div(parentQuoteDetail.quantity)
        totals.product.quantity = totals.product.quantity.div(parentQuoteDetail.quantity)
        totals.product.taxableAmount = totals.product.taxableAmount.div(parentQuoteDetail.quantity)

        for (const { field: laborFieldKey } of laborSummaryData) {
          const unitLaborHours = totals.labor[laborFieldKey].div(parentQuoteDetail.quantity)
          totals.labor[laborFieldKey] = unitLaborHours
        }
      }

      // Handle rounding
      totals.product.price = totals.product.price.toDecimalPlaces(2)
      totals.product.cost = totals.product.cost.toDecimalPlaces(2)
      totals.product.taxableAmount = totals.product.taxableAmount.toDecimalPlaces(2)

      // Calculate totals based on each labor type summary line item
      for (const { field: laborFieldKey, detail: laborSummaryDetail } of laborSummaryData) {
        const factoredLaborHours = totals.labor[laborFieldKey].toDecimalPlaces(5)
        totals.labor.hours = totals.labor.hours.plus(factoredLaborHours).toDecimalPlaces(5)

        const laborPriceRate = new Decimal(laborSummaryDetail.priceperunit ?? 0)
        const totalLaborPrice = laborPriceRate.mul(factoredLaborHours).toDecimalPlaces(2)
        totals.labor.price = totals.labor.price.plus(totalLaborPrice)

        const laborCostRate = new Decimal(laborSummaryDetail.msdyn_costpriceperunit ?? 0)
        // Extended cost is derived by LaborPlugin from authored rate × computed hours.
        const totalLaborCost = laborCostRate.mul(factoredLaborHours).toDecimalPlaces(2)
        totals.labor.cost = totals.labor.cost.plus(totalLaborCost)

        if (laborSummaryDetail.msdyn_taxable) {
          totals.labor.taxableAmount = totals.labor.taxableAmount.plus(totalLaborPrice)
        }
      }

      const subtotal = totals.product.price.plus(totals.labor.price)

      // Disable discounts when filters are applied
      const shouldApplyDiscounts =
        !filters?.laborKey &&
        !filters?.onlySummaryDetails &&
        !filters?.quoteDetail &&
        !filters?.roomId &&
        !filters?.systemId &&
        !filters?.unassignedSystem &&
        !filters?.search

      // Calculate product discounts. When a NONTAX line exists for the category, route through the
      // ratio-split path so that NetSuite invoicing — which is line-aware — receives a correct split.
      // When only the legacy single TAXABLE line exists, preserve current behaviour for active/won quotes.
      const equipmentDiscountDetails = summaryDetails.filter(
        (d) =>
          shouldApplyDiscounts &&
          (d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT'] ||
            d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:PRODUCTS'] ||
            d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT NOT TAXABLE'])
      )
      // Legacy chains force ratio=1 path to match parent SO's pre-fix V2 invoiced math.
      const hasEquipmentNonTaxableLine =
        !isLegacyChain &&
        equipmentDiscountDetails.some((d) => d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT NOT TAXABLE'])

      const productsDiscountAmount = equipmentDiscountDetails.reduce((acc, curr) => {
        const manual = new Decimal(curr.manualdiscountamount ?? curr.priceperunit ?? 0).mul(curr.quantity).abs().toDecimalPlaces(2)
        const percentage = totals.product.price
          .mul(new Decimal(curr.dsy_cpqfields.dsy_laborfactorpercent ?? 0).div(100))
          .abs()
        return acc.plus(manual.plus(percentage).toDecimalPlaces(2))
      }, new Decimal(0))
      const productsDiscountFixedAmount = equipmentDiscountDetails.reduce(
        (acc, curr) =>
          acc.plus(new Decimal(curr.manualdiscountamount ?? curr.priceperunit ?? 0).mul(curr.quantity).abs().toDecimalPlaces(2)),
        new Decimal(0)
      )

      const productTaxableRatio =
        hasEquipmentNonTaxableLine && !totals.product.price.isZero()
          ? totals.product.taxableAmount.div(totals.product.price)
          : new Decimal(1)
      const productsDiscountTaxableAmount = hasEquipmentNonTaxableLine
        ? productsDiscountAmount.mul(productTaxableRatio).toDecimalPlaces(2)
        : productsDiscountAmount
      const productsDiscountNonTaxAmount = hasEquipmentNonTaxableLine
        ? productsDiscountAmount.sub(productsDiscountTaxableAmount).toDecimalPlaces(2)
        : new Decimal(0)

      // Calculate labor discounts (same TAXABLE/NONTAX split logic as products).
      const isLaborTaxable = laborSummaryData.every((d) => d.detail.msdyn_taxable)
      const laborDiscountDetails = summaryDetails.filter(
        (d) =>
          shouldApplyDiscounts &&
          (d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:LABOR'] ||
            d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:LABOR NOT TAXABLE'])
      )
      const hasLaborNonTaxableLine =
        !isLegacyChain &&
        laborDiscountDetails.some((d) => d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:LABOR NOT TAXABLE'])

      const laborDiscountAmount = laborDiscountDetails.reduce((acc, curr) => {
        const manual = new Decimal(curr.manualdiscountamount ?? curr.priceperunit ?? 0).mul(curr.quantity).abs().toDecimalPlaces(2)
        const percentage = totals.labor.price
          .mul(new Decimal(curr.dsy_cpqfields.dsy_laborfactorpercent ?? 0).div(100))
          .abs()
        return acc.plus(manual.plus(percentage).toDecimalPlaces(2))
      }, new Decimal(0))
      const laborDiscountFixedAmount = laborDiscountDetails.reduce(
        (acc, curr) =>
          acc.plus(new Decimal(curr.manualdiscountamount ?? curr.priceperunit ?? 0).mul(curr.quantity).abs().toDecimalPlaces(2)),
        new Decimal(0)
      )

      const laborTaxableRatio =
        hasLaborNonTaxableLine && !totals.labor.price.isZero()
          ? totals.labor.taxableAmount.div(totals.labor.price)
          : new Decimal(1)
      const laborDiscountTaxableAmount = hasLaborNonTaxableLine
        ? laborDiscountAmount.mul(laborTaxableRatio).toDecimalPlaces(2)
        : isLaborTaxable
          ? laborDiscountAmount
          : new Decimal(0)
      const laborDiscountNonTaxAmount = hasLaborNonTaxableLine
        ? laborDiscountAmount.sub(laborDiscountTaxableAmount).toDecimalPlaces(2)
        : new Decimal(0)

      // Legacy chains intentionally retain the pre-split single-line contract.
      // Their line stores the full discount even when the taxable base receives 0.
      // Modern quotes keep the taxable amount here and use the NONTAX line for
      // the remainder.
      const laborDiscountLineAmount = getLaborDiscountLineAmount(
        laborDiscountAmount.toDecimalPlaces(2).toNumber(),
        laborDiscountTaxableAmount.toDecimalPlaces(2).toNumber(),
        isLegacyChain
      )

      // Calculate totals with discounts
      const totalProductPriceWithDiscounts = totals.product.price.sub(productsDiscountAmount)
      totals.product.taxableAmount = totals.product.taxableAmount.sub(productsDiscountTaxableAmount)
      const totalLaborPriceWithDiscounts = totals.labor.price.sub(laborDiscountAmount)
      totals.labor.taxableAmount = totals.labor.taxableAmount.sub(laborDiscountTaxableAmount)

      const subtotalWithProductAndLaborDiscounts = subtotal.sub(productsDiscountAmount).sub(laborDiscountAmount)

      // Calculate supplies
      const suppliesPriceRate = new Decimal(quote.dsy_suppliespricerate ?? 0).div(100)
      const suppliesPrice = suppliesPriceRate
        .mul(subtotalWithProductAndLaborDiscounts)
        .div(new Decimal(1).sub(suppliesPriceRate))
      const suppliesCostRate = new Decimal(quote.dsy_suppliescostrate ?? 0).div(100)
      const suppliesCost = suppliesCostRate
        .mul(subtotalWithProductAndLaborDiscounts)
        .div(new Decimal(1).sub(suppliesCostRate))
      const suppliesMargin = getMarginPercentage(suppliesPrice, suppliesCost)

      const subtotalWithSupplies = subtotal.plus(suppliesPrice)

      // Calculate client loyalty discounts
      const clientLoyaltyDiscountPercentage = new Decimal(quote.discountpercentage ?? 0)
      const clientLoyaltyPercentageDiscountAmount = subtotalWithSupplies
        .mul(clientLoyaltyDiscountPercentage)
        .div(100)
        .abs()
      const clientLoyaltyDiscountAmount = new Decimal(quote.discountamount ?? 0).abs()
      const clientLoyaltyTotalDiscountAmount = shouldApplyDiscounts
        ? clientLoyaltyPercentageDiscountAmount.plus(clientLoyaltyDiscountAmount)
        : new Decimal(0)

      // Total discount is the sum of all discount amounts (all negative)
      const totalDiscountAmount = clientLoyaltyTotalDiscountAmount
        .plus(productsDiscountAmount)
        .plus(laborDiscountAmount)
      const subtotalWithSuppliesAndDiscounts = subtotalWithSupplies.sub(totalDiscountAmount)

      // Calculate tax
      const suppliesTax = getTaxDecimal(suppliesPrice, 1, filters?.previousQuote)
      totals.product.tax = getTaxDecimal(totals.product.taxableAmount, 1, filters?.previousQuote)
      totals.labor.tax = getTaxDecimal(totals.labor.taxableAmount, 1, filters?.previousQuote)
      const totalTaxableAmount = totals.product.taxableAmount.plus(totals.labor.taxableAmount).plus(suppliesPrice)

      const totalTax = getTaxDecimal(totalTaxableAmount, 1, filters?.previousQuote)

      // Final calculations
      const total = subtotalWithSuppliesAndDiscounts.plus(totalTax)
      const totalCost = totals.product.cost.plus(totals.labor.cost).plus(suppliesCost)
      const grossProfitPerHour = !totals.labor.hours.isZero()
        ? subtotalWithSuppliesAndDiscounts.sub(totalCost).div(totals.labor.hours)
        : new Decimal(0)

      return {
        product: {
          quantity: totals.product.quantity.toDecimalPlaces(0).toNumber(),
          cost: totals.product.cost.toDecimalPlaces(2).toNumber(),
          price: totals.product.price.toDecimalPlaces(2).toNumber(),
          priceWithDiscounts: totalProductPriceWithDiscounts.toDecimalPlaces(2).toNumber(),
          margin: getMarginPercentage(totalProductPriceWithDiscounts, totals.product.cost)
            .toDecimalPlaces(2)
            .toNumber(),
          subtotalPercentage: !subtotalWithSuppliesAndDiscounts.isZero()
            ? totalProductPriceWithDiscounts
                .div(subtotalWithSuppliesAndDiscounts)
                .mul(100)
                .toDecimalPlaces(2)
                .toNumber()
            : 0,
          tax: totals.product.tax.toDecimalPlaces(2).toNumber(),
        },
        labor: {
          hours: totals.labor.hours.toDecimalPlaces(5).toNumber(),
          cost: totals.labor.cost.toDecimalPlaces(2).toNumber(),
          price: totals.labor.price.toDecimalPlaces(2).toNumber(),
          priceWithDiscounts: totalLaborPriceWithDiscounts.toDecimalPlaces(2).toNumber(),
          margin: getMarginPercentage(totalLaborPriceWithDiscounts, totals.labor.cost).toDecimalPlaces(2).toNumber(),
          subtotalPercentage: !subtotalWithSuppliesAndDiscounts.isZero()
            ? totalLaborPriceWithDiscounts.div(subtotalWithSuppliesAndDiscounts).mul(100).toDecimalPlaces(2).toNumber()
            : 0,
          tax: totals.labor.tax.toDecimalPlaces(2).toNumber(),
        },
        supplies: {
          price: suppliesPrice.toDecimalPlaces(2).toNumber(),
          cost: suppliesCost.toDecimalPlaces(2).toNumber(),
          margin: suppliesMargin.toDecimalPlaces(2).toNumber(),
          priceRate: suppliesPriceRate.mul(100).toDecimalPlaces(8).toNumber(),
          costRate: suppliesCostRate.mul(100).toDecimalPlaces(8).toNumber(),
          subtotalPercentage: !subtotalWithSuppliesAndDiscounts.isZero()
            ? suppliesPrice.div(subtotalWithSuppliesAndDiscounts).mul(100).toDecimalPlaces(2).toNumber()
            : 0,
          tax: suppliesTax.toDecimalPlaces(2).toNumber(),
        },
        tax: {
          price: totalTax.toDecimalPlaces(2).toNumber(),
          rate: taxRate.toDecimalPlaces(5).toNumber(),
        },
        discounts: {
          products: productsDiscountAmount.toDecimalPlaces(2).toNumber(),
          productsTax: getTaxDecimal(productsDiscountTaxableAmount, 1, filters?.previousQuote).toNumber(),
          productsTaxable: productsDiscountTaxableAmount.toDecimalPlaces(2).toNumber(),
          productsNonTax: productsDiscountNonTaxAmount.toDecimalPlaces(2).toNumber(),
          labor: laborDiscountAmount.toDecimalPlaces(2).toNumber(),
          laborTax: getTaxDecimal(laborDiscountTaxableAmount, 1, filters?.previousQuote).toNumber(),
          laborTaxable: laborDiscountLineAmount,
          laborNonTax: laborDiscountNonTaxAmount.toDecimalPlaces(2).toNumber(),
          customerLoyalty: clientLoyaltyTotalDiscountAmount.toDecimalPlaces(2).toNumber(),
          tax: getTaxDecimal(productsDiscountTaxableAmount.plus(laborDiscountTaxableAmount), 1, filters?.previousQuote)
            .toDecimalPlaces(2)
            .toNumber(),
          total: totalDiscountAmount.toDecimalPlaces(2).toNumber(),
          productsFixed: productsDiscountFixedAmount.toDecimalPlaces(2).toNumber(),
          laborFixed: laborDiscountFixedAmount.toDecimalPlaces(2).toNumber(),
          totalFixed: productsDiscountFixedAmount
            .plus(laborDiscountFixedAmount)
            .plus(shouldApplyDiscounts ? clientLoyaltyDiscountAmount : 0)
            .toDecimalPlaces(2)
            .toNumber(),
        },
        subtotal: subtotal.toDecimalPlaces(2).toNumber(),
        subtotalWithSupplies: subtotalWithSupplies.toDecimalPlaces(2).toNumber(),
        subtotalWithSuppliesAndDiscounts: subtotalWithSuppliesAndDiscounts.toDecimalPlaces(2).toNumber(),
        storedSubtotalAdjustment: 0,
        totalMargin: getMarginPercentage(subtotalWithSuppliesAndDiscounts, totalCost).toDecimalPlaces(2).toNumber(),
        total: total.toDecimalPlaces(2).toNumber(),
        totalCost: totalCost.toDecimalPlaces(2).toNumber(),
        grossProfitPerHour: grossProfitPerHour.toDecimalPlaces(2).toNumber(),
      }
}

export function useQuoteDetailsSummary<T = QuoteDetailsSummary>(
  filters?: QuoteDetailFilters | null,
  select?: (summary: QuoteDetailsSummary) => T
) {
  const { data: quote } = useQuoteQuery({ previousQuote: filters?.previousQuote })
  const { data: originalQuote, isFetched: originalQuoteIsFetched } = useOriginalQuoteQuery()

  // Parent rooms roll their child rooms up: one reduce over the whole group, not
  // a sum of per-room summaries (discounts are filter-gated and supplies re-gross
  // off the group subtotal, so the two do not agree).
  const {
    data: quoteDetails,
    dataUpdatedAt: quoteDetailsUpdatedAt,
    roomIds: groupRoomIds,
  } = useRoomScopedQuoteDetails(filters)

  const { data: summaryDetails, dataUpdatedAt: summaryDetailsUpdatedAt } = useQuoteDetailsQuery({
    onlySummaryDetails: 'all',
    previousQuote: filters?.previousQuote,
  })

  const cpqVersion = useCPQVersion(filters?.previousQuote)

  // CO chains rooted before the cutoff were invoiced under pre-fix V2 (no ratio split).
  // Force the legacy ratio=1 path so totals match parent SO regardless of NONTAX line presence.
  // Filter on getOriginalQuote guarantees dsy_approvalsignaturedatetime is non-null.
  const isLegacyChain = isLegacyDiscountChain(quote?.dsy_type, originalQuote)

  const calculateSummary = useCallback(
    ({
      filters,
      quote,
      quoteDetails,
      summaryDetails,
      isLegacyChain,
    }: {
      filters: QuoteDetailFilters | undefined
      quote: QuoteData
      quoteDetails: QuoteDetailData[]
      summaryDetails: QuoteDetailData[]
      isLegacyChain: boolean
    }) => {
      const summary =
        cpqVersion === 2
          ? calculateSummaryV2({ filters, quote, quoteDetails, summaryDetails, isLegacyChain })
          : calculateSummaryV1({ filters, quote, quoteDetails, summaryDetails })
      return applyLockedQuoteStoredTotals({ filters, quote, summary })
    },
    [cpqVersion]
  )

  const isChangeOrder = quote?.dsy_type === QuoteType.ChangeOrder

  const { data, dataUpdatedAt, isFetching, isLoading } = useQuery({
    queryKey: [
      ...QUERY_KEYS.quoteDetailSummary(
        filters,
        quote,
        quoteDetailsUpdatedAt,
        summaryDetailsUpdatedAt,
        groupRoomIds
      ),
      // Nothing else in the key tracks this side's version: while the
      // (previous-side) quote is still loading the version reads as 1, and a
      // summary cached under that transient value used to survive the settle —
      // a non-deterministic V1/V2 baseline. The key entry makes it invalidate.
      { isLegacyChain, cpqVersion },
    ],
    // Every cache write moves quoteDetailsUpdatedAt, so the key changes on each
    // edit and the new key starts empty — the roll-up columns would blank out
    // and repopulate twice per keystroke-commit. Keep the last summary on
    // screen until the recomputed one lands.
    placeholderData: keepPreviousData,
    // Wait for originalQuote to settle on CO quotes — otherwise initial render flickers wrong totals.
    enabled: !!quote && !!quoteDetails && !!summaryDetails && (!isChangeOrder || originalQuoteIsFetched),
    queryFn: () => {
      if (!quote || !quoteDetails || !summaryDetails) {
        throw new Error('Quote, quote details, or summary details not found')
      }
      return calculateSummary({ filters, quote, quoteDetails, summaryDetails, isLegacyChain })
    },
  })

  return useMemo(() => {
    const ensuredData = data ?? INITIAL_SUMMARY
    const selectedData = select ? select(ensuredData) : ensuredData
    // `roomIds` is the group this summary actually reduced over (null for exact
    // reads) — `useChangesSummary` needs it for its own key.
    return { data: selectedData as T, dataUpdatedAt, isFetching, isLoading, roomIds: groupRoomIds }
  }, [data, dataUpdatedAt, isFetching, isLoading, select, groupRoomIds])
}

export type AccessoryPriceListItemData = PriceListItemData & {
  dsy_quantity: number | null
  dsy_requirement: number | null
  /**
   * Package contents only: the content this one hangs under, so the insert
   * recreates it as that member's accessory instead of a member of its own.
   * Null/absent on every ordinary accessory read.
   */
  packageParentProductId?: string | null
}

export async function getAccessoryPriceListItems({
  productId,
  priceListId,
}: {
  productId: string
  priceListId: string
}) {
  // Get all accessory price list items in one query
  const accessoryPriceListItems = await getMultipleRecords<{
    dsy_quantity: number | null
    dsy_requirement: number | null
    dsy_accessorypricelistitem: PriceListItemData
  }>(
    'dsy_pricelistitemaccessories',
    `?$select=dsy_quantity,dsy_requirement
    &$expand=dsy_accessorypricelistitem(
      $select=_dsy_product_value,_dsy_pricelist_value,dsy_unitcost,dsy_unitprice,dsy_taxable,dsy_description,dsy_engineeringlabor,dsy_installationlabor,dsy_roughinlabor,dsy_programminglabor,dsy_projectmanagementlabor,dsy_servicelabor;
      $expand=dsy_product($select=productid,name,msdyn_fieldserviceproducttype,dsy_defaultphase,entityimage,_defaultuomid_value,dsy_ispackage))
    &$filter=dsy_parentpricelistitem/_dsy_product_value eq '${productId}' and _dsy_pricelist_value eq '${priceListId}'`
  )

  if (!accessoryPriceListItems.length) {
    return []
  }

  return accessoryPriceListItems.map((accessory) => ({
    ...accessory.dsy_accessorypricelistitem,
    dsy_quantity: accessory.dsy_quantity,
    dsy_requirement: accessory.dsy_requirement,
  }))
}

export function useAccessoryPriceListItemsQuery({ productId }: { productId: string | undefined | null }) {
  const { data: quote } = useQuoteQuery()
  const priceListId = quote?._pricelevelid_value

  return useQuery({
    queryKey: QUERY_KEYS.accessoryPriceListItems({ productId, priceListId }),
    enabled: !!productId && !!priceListId,
    queryFn: async () => {
      if (!productId) {
        throw new Error('Product is required')
      }
      if (!priceListId) {
        throw new Error('Price list is required')
      }
      return getAccessoryPriceListItems({ productId, priceListId })
    },
  })
}

const PACKAGE_CONTENT_PRICE_LIST_ITEM_SELECT =
  '_dsy_product_value,_dsy_pricelist_value,dsy_unitcost,dsy_unitprice,dsy_taxable,dsy_description,' +
  'dsy_engineeringlabor,dsy_installationlabor,dsy_roughinlabor,dsy_programminglabor,dsy_projectmanagementlabor,dsy_servicelabor,' +
  'dsy_systemsearchtag,dsy_scopesearchtag,dsy_searchtag1text,dsy_searchtag2text,dsy_searchtag3text,dsy_searchtag4text,' +
  'dsy_searchtag5text,dsy_searchtag6text,dsy_manufacturertext'

const PACKAGE_CONTENT_PRODUCT_EXPAND =
  'dsy_product($select=productid,name,msdyn_fieldserviceproducttype,dsy_defaultphase,entityimage_url,' +
  '_defaultuomid_value,dsy_partnumber,statuscode,dsy_weight,dsy_subcategory,dsy_vesapattern,dsy_ispackage)'

const productAccessorySchema = z.object({
  dsy_quantity: z.number().nullable(),
  dsy_requirement: z.number().nullable(),
  _dsy_accessoryproduct_value: z.string(),
  /**
   * The member product this content nests under, written literally at save time.
   *
   * Optional, not required: every content saved before the column existed — and
   * every content of a D-Tools SI package — carries no parent at all, and those
   * rows still have to parse.
   */
  _dsy_packageparentproduct_value: z.string().optional().nullable(),
})

/**
 * Which contents of a package are accessories of other contents, INFERRED from
 * the catalog's own accessory definitions.
 *
 * The fallback, not the rule: a package saved today records each content's
 * literal parent in dsy_packageparentproduct. This rebuilds the shape of the
 * ones that cannot — packages saved before that column existed, and the ones
 * D-Tools SI writes — from the member products' OWN catalog accessory
 * definitions: a content that the catalog lists as an accessory of another
 * content is re-attached under it, everything else stays a member.
 *
 * The rules keep the result a deterministic two-level tree, matching what a
 * quote can actually hold:
 * - edges to or from products outside this package are ignored, as are self-edges
 * - a content whose only candidate parents are themselves accessories stays a
 *   member, so a chain never nests deeper than one level and a cycle resolves
 * - with several candidate parents the lowest product id wins, so two inserts of
 *   the same package produce the same tree
 *
 * A content the catalog no longer lists under any other content comes back
 * unmapped and inserts as a plain member: the item and its money are kept, only
 * the nesting is lost.
 */
export function resolvePackageContentParents(
  contentProductIds: string[],
  accessoryEdges: { parentProductId: string; accessoryProductId: string }[]
): Map<string, string> {
  const contents = new Set(contentProductIds)
  const candidateParents = new Map<string, string[]>()

  for (const edge of accessoryEdges) {
    if (edge.parentProductId === edge.accessoryProductId) continue
    if (!contents.has(edge.parentProductId) || !contents.has(edge.accessoryProductId)) continue
    const existing = candidateParents.get(edge.accessoryProductId)
    if (existing) existing.push(edge.parentProductId)
    else candidateParents.set(edge.accessoryProductId, [edge.parentProductId])
  }

  const parentByProductId = new Map<string, string>()
  for (const [accessoryProductId, parents] of candidateParents) {
    const parent = [...parents].sort().find((candidate) => !candidateParents.has(candidate))
    if (parent) parentByProductId.set(accessoryProductId, parent)
  }
  return parentByProductId
}

export type PackageInsertContent = {
  productId: string
  /** From getPackageContentPriceListItems; null on a content that is nobody's accessory. */
  packageParentProductId?: string | null
  checked: boolean
}

/**
 * How a package's contents split into the two waves an insert writes: members
 * first, then the accessories that hang under them.
 *
 * The split exists because a member's dsy_globalid is assigned by the server, so
 * an accessory cannot be linked until its member has been created and read back.
 *
 * Two contents never reach the second wave: one whose own member is not on the
 * list at all (dropped for want of a price, or already on the quote) becomes a
 * member itself rather than being lost, and one whose member is on the list but
 * unchecked is skipped entirely — the checklist renders it unchecked under its
 * unchecked item, and an accessory follows its item.
 */
export function planPackageContentInsert<T extends PackageInsertContent>(contents: T[]) {
  const listedProductIds = new Set(contents.map((content) => content.productId))
  const checkedProductIds = new Set(
    contents.filter((content) => content.checked).map((content) => content.productId)
  )
  const memberOf = (content: T) =>
    content.packageParentProductId && listedProductIds.has(content.packageParentProductId)
      ? content.packageParentProductId
      : null

  const checked = contents.filter((content) => content.checked)
  return {
    members: checked.filter((content) => !memberOf(content)),
    accessories: checked
      .filter((content) => {
        const parentProductId = memberOf(content)
        return !!parentProductId && checkedProductIds.has(parentProductId)
      })
      .map((content) => ({ content, parentProductId: memberOf(content)! })),
  }
}

const productAccessoryEdgeSchema = z.object({
  _dsy_parentproduct_value: z.string(),
  _dsy_accessoryproduct_value: z.string(),
})

/**
 * The catalog accessory edges among a set of products: every
 * dsy_productaccessory row whose parent is one of them.
 *
 * Batched at GLOBAL_ID_READBACK_BATCH_SIZE like every other or-filtered
 * readback here: a full-house package carries 150+ contents, and one clause per
 * content is a query string long enough for Dataverse to reject — which would
 * fail the whole content-pricing read and insert the package with no items.
 */
export async function getProductAccessoryEdges(productIds: string[]) {
  const batches: string[][] = []
  for (let i = 0; i < productIds.length; i += GLOBAL_ID_READBACK_BATCH_SIZE) {
    batches.push(productIds.slice(i, i + GLOBAL_ID_READBACK_BATCH_SIZE))
  }

  const results = await Promise.all(
    batches.map((batch) =>
      getMultipleRecords<unknown>(
        'dsy_productaccessories',
        `?$select=_dsy_parentproduct_value,_dsy_accessoryproduct_value&$filter=${batch
          .map((productId) => `_dsy_parentproduct_value eq '${encodeODataLiteral(productId)}'`)
          .join(' or ')}`
      )
    )
  )

  // Parsed per row and dropped on failure, like the price list items this feeds:
  // one malformed edge must not reject the whole content read, which would fail
  // the create it is part of and leave a header on the quote with no items.
  const rows = results.flat()
  const edges = rows.flatMap((row) => {
    const parsed = productAccessoryEdgeSchema.safeParse(row)
    if (!parsed.success) return []
    return [
      {
        parentProductId: parsed.data._dsy_parentproduct_value,
        accessoryProductId: parsed.data._dsy_accessoryproduct_value,
      },
    ]
  })
  if (edges.length < rows.length) {
    trackException(new Error(`${rows.length - edges.length} product accessory edge(s) failed schema validation`), {
      context: 'quote-details.getProductAccessoryEdges',
      totalRecords: String(rows.length),
      droppedRecords: String(rows.length - edges.length),
    })
  }
  return edges
}

/**
 * Where each content of a package nests, literal first.
 *
 * A package saved from a quote records every content's actual parent member in
 * dsy_packageparentproduct, so the tree the author saw is the tree that comes
 * back. Only when NOT ONE content carries a stored parent — a package saved
 * before the column existed, or one D-Tools SI wrote — is the shape inferred
 * from the catalog's accessory edges instead, which also spares that read on
 * every package that does carry them.
 *
 * A stored parent that is not itself a priced content of this package (deleted,
 * unpriced on this price list, or the row's own product) is dropped, not
 * honoured: the content inserts as a top-level member. The row is never lost.
 */
async function getPackageContentParents(
  contents: { _dsy_accessoryproduct_value: string; _dsy_packageparentproduct_value?: string | null }[],
  pricedProductIds: string[]
): Promise<Map<string, string>> {
  const priced = new Set(pricedProductIds)
  const storedParents = new Map<string, string>()
  let hasStoredParent = false

  for (const content of contents) {
    const parentProductId = content._dsy_packageparentproduct_value
    if (!parentProductId) continue
    hasStoredParent = true
    const productId = content._dsy_accessoryproduct_value
    if (parentProductId === productId) continue
    if (!priced.has(parentProductId) || !priced.has(productId)) continue
    storedParents.set(productId, parentProductId)
  }

  if (hasStoredParent) return storedParents
  return resolvePackageContentParents(pricedProductIds, await getProductAccessoryEdges(pricedProductIds))
}

/**
 * A catalog package's contents, priced against a price list right now.
 *
 * Read from dsy_productaccessory (the package's own contents) and joined to
 * live price list items, NOT from dsy_pricelistitemaccessory: that projection is
 * rebuilt by a batch import, so a package saved today would have no contents
 * until the next run. Joining here also guarantees the prices are the ones this
 * quote's price list holds at insert time.
 *
 * Contents that are accessories of other contents carry packageParentProductId
 * (see getPackageContentParents) so the insert can nest them under their member
 * instead of adding them as members of their own.
 *
 * Contents with no price list item on this price list are dropped — there is no
 * price to put on the line — and how many were dropped comes back with the
 * items, so an insert can say so instead of quietly adding a smaller package.
 */
export type PackageContentPriceListItems = {
  items: AccessoryPriceListItemData[]
  /** Catalog contents with no price list item on this price list. */
  unpricedCount: number
}

export async function getPackageContentPriceListItems({
  productId,
  priceListId,
}: {
  productId: string
  priceListId: string
}): Promise<PackageContentPriceListItems> {
  const rawContents = await getMultipleRecords<unknown>(
    'dsy_productaccessories',
    `?$select=dsy_quantity,dsy_requirement,_dsy_accessoryproduct_value,_dsy_packageparentproduct_value` +
      `&$filter=_dsy_parentproduct_value eq '${encodeODataLiteral(productId)}'`
  )

  // Parsed per row and dropped on failure, like the edges and the price list
  // items: one malformed content row must not reject the whole read, which
  // fails the insert it is part of and adds a header with nothing under it.
  const contents = rawContents.flatMap((row) => {
    const parsed = productAccessorySchema.safeParse(row)
    return parsed.success ? [parsed.data] : []
  })
  if (contents.length < rawContents.length) {
    trackException(
      new Error(`${rawContents.length - contents.length} package content(s) failed schema validation and were dropped`),
      {
        context: 'quote-details.getPackageContentPriceListItems.contents',
        productId,
        totalRecords: String(rawContents.length),
        droppedRecords: String(rawContents.length - contents.length),
      }
    )
  }

  if (!contents.length) return { items: [], unpricedCount: 0 }

  const productFilter = contents
    .map((content) => `_dsy_product_value eq '${encodeODataLiteral(content._dsy_accessoryproduct_value)}'`)
    .join(' or ')

  const rawPriceListItems = await getMultipleRecords<unknown>(
    'dsy_pricelistitems',
    `?$select=${PACKAGE_CONTENT_PRICE_LIST_ITEM_SELECT}&$expand=${PACKAGE_CONTENT_PRODUCT_EXPAND}` +
      `&$filter=_dsy_pricelist_value eq '${encodeODataLiteral(priceListId)}' and (${productFilter})`
  )

  // Parsed, not cast, exactly like the product search: a row missing a labor
  // rate would otherwise reach the insert as a NaN price. A dropped row is a
  // content with no usable price — the same outcome as no price list item at
  // all — and the drop is reported rather than silent.
  const parsedResults = rawPriceListItems.map((item) => priceListItemSchema.safeParse(item))
  const priceListItems = parsedResults
    .filter((result): result is z.SafeParseSuccess<PriceListItemData> => result.success)
    .map((result) => result.data)
  if (priceListItems.length < rawPriceListItems.length) {
    trackException(
      new Error(
        `${rawPriceListItems.length - priceListItems.length} package content(s) failed schema validation and were excluded`
      ),
      {
        context: 'quote-details.getPackageContentPriceListItems',
        productId,
        totalRecords: String(rawPriceListItems.length),
        droppedRecords: String(rawPriceListItems.length - priceListItems.length),
        failedItems: JSON.stringify(
          parsedResults
            .flatMap((result, index) =>
              result.success
                ? []
                : [
                    {
                      id: (rawPriceListItems[index] as Record<string, unknown>).dsy_pricelistitemid,
                      errors: result.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`),
                    },
                  ]
            )
            .slice(0, 10)
        ),
      }
    )
  }

  const byProduct = new Map(priceListItems.map((item) => [item._dsy_product_value, item]))

  // Resolved over the PRICED contents only: a content with no price list item
  // never reaches the quote, so it can neither be nor have a parent there.
  const pricedProductIds = contents
    .map((content) => content._dsy_accessoryproduct_value)
    .filter((contentProductId) => byProduct.has(contentProductId))
  const parentByProductId = await getPackageContentParents(contents, pricedProductIds)

  const items = contents.flatMap((content) => {
    const priceListItem = byProduct.get(content._dsy_accessoryproduct_value)
    if (!priceListItem) return []
    return [
      {
        ...priceListItem,
        dsy_quantity: content.dsy_quantity ?? 1,
        dsy_requirement: content.dsy_requirement ?? PRODUCT_REQUIREMENT.Optional,
        packageParentProductId: parentByProductId.get(content._dsy_accessoryproduct_value) ?? null,
      },
    ]
  })

  return { items, unpricedCount: contents.length - items.length }
}

/** One priced package content, plus whether the caller wants it written. */
export type PackageContentInsertRow = AccessoryPriceListItemData & { checked: boolean }

export type PackageContentInsertResult = {
  /** Rows that landed on the quote — members and accessories together. */
  created: number
  failed: { productName: string; error: unknown }[]
  /**
   * Accessories written as plain package members because their member's
   * dsy_globalid never came back (or the member itself failed): the item and
   * its money are kept, only the nesting is lost.
   */
  flattenedAccessoryCount: number
  /**
   * Checked contents skipped for holding a quantity of 0 — a legacy or SI
   * catalog row nothing on the quote can represent. Reported rather than
   * silently missing from the count.
   */
  skippedZeroQuantityCount: number
}

/**
 * Write a package's contents under an existing header — the one insert path,
 * shared by the direct insert and the header's own "add items" dialog.
 *
 * Two waves, because a content that is an accessory of another content can only
 * be linked once its member exists and its server-assigned dsy_globalid has
 * been read back.
 *
 * Every dsy_grouporder is assigned here, from one base slot read once:
 * getEndOfGroupOrder reads the cache, and concurrent creates all read the same
 * snapshot, so letting each row compute its own slot lands the entire package
 * on dsy_grouporder = 1 and leaves the order to the sort's tiebreaker.
 *
 * allSettled on both waves: a rejected content must not abandon the ones that
 * already landed, and it must not take the caller's invalidation with it. The
 * result is facts, not side effects — no toast, no invalidation. Callers own
 * both, and the direct-insert path invalidates on either outcome.
 */
export async function insertPackageContents({
  headerQuoteDetail,
  contents,
  queryClient: client,
  createLine = createQuoteDetailFromPriceListItem,
}: {
  headerQuoteDetail: QuoteDetailData
  contents: PackageContentInsertRow[]
  queryClient: QueryClient
  /**
   * The line create. Injectable because the atomic batch create owns its own
   * test suite and bun's mock.module is process-global: stubbing that module
   * to exercise these two waves would stub it out from under that suite too.
   */
  createLine?: (
    variables: CreateQuoteDetailFromPriceListItemVariables,
    queryClient: QueryClient
  ) => Promise<string>
}): Promise<PackageContentInsertResult> {
  const fields = headerQuoteDetail.dsy_cpqfields
  const roomId = fields._dsy_room_value
  const systemId = fields.dsy_system
  const headerGlobalId = fields.dsy_globalid
  if (!roomId || !systemId) throw new Error('A package must belong to a room and system')
  if (!headerGlobalId) throw new Error('Package header is missing its global id')

  const skippedZeroQuantityCount = contents.filter((item) => item.checked && item.dsy_quantity === 0).length

  const { members, accessories } = planPackageContentInsert(
    contents.map((item) => ({
      productId: item.dsy_product.productid,
      packageParentProductId: item.packageParentProductId,
      checked: item.checked && item.dsy_quantity !== 0,
      item,
    }))
  )

  const baseGroupOrder = getEndOfGroupOrder(client.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails()), {
    roomId,
    systemId,
    scopeTag: fields.dsy_scopetag ?? null,
    packageGlobalId: headerGlobalId,
    parentGlobalId: null,
  })

  // One running counter over the interleaved order — member, that member's
  // accessories, next member — so members read in list order and each member's
  // accessories stay contiguous under it. A member and its accessories number
  // in different slot spaces, so only the relative order matters.
  const accessoriesByParent = new Map<string, typeof accessories>()
  for (const accessory of accessories) {
    const group = accessoriesByParent.get(accessory.parentProductId)
    if (group) group.push(accessory)
    else accessoriesByParent.set(accessory.parentProductId, [accessory])
  }
  const groupOrderByProductId = new Map<string, number>()
  let slot = baseGroupOrder
  for (const member of members) {
    groupOrderByProductId.set(member.productId, slot++)
    for (const accessory of accessoriesByParent.get(member.productId) ?? []) {
      groupOrderByProductId.set(accessory.content.productId, slot++)
    }
  }

  const createRow = (
    row: PackageContentInsertRow,
    links: { parentGlobalId: string | null; packageGlobalId: string | null },
    groupOrder: number | undefined
  ) =>
    createLine(
      {
        priceListItem: row,
        roomId,
        systemId,
        systemScopeTag: fields.dsy_scopetag,
        ...links,
        quantity: row.dsy_quantity,
        groupOrder,
        // The saved quantity is the answer, wire runs included: a package whose
        // author saved CABLE-CAT6 × 1 must not insert at the quote's default
        // run length.
        isPackageContent: true,
      },
      client
    )

  const failed: { productName: string; error: unknown }[] = []
  let created = 0

  const settledMembers = await Promise.allSettled(
    members.map((member) =>
      createRow(
        member.item,
        { parentGlobalId: null, packageGlobalId: headerGlobalId },
        groupOrderByProductId.get(member.productId)
      )
    )
  )

  const memberQuoteDetailIds: string[] = []
  settledMembers.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      created += 1
      memberQuoteDetailIds.push(result.value)
      return
    }
    failed.push({ productName: members[index].item.dsy_product.name, error: result.reason })
  })

  let flattenedAccessoryCount = 0

  if (accessories.length) {
    // A failed readback is not a failed insert: the members are already on the
    // quote, so it degrades to "no global ids" and the accessories go in flat
    // (disclosed through flattenedAccessoryCount) instead of throwing over rows
    // that exist and toasting "could not add".
    let globalIdByQuoteDetailId = new Map<string, string>()
    if (memberQuoteDetailIds.length) {
      try {
        globalIdByQuoteDetailId = await getCreatedGlobalIdsByQuoteDetailId(memberQuoteDetailIds)
      } catch (readbackError) {
        trackException(
          readbackError instanceof Error ? readbackError : new Error(String(readbackError)),
          {
            context: 'quote-details.insertPackageContents.globalIdReadback',
            headerGlobalId,
            memberQuoteDetailIds: memberQuoteDetailIds.join(','),
          }
        )
      }
    }
    const memberGlobalIdByProductId = new Map<string, string>()
    settledMembers.forEach((result, index) => {
      if (result.status !== 'fulfilled') return
      const globalId = globalIdByQuoteDetailId.get(result.value)
      if (globalId) memberGlobalIdByProductId.set(members[index].productId, globalId)
    })

    const unresolvedMemberIds: string[] = []
    const settledAccessories = await Promise.allSettled(
      accessories.map(({ content, parentProductId }) => {
        const parentGlobalId = memberGlobalIdByProductId.get(parentProductId) ?? null
        if (!parentGlobalId) unresolvedMemberIds.push(parentProductId)
        // Never both keys: the server-side XOR rejects such a row, so a member
        // whose global id never came back takes its accessory as a package
        // member rather than losing it.
        return createRow(
          content.item,
          parentGlobalId
            ? { parentGlobalId, packageGlobalId: null }
            : { parentGlobalId: null, packageGlobalId: headerGlobalId },
          groupOrderByProductId.get(content.productId)
        )
      })
    )

    settledAccessories.forEach((result, index) => {
      if (result.status !== 'fulfilled') {
        failed.push({ productName: accessories[index].content.item.dsy_product.name, error: result.reason })
        return
      }
      created += 1
      // Counted on the rows that actually landed: an accessory that failed is
      // reported as a failure, not as one that lost its nesting.
      if (!memberGlobalIdByProductId.has(accessories[index].parentProductId)) flattenedAccessoryCount += 1
    })

    if (flattenedAccessoryCount) {
      trackException(
        new Error(`${flattenedAccessoryCount} package accessory(s) were added flat — no member global id to nest under`),
        {
          context: 'quote-details.insertPackageContents',
          headerGlobalId,
          flattenedAccessoryCount: String(flattenedAccessoryCount),
          memberProductIds: Array.from(new Set(unresolvedMemberIds)).join(','),
        }
      )
    }
  }

  // Nothing landed at all: there is no partial package to report, so the caller
  // gets the failure it would have got before any of this was settled.
  if (!created && failed.length) throw failed[0].error

  return { created, failed, flattenedAccessoryCount, skippedZeroQuantityCount }
}

/**
 * The direct insert behind adding a catalog package from search: the header is
 * already on the quote, so its contents go straight in.
 *
 * Every catalog content is stamped Required when the package is saved, so there
 * is nothing for a picker to decide — the dialog it replaces asked a question
 * with one answer, and cancelling it left an empty header nothing could fill.
 * The dialog itself stays reachable from the header's own menu.
 */
async function insertCatalogPackageContents({
  headerQuoteDetail,
  headerQuoteDetailId,
  packageName,
  contents,
  queryClient: client,
}: {
  headerQuoteDetail: QuoteDetailData | null
  headerQuoteDetailId: string | null
  packageName: string
  contents: PackageContentPriceListItems | undefined
  queryClient: QueryClient
}) {
  if (!contents?.items.length) {
    // A package whose contents are priced nowhere on this quote's price list
    // lands as a bare $0 header with no members. Silence reads as a broken
    // insert, so name what happened and point at the affordance that fixes it.
    toast({
      title: `Added "${packageName}" with no items`,
      description:
        "None of its contents are priced on this quote's price list. Delete it or add items from its menu.",
    })
    return
  }

  // Without the header row there is nothing to hang members off. The dialog is
  // the one remaining way in, so hand the user to it rather than dropping the
  // contents on the floor.
  if (!headerQuoteDetail) {
    if (headerQuoteDetailId) useAddAccessoriesDialogStore.getState().openDialog(headerQuoteDetailId)
    return
  }

  let result: PackageContentInsertResult | null = null
  let error: unknown = null
  try {
    result = await insertPackageContents({
      headerQuoteDetail,
      contents: contents.items.map((item) => ({ ...item, checked: true })),
      queryClient: client,
    })
  } catch (insertError) {
    error = insertError
  }

  // Rows were written whichever way this went, so the cache is stale either
  // way — invalidate before the toast, never behind an early return.
  await Promise.all(
    getUniqueQueryKeys([
      QUERY_KEYS.quoteDetails(),
      QUERY_KEYS.quoteDetails({ roomId: headerQuoteDetail.dsy_cpqfields._dsy_room_value }),
      QUERY_KEYS.quoteDetails({ systemId: headerQuoteDetail.dsy_cpqfields.dsy_system }),
      QUERY_KEYS.quoteDetails({
        roomId: headerQuoteDetail.dsy_cpqfields._dsy_room_value,
        systemId: headerQuoteDetail.dsy_cpqfields.dsy_system,
      }),
    ]).map((queryKey) => client.invalidateQueries({ queryKey }))
  )

  if (!result) {
    toast({ title: `Could not add the items to "${packageName}"`, error, variant: 'destructive' })
    return
  }

  const notes: string[] = []
  if (result.failed.length) {
    notes.push(`Could not add: ${result.failed.map((failure) => failure.productName).join(', ')}.`)
  }
  if (result.flattenedAccessoryCount) {
    notes.push(`${result.flattenedAccessoryCount} item(s) could not be nested under their item.`)
  }
  if (result.skippedZeroQuantityCount) {
    notes.push(`${result.skippedZeroQuantityCount} zero-quantity item(s) skipped.`)
  }
  if (contents.unpricedCount) {
    notes.push(`${contents.unpricedCount} catalog item(s) are not priced on this quote's price list.`)
  }

  toast({
    title:
      `Added ${packageName} — ${result.created} items` +
      (result.failed.length ? ` (${result.failed.length} failed)` : ''),
    description: notes.length ? notes.join(' ') : undefined,
    variant: result.failed.length ? 'destructive' : 'default',
  })
}

export function usePackageContentPriceListItemsQuery({ productId }: { productId: string | undefined | null }) {
  const { data: quote } = useQuoteQuery()
  const priceListId = quote?._pricelevelid_value

  return useQuery({
    queryKey: QUERY_KEYS.packageContentPriceListItems({ productId, priceListId }),
    enabled: !!productId && !!priceListId && productId !== PRODUCT_ID_MAP.PACKAGE,
    queryFn: async () => {
      if (!productId) throw new Error('Product is required')
      if (!priceListId) throw new Error('Price list is required')
      return await getPackageContentPriceListItems({ productId, priceListId })
    },
  })
}

export type LaborHoursChanges = {
  engineering?: { from: number | null; to: number | null }
  installation?: { from: number | null; to: number | null }
  roughin?: { from: number | null; to: number | null }
  programming?: { from: number | null; to: number | null }
  projectManagement?: { from: number | null; to: number | null }
  service?: { from: number | null; to: number | null }
}
export type DetailChange = {
  type: 'new' | 'changed' | 'deleted'
  newQuoteDetail?: QuoteDetailData & {
    phaseLabel: string | undefined
    systemLabel: string | undefined
    roomLabel: string | undefined
  }
  previousQuoteDetail?: QuoteDetailData & {
    phaseLabel: string | undefined
    systemLabel: string | undefined
    roomLabel: string | undefined
  }
  changes?: {
    quotedetailname?: { from: string | null; to: string | null }
    description?: { from: string | null; to: string | null }
    quantity?: { from: number; to: number; variance: number }
    priceperunit?: { from: number | null; to: number | null; variance: number }
    manualdiscountamount?: { from: number | null; to: number | null }
    dsy_laborfactorpercent?: { from: number | null; to: number | null }
    msdyn_costpriceperunit?: { from: number | null; to: number | null; variance: number }
    msdyn_taxable?: { from: boolean | null; to: boolean | null }
    room?: { from: string | null; to: string | null }
    system?: { from: number | null; to: number | null }
    phase?: { from: number | null; to: number | null }
    location?: { from: string | null; to: string | null }
    laborHours?: LaborHoursChanges
  }
}

/**
 * A renamed row, but only where the name is the author's own: a package header
 * carries the package label, so renaming one is a real change-order change.
 * Every other row takes its name from the catalog product, where a product
 * rename would flood a change order with entries nobody made on the quote.
 */
export function getPackageHeaderNameChange(
  quoteDetail: QuoteDetailData,
  previousQuoteDetail: QuoteDetailData
): { from: string | null; to: string | null } | null {
  if (!isPackageHeader(quoteDetail.productid) && !isPackageHeader(previousQuoteDetail.productid)) return null
  if (quoteDetail.quotedetailname === previousQuoteDetail.quotedetailname) return null
  return { from: previousQuoteDetail.quotedetailname, to: quoteDetail.quotedetailname }
}

// NOTE: This logic is duplicated 1-to-1 in the backend at
// GET /api/change-order/{quoteId}/changes (proposal-api).
// Any edits here must be mirrored there, and vice versa.
export function useQuoteDetailsChanges(filters?: QuoteDetailFilters | null) {
  // Both sides widen together under a parent filter: a change on a child-room
  // line only reads as a change when both snapshots cover that child room.
  const currentFilters = useMemo<QuoteDetailFilters>(() => ({ ...filters, previousQuote: false }), [filters])
  const previousFilters = useMemo<QuoteDetailFilters>(() => ({ ...filters, previousQuote: true }), [filters])
  const {
    data: quoteDetails,
    dataUpdatedAt: quoteDetailsUpdatedAt,
    roomIds: groupRoomIds,
  } = useRoomScopedQuoteDetails(currentFilters)
  const { data: previousQuoteDetails, dataUpdatedAt: previousQuoteDetailsUpdatedAt } =
    useRoomScopedQuoteDetails(previousFilters)

  const { data, dataUpdatedAt } = useQuery({
    queryKey: QUERY_KEYS.quoteDetailsChanges(
      filters,
      quoteDetailsUpdatedAt,
      previousQuoteDetailsUpdatedAt,
      groupRoomIds
    ),
    enabled: !!quoteDetails && !!previousQuoteDetails,
    queryFn: () => {
      if (!quoteDetails || !previousQuoteDetails) {
        throw new Error('Quote details or previous quote details not found')
      }

      const changes: DetailChange[] = []

      // Create maps for faster lookups
      const quoteDetailsMap = new Map(quoteDetails.map((detail) => [detail.dsy_cpqfields.dsy_globalid, detail]))
      const previousQuoteDetailsMap = new Map(
        previousQuoteDetails.map((detail) => [detail.dsy_cpqfields.dsy_globalid, detail])
      )

      // Find new and changed items
      quoteDetails.forEach((quoteDetail) => {
        const previousQuoteDetail = previousQuoteDetailsMap.get(quoteDetail.dsy_cpqfields.dsy_globalid)

        if (!previousQuoteDetail) {
          // New item
          changes.push({
            type: 'new',
            newQuoteDetail: {
              ...quoteDetail,
              phaseLabel: getPhaseLabel(quoteDetail.dsy_cpqfields.dsy_phase),
              systemLabel: getSystemLabel(quoteDetail.dsy_cpqfields.dsy_system),
              roomLabel: getRoomLabel(quoteDetail.dsy_cpqfields._dsy_room_value),
            },
          })
          return
        }

        // Check for changes
        const detailChanges: DetailChange['changes'] = {}
        let hasChanges = false

        // Compare the row name — package headers only (see the predicate).
        const nameChange = getPackageHeaderNameChange(quoteDetail, previousQuoteDetail)
        if (nameChange) {
          detailChanges.quotedetailname = nameChange
          hasChanges = true
        }

        // Compare description
        if (quoteDetail.description !== previousQuoteDetail.description) {
          detailChanges.description = {
            from: previousQuoteDetail.description,
            to: quoteDetail.description,
          }
          hasChanges = true
        }

        // Compare quantities
        const newQuantity = new Decimal(quoteDetail.quantity)
        const previousQuantity = new Decimal(previousQuoteDetail.quantity)
        if (!newQuantity.eq(previousQuantity)) {
          detailChanges.quantity = {
            from: previousQuantity.toNumber(),
            to: newQuantity.toNumber(),
            variance: newQuantity.minus(previousQuantity).toNumber(),
          }
          hasChanges = true
        }

        // Compare prices
        const newPrice = new Decimal(quoteDetail.priceperunit || 0)
        const previousPrice = new Decimal(previousQuoteDetail.priceperunit || 0)
        if (!newPrice.eq(previousPrice)) {
          detailChanges.priceperunit = {
            from: previousPrice.toNumber(),
            to: newPrice.toNumber(),
            variance: newPrice.minus(previousPrice).toNumber(),
          }
          hasChanges = true
        }

        // Authored input may change before calculated prices settle.
        if (quoteDetail.manualdiscountamount !== previousQuoteDetail.manualdiscountamount) {
          detailChanges.manualdiscountamount = {
            from: previousQuoteDetail.manualdiscountamount,
            to: quoteDetail.manualdiscountamount,
          }
          hasChanges = true
        }

        // Compare cost
        const newCost = new Decimal(quoteDetail.msdyn_costpriceperunit || 0)
        const previousCost = new Decimal(previousQuoteDetail.msdyn_costpriceperunit || 0)
        if (!newCost.eq(previousCost)) {
          detailChanges.msdyn_costpriceperunit = {
            from: previousCost.toNumber(),
            to: newCost.toNumber(),
            variance: newCost.minus(previousCost).toNumber(),
          }
          hasChanges = true
        }

        // Compare taxable
        if (quoteDetail.msdyn_taxable !== previousQuoteDetail.msdyn_taxable) {
          detailChanges.msdyn_taxable = {
            from: previousQuoteDetail.msdyn_taxable,
            to: quoteDetail.msdyn_taxable,
          }
          hasChanges = true
        }

        // Compare CPQ fields
        const newCPQ = quoteDetail.dsy_cpqfields
        const oldCPQ = previousQuoteDetail.dsy_cpqfields

        if (newCPQ.dsy_laborfactorpercent !== oldCPQ.dsy_laborfactorpercent) {
          detailChanges.dsy_laborfactorpercent = {
            from: oldCPQ.dsy_laborfactorpercent,
            to: newCPQ.dsy_laborfactorpercent,
          }
          hasChanges = true
        }

        // Room
        if (newCPQ._dsy_room_value !== oldCPQ._dsy_room_value) {
          detailChanges.room = {
            from: oldCPQ._dsy_room_value,
            to: newCPQ._dsy_room_value,
          }
          hasChanges = true
        }

        // System
        if (newCPQ.dsy_system !== oldCPQ.dsy_system) {
          detailChanges.system = {
            from: oldCPQ.dsy_system,
            to: newCPQ.dsy_system,
          }
          hasChanges = true
        }

        // Phase
        if (newCPQ.dsy_phase !== oldCPQ.dsy_phase) {
          detailChanges.phase = {
            from: oldCPQ.dsy_phase,
            to: newCPQ.dsy_phase,
          }
          hasChanges = true
        }

        // Location
        if (newCPQ.dsy_location !== oldCPQ.dsy_location) {
          detailChanges.location = {
            from: oldCPQ.dsy_location,
            to: newCPQ.dsy_location,
          }
          hasChanges = true
        }

        // Labor Hours
        const laborChanges: LaborHoursChanges = {}
        let hasLaborChanges = false

        if ((newCPQ.dsy_engineeringlabor || 0) !== (oldCPQ.dsy_engineeringlabor || 0)) {
          laborChanges.engineering = {
            from: oldCPQ.dsy_engineeringlabor,
            to: newCPQ.dsy_engineeringlabor,
          }
          hasLaborChanges = true
        }

        if ((newCPQ.dsy_installationlabor || 0) !== (oldCPQ.dsy_installationlabor || 0)) {
          laborChanges.installation = {
            from: oldCPQ.dsy_installationlabor,
            to: newCPQ.dsy_installationlabor,
          }
          hasLaborChanges = true
        }

        if ((newCPQ.dsy_roughinlabor || 0) !== (oldCPQ.dsy_roughinlabor || 0)) {
          laborChanges.roughin = {
            from: oldCPQ.dsy_roughinlabor,
            to: newCPQ.dsy_roughinlabor,
          }
          hasLaborChanges = true
        }

        if ((newCPQ.dsy_programminglabor || 0) !== (oldCPQ.dsy_programminglabor || 0)) {
          laborChanges.programming = {
            from: oldCPQ.dsy_programminglabor,
            to: newCPQ.dsy_programminglabor,
          }
          hasLaborChanges = true
        }

        if ((newCPQ.dsy_projectmanagementlabor || 0) !== (oldCPQ.dsy_projectmanagementlabor || 0)) {
          laborChanges.projectManagement = {
            from: oldCPQ.dsy_projectmanagementlabor,
            to: newCPQ.dsy_projectmanagementlabor,
          }
          hasLaborChanges = true
        }

        if ((newCPQ.dsy_servicelabor || 0) !== (oldCPQ.dsy_servicelabor || 0)) {
          laborChanges.service = {
            from: oldCPQ.dsy_servicelabor,
            to: newCPQ.dsy_servicelabor,
          }
          hasLaborChanges = true
        }

        if (hasLaborChanges) {
          detailChanges.laborHours = laborChanges
          hasChanges = true
        }

        if (hasChanges) {
          changes.push({
            type: 'changed',
            newQuoteDetail: {
              ...quoteDetail,
              phaseLabel: getPhaseLabel(quoteDetail.dsy_cpqfields.dsy_phase),
              systemLabel: getSystemLabel(quoteDetail.dsy_cpqfields.dsy_system),
              roomLabel: getRoomLabel(quoteDetail.dsy_cpqfields._dsy_room_value),
            },
            previousQuoteDetail: {
              ...previousQuoteDetail,
              phaseLabel: getPhaseLabel(previousQuoteDetail.dsy_cpqfields.dsy_phase),
              systemLabel: getSystemLabel(previousQuoteDetail.dsy_cpqfields.dsy_system),
              roomLabel: getRoomLabel(previousQuoteDetail.dsy_cpqfields._dsy_room_value),
            },
            changes: detailChanges,
          })
        }
      })

      // Find deleted items
      previousQuoteDetails.forEach((previousQuoteDetail) => {
        if (!quoteDetailsMap.has(previousQuoteDetail.dsy_cpqfields.dsy_globalid)) {
          changes.push({
            type: 'deleted',
            previousQuoteDetail: {
              ...previousQuoteDetail,
              phaseLabel: getPhaseLabel(previousQuoteDetail.dsy_cpqfields.dsy_phase),
              systemLabel: getSystemLabel(previousQuoteDetail.dsy_cpqfields.dsy_system),
              roomLabel: getRoomLabel(previousQuoteDetail.dsy_cpqfields._dsy_room_value),
            },
          })
        }
      })

      return changes
    },
  })

  return useMemo(() => ({ data: data ?? [], dataUpdatedAt }), [data, dataUpdatedAt])
}

export function useChangesSummary<T = QuoteDetailsSummary>(
  filters?: QuoteDetailFilters | null,
  select?: (summary: QuoteDetailsSummary) => T
) {
  const { data: quote, dataUpdatedAt: quoteUpdatedAt } = useQuoteQuery()
  const {
    data: currentSummary,
    dataUpdatedAt: currentSummaryUpdatedAt,
    roomIds: groupRoomIds,
  } = useQuoteDetailsSummary({
    ...filters,
    previousQuote: false,
  })
  const { data: previousSummary, dataUpdatedAt: previousSummaryUpdatedAt } = useQuoteDetailsSummary({
    ...filters,
    previousQuote: true,
  })
  const summariesLoaded = !!quote && currentSummaryUpdatedAt > 0 && previousSummaryUpdatedAt > 0

  const { data, dataUpdatedAt } = useQuery({
    queryKey: [
      ...QUERY_KEYS.quoteDetailsChangesSummary(
        filters,
        currentSummaryUpdatedAt,
        previousSummaryUpdatedAt,
        groupRoomIds
      ),
      quote?.quoteid,
      quoteUpdatedAt,
      quote,
    ],
    enabled: summariesLoaded,
    queryFn: () => {
      if (!summariesLoaded || !currentSummary || !previousSummary) {
        throw new Error('Current summary or previous summary not found')
      }

      const summary = {
        product: {
          quantity: new Decimal(currentSummary.product.quantity)
            .sub(previousSummary.product.quantity)
            .toDecimalPlaces(0)
            .toNumber(),
          cost: new Decimal(currentSummary.product.cost)
            .sub(previousSummary.product.cost)
            .toDecimalPlaces(2)
            .toNumber(),
          price: new Decimal(currentSummary.product.price)
            .sub(previousSummary.product.price)
            .toDecimalPlaces(2)
            .toNumber(),
          priceWithDiscounts: new Decimal(currentSummary.product.priceWithDiscounts)
            .sub(previousSummary.product.priceWithDiscounts)
            .toDecimalPlaces(2)
            .toNumber(),
          margin: getMarginPercentage(
            new Decimal(currentSummary.product.priceWithDiscounts).sub(previousSummary.product.priceWithDiscounts),
            new Decimal(currentSummary.product.cost).sub(previousSummary.product.cost)
          )
            .toDecimalPlaces(2)
            .toNumber(),
          subtotalPercentage: new Decimal(currentSummary.product.subtotalPercentage)
            .sub(previousSummary.product.subtotalPercentage)
            .toDecimalPlaces(2)
            .toNumber(),
          tax: new Decimal(currentSummary.product.tax).sub(previousSummary.product.tax).toDecimalPlaces(2).toNumber(),
        },
        labor: {
          hours: new Decimal(currentSummary.labor.hours).sub(previousSummary.labor.hours).toDecimalPlaces(5).toNumber(),
          price: new Decimal(currentSummary.labor.price).sub(previousSummary.labor.price).toDecimalPlaces(2).toNumber(),
          priceWithDiscounts: new Decimal(currentSummary.labor.priceWithDiscounts)
            .sub(previousSummary.labor.priceWithDiscounts)
            .toDecimalPlaces(2)
            .toNumber(),
          cost: new Decimal(currentSummary.labor.cost).sub(previousSummary.labor.cost).toDecimalPlaces(2).toNumber(),
          margin: getMarginPercentage(
            new Decimal(currentSummary.labor.priceWithDiscounts).sub(previousSummary.labor.priceWithDiscounts),
            new Decimal(currentSummary.labor.cost).sub(previousSummary.labor.cost)
          )
            .toDecimalPlaces(2)
            .toNumber(),
          subtotalPercentage: new Decimal(currentSummary.labor.subtotalPercentage)
            .sub(previousSummary.labor.subtotalPercentage)
            .toDecimalPlaces(2)
            .toNumber(),
          tax: new Decimal(currentSummary.labor.tax).sub(previousSummary.labor.tax).toDecimalPlaces(2).toNumber(),
        },
        supplies: {
          price: new Decimal(currentSummary.supplies.price)
            .sub(previousSummary.supplies.price)
            .toDecimalPlaces(2)
            .toNumber(),
          cost: new Decimal(currentSummary.supplies.cost)
            .sub(previousSummary.supplies.cost)
            .toDecimalPlaces(2)
            .toNumber(),
          margin: getMarginPercentage(
            new Decimal(currentSummary.supplies.price).sub(previousSummary.supplies.price),
            new Decimal(currentSummary.supplies.cost).sub(previousSummary.supplies.cost)
          )
            .toDecimalPlaces(2)
            .toNumber(),
          priceRate: new Decimal(currentSummary.supplies.priceRate)
            .sub(previousSummary.supplies.priceRate)
            .toDecimalPlaces(8)
            .toNumber(),
          costRate: new Decimal(currentSummary.supplies.costRate)
            .sub(previousSummary.supplies.costRate)
            .toDecimalPlaces(8)
            .toNumber(),
          subtotalPercentage: new Decimal(currentSummary.supplies.subtotalPercentage)
            .sub(previousSummary.supplies.subtotalPercentage)
            .toDecimalPlaces(2)
            .toNumber(),
          tax: new Decimal(currentSummary.supplies.tax).sub(previousSummary.supplies.tax).toDecimalPlaces(2).toNumber(),
        },
        tax: {
          price: new Decimal(currentSummary.tax.price).sub(previousSummary.tax.price).toDecimalPlaces(2).toNumber(),
          rate: new Decimal(currentSummary.tax.rate).sub(previousSummary.tax.rate).toDecimalPlaces(5).toNumber(),
        },
        discounts: {
          products: new Decimal(currentSummary.discounts.products)
            .sub(previousSummary.discounts.products)
            .toDecimalPlaces(2)
            .toNumber(),
          productsTax: new Decimal(currentSummary.discounts.productsTax)
            .sub(previousSummary.discounts.productsTax)
            .toDecimalPlaces(2)
            .toNumber(),
          productsTaxable: new Decimal(currentSummary.discounts.productsTaxable)
            .sub(previousSummary.discounts.productsTaxable)
            .toDecimalPlaces(2)
            .toNumber(),
          productsNonTax: new Decimal(currentSummary.discounts.productsNonTax)
            .sub(previousSummary.discounts.productsNonTax)
            .toDecimalPlaces(2)
            .toNumber(),
          labor: new Decimal(currentSummary.discounts.labor)
            .sub(previousSummary.discounts.labor)
            .toDecimalPlaces(2)
            .toNumber(),
          laborTax: new Decimal(currentSummary.discounts.laborTax)
            .sub(previousSummary.discounts.laborTax)
            .toDecimalPlaces(2)
            .toNumber(),
          laborTaxable: new Decimal(currentSummary.discounts.laborTaxable)
            .sub(previousSummary.discounts.laborTaxable)
            .toDecimalPlaces(2)
            .toNumber(),
          laborNonTax: new Decimal(currentSummary.discounts.laborNonTax)
            .sub(previousSummary.discounts.laborNonTax)
            .toDecimalPlaces(2)
            .toNumber(),
          customerLoyalty: new Decimal(currentSummary.discounts.customerLoyalty)
            .sub(previousSummary.discounts.customerLoyalty)
            .toDecimalPlaces(2)
            .toNumber(),
          tax: new Decimal(currentSummary.discounts.tax)
            .sub(previousSummary.discounts.tax)
            .toDecimalPlaces(2)
            .toNumber(),
          total: new Decimal(currentSummary.discounts.total)
            .sub(previousSummary.discounts.total)
            .toDecimalPlaces(2)
            .toNumber(),
          productsFixed: new Decimal(currentSummary.discounts.productsFixed)
            .sub(previousSummary.discounts.productsFixed)
            .toDecimalPlaces(2)
            .toNumber(),
          laborFixed: new Decimal(currentSummary.discounts.laborFixed)
            .sub(previousSummary.discounts.laborFixed)
            .toDecimalPlaces(2)
            .toNumber(),
          totalFixed: new Decimal(currentSummary.discounts.totalFixed)
            .sub(previousSummary.discounts.totalFixed)
            .toDecimalPlaces(2)
            .toNumber(),
        },
        subtotal: new Decimal(currentSummary.subtotal).sub(previousSummary.subtotal).toDecimalPlaces(2).toNumber(),
        subtotalWithSupplies: new Decimal(currentSummary.subtotalWithSupplies)
          .sub(previousSummary.subtotalWithSupplies)
          .toDecimalPlaces(2)
          .toNumber(),
        subtotalWithSuppliesAndDiscounts: new Decimal(currentSummary.subtotalWithSuppliesAndDiscounts)
          .sub(previousSummary.subtotalWithSuppliesAndDiscounts)
          .toDecimalPlaces(2)
          .toNumber(),
        storedSubtotalAdjustment: new Decimal(currentSummary.storedSubtotalAdjustment)
          .sub(previousSummary.storedSubtotalAdjustment)
          .toDecimalPlaces(2)
          .toNumber(),
        total: new Decimal(currentSummary.total).sub(previousSummary.total).toDecimalPlaces(2).toNumber(),
        totalCost: new Decimal(currentSummary.totalCost).sub(previousSummary.totalCost).toDecimalPlaces(2).toNumber(),
        totalMargin: new Decimal(currentSummary.totalMargin)
          .sub(previousSummary.totalMargin)
          .toDecimalPlaces(2)
          .toNumber(),
        grossProfitPerHour: new Decimal(currentSummary.grossProfitPerHour)
          .sub(previousSummary.grossProfitPerHour)
          .toDecimalPlaces(2)
          .toNumber(),
      }
      return quote && !hasActiveSummaryFilter(filters)
        ? applyStoredChangeOrderTotals({ quote, summary })
        : summary
    },
  })

  return useMemo(() => {
    const ensuredData = summariesLoaded ? (data ?? INITIAL_SUMMARY) : INITIAL_SUMMARY
    const selectedData = select ? select(ensuredData) : ensuredData
    return { data: selectedData as T, dataUpdatedAt: summariesLoaded ? dataUpdatedAt : 0 }
  }, [data, dataUpdatedAt, select, summariesLoaded])
}

// Max OR clauses per readback $filter — keeps the request URL under length
// limits (mirrors the inventory availability fetch's batching).
const GLOBAL_ID_READBACK_BATCH_SIZE = 15

// Read back the server-generated dsy_globalid for freshly created quotedetails.
// Bulk-insert flows (paste, apply template) need the new parents' global ids to
// re-point pasted children, and one GET per created row is an N+1 — batch the
// ids into a handful of filtered list queries instead.
export async function getCreatedGlobalIdsByQuoteDetailId(quoteDetailIds: string[]) {
  const ids = quoteDetailIds.filter(Boolean)
  const batches: string[][] = []
  for (let i = 0; i < ids.length; i += GLOBAL_ID_READBACK_BATCH_SIZE) {
    batches.push(ids.slice(i, i + GLOBAL_ID_READBACK_BATCH_SIZE))
  }

  const results = await Promise.all(
    batches.map((batch) =>
      getMultipleRecords<{ quotedetailid: string; dsy_cpqfields: { dsy_globalid: string } | null }>(
        'quotedetails',
        `?$select=quotedetailid&$expand=dsy_cpqfields($select=dsy_globalid)` +
          `&$filter=${batch.map((id) => `quotedetailid eq '${id}'`).join(' or ')}`
      )
    )
  )

  const globalIdByQuoteDetailId = new Map<string, string>()
  for (const row of results.flat()) {
    const globalId = row.dsy_cpqfields?.dsy_globalid
    if (globalId) globalIdByQuoteDetailId.set(row.quotedetailid, globalId)
  }
  return globalIdByQuoteDetailId
}

/**
 * Names a paste has to write back once its rows exist.
 *
 * The platform stamps quotedetailname from the bound product on create, so the
 * name the create body sends is thrown away: every ad-hoc package header pastes
 * back reading "PACKAGE" (the reserved product's name) and a renamed catalog
 * header loses its name. Package headers are the rows whose name is authored
 * rather than the product's, so they are the rows patched — the same
 * PATCH-after-create useGroupIntoPackage already makes for the same reason.
 *
 * Indexes line up: createdQuoteDetailIds comes from a Promise.all over `items`.
 */
export function getPastedPackageHeaderNames(
  items: QuoteDetailData[],
  createdQuoteDetailIds: string[]
): { quoteDetailId: string; quotedetailname: string }[] {
  return items.flatMap((detail, index) => {
    const quoteDetailId = createdQuoteDetailIds[index]
    if (!quoteDetailId || !isPackageHeader(detail.productid) || !detail.quotedetailname) return []
    return [{ quoteDetailId, quotedetailname: detail.quotedetailname }]
  })
}

export async function pasteItemsToRoom(roomId: string, copiedQuoteDetails: QuoteDetailData[]) {
  const quoteId = getQuoteId()
  const productIds = [...new Set(copiedQuoteDetails.map((item) => z.string().uuid().parse(item.productid.productid)))]
  const currentNames = new Map<string, string>()
  for (let offset = 0; offset < productIds.length; offset += 50) {
    const filter = productIds.slice(offset, offset + 50).map((id) => `productid eq ${id}`).join(' or ')
    const products = z.array(z.object({ productid: z.string(), name: z.string().min(1) })).parse(
      await getMultipleRecords('products', `?$select=productid,name&$filter=${filter}`)
    )
    for (const product of products) currentNames.set(product.productid.toLowerCase(), product.name)
  }
  copiedQuoteDetails = copiedQuoteDetails.map((item) => {
    const name = currentNames.get(item.productid.productid.toLowerCase())
    if (!name) throw new Error(`Copied product ${item.productid.productid} is unavailable.`)
    return { ...item, productid: { ...item.productid, name } }
  })

  // Three waves, because every pasted row gets a fresh dsy_globalid and the two
  // link keys can only be remapped once their target exists:
  //   1. headers + plain rows, 2. package members (remapped dsy_packageglobalid),
  //   3. accessories (remapped dsy_parentglobalid, parent may be from wave 1 or 2).
  // Dangling-membership guard: a package key pointing outside the copied set is
  // nulled and the row pastes as a plain top-level row.
  const copiedGlobalIds = new Set(copiedQuoteDetails.map((detail) => detail.dsy_cpqfields.dsy_globalid).filter(Boolean))
  const isCopiedMember = (detail: QuoteDetailData) => {
    const packageGlobalId = detail.dsy_cpqfields.dsy_packageglobalid
    return !detail.dsy_cpqfields.dsy_parentglobalid && !!packageGlobalId && copiedGlobalIds.has(packageGlobalId)
  }
  const parentItems = copiedQuoteDetails.filter(
    (detail) => !detail.dsy_cpqfields.dsy_parentglobalid && !isCopiedMember(detail)
  )
  const memberItems = copiedQuoteDetails.filter(isCopiedMember)
  const childItems = copiedQuoteDetails.filter((detail) => detail.dsy_cpqfields.dsy_parentglobalid)

  const targetCache = queryClient.getQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails())
  const quoteDetailMap = new Map<string, string>()

  // Assign incrementing dsy_grouporder slots to source-ordered siblings, starting
  // from the bucket's current max in the target room.
  const assignGroupOrders = (
    items: QuoteDetailData[],
    bucketKey: (detail: QuoteDetailData) => string,
    bucketOf: (detail: QuoteDetailData) => InsertBucket
  ) => {
    const groupOrderByGlobalId = new Map<string, number>()
    const bucketStart = new Map<string, number>()
    const bucketCount = new Map<string, number>()
    for (const detail of [...items].sort(
      (a, b) => (a.dsy_cpqfields.dsy_grouporder ?? 0) - (b.dsy_cpqfields.dsy_grouporder ?? 0)
    )) {
      const key = bucketKey(detail)
      if (!bucketStart.has(key)) {
        bucketStart.set(key, getEndOfGroupOrder(targetCache, bucketOf(detail)))
        bucketCount.set(key, 0)
      }
      const start = bucketStart.get(key)!
      const count = bucketCount.get(key)!
      groupOrderByGlobalId.set(detail.dsy_cpqfields.dsy_globalid, start + count)
      bucketCount.set(key, count + 1)
    }
    return groupOrderByGlobalId
  }

  // Top-level rows: headers, plain rows (wave 1) and package members (wave 2)
  // share one body shape — only the package key and the bucket differ.
  const createTopLevelRow = (
    detail: QuoteDetailData,
    groupOrder: number | null,
    packageGlobalId: string | null
  ) => {
    const cpqFieldsBody: UpsertCPQFieldsBody = {
      dsy_system: detail.dsy_cpqfields.dsy_system,
      dsy_phase: detail.dsy_cpqfields.dsy_phase,
      dsy_scopetag: detail.dsy_cpqfields.dsy_scopetag,
      dsy_grouporder: groupOrder,
      dsy_location: detail.dsy_cpqfields.dsy_location,
      dsy_engineeringlabor: detail.dsy_cpqfields.dsy_engineeringlabor,
      dsy_installationlabor: detail.dsy_cpqfields.dsy_installationlabor,
      dsy_roughinlabor: detail.dsy_cpqfields.dsy_roughinlabor,
      dsy_programminglabor: detail.dsy_cpqfields.dsy_programminglabor,
      dsy_projectmanagementlabor: detail.dsy_cpqfields.dsy_projectmanagementlabor,
      dsy_servicelabor: detail.dsy_cpqfields.dsy_servicelabor,
      dsy_laborfactorpercent: detail.dsy_cpqfields.dsy_laborfactorpercent,
      'dsy_room@odata.bind': `/dsy_accountrooms(${roomId})`,
    }
    // Never write both link keys: the server-side XOR plugin rejects such a row.
    if (packageGlobalId) cpqFieldsBody.dsy_packageglobalid = packageGlobalId

    const quantityDecimal = new Decimal(detail.quantity)
    const unitPriceDecimal = new Decimal(detail.priceperunit ?? 0)
    const unitCostDecimal = new Decimal(detail.msdyn_costpriceperunit ?? 0)

    const quoteDetailBody: UpsertQuoteDetailBody = {
      quantity: quantityDecimal.toNumber(),
      msdyn_costpriceperunit: unitCostDecimal.toNumber(),
      msdyn_costamount: quantityDecimal.mul(unitCostDecimal).toNumber(),
      priceperunit: unitPriceDecimal.toNumber(),
      baseamount: quantityDecimal.mul(unitPriceDecimal).toNumber(),
      quotedetailname: isPackageHeader(detail.productid) ? detail.quotedetailname : detail.productid.name,
      description: detail.description,
      tax: detail.tax,
      msdyn_taxable: detail.msdyn_taxable,
      'quoteid@odata.bind': `/quotes(${quoteId})`,
      'productid@odata.bind': `/products(${detail.productid.productid})`,
      'uomid@odata.bind': `/uoms(${detail.productid._defaultuomid_value})`,
    }

    return createQuoteDetail({ cpqFieldsBody, quoteDetailBody, productName: detail.productid.name })
  }

  // Wave 1 — headers and plain rows. Bucket = (room, system, scope, no package, no parent).
  const parentGroupOrderByGlobalId = assignGroupOrders(
    parentItems,
    (detail) => `${detail.dsy_cpqfields.dsy_system}:${detail.dsy_cpqfields.dsy_scopetag}`,
    (detail) => ({
      roomId,
      systemId: detail.dsy_cpqfields.dsy_system,
      scopeTag: detail.dsy_cpqfields.dsy_scopetag,
      packageGlobalId: null,
      parentGlobalId: null,
    })
  )

  const parentResults = await Promise.all(
    parentItems.map((detail) =>
      createTopLevelRow(detail, parentGroupOrderByGlobalId.get(detail.dsy_cpqfields.dsy_globalid) ?? null, null)
    )
  )

  // Package headers carry an authored name the create-time stamp overwrote —
  // put it back before anything else reads the pasted rows.
  await Promise.all(
    getPastedPackageHeaderNames(parentItems, parentResults).map(({ quoteDetailId, quotedetailname }) =>
      updateRecord('quotedetails', quoteDetailId, { quotedetailname })
    )
  )

  // One batched readback for every new parent instead of a GET per row —
  // Promise.all preserves order, so index i is parentItems[i]'s new row.
  const newGlobalIdByQuoteDetailId = await getCreatedGlobalIdsByQuoteDetailId(parentResults)
  parentItems.forEach((detail, index) => {
    const newGlobalId = newGlobalIdByQuoteDetailId.get(parentResults[index])
    if (detail.dsy_cpqfields.dsy_globalid && newGlobalId) {
      quoteDetailMap.set(detail.dsy_cpqfields.dsy_globalid, newGlobalId)
    }
  })

  // Wave 2 — package members, remapped onto the new header ids. The bucket is the
  // new package id alone: a header owns one slot space for all of its members, so
  // members that span systems or scopes must not restart numbering per scope.
  const newPackageGlobalId = (detail: QuoteDetailData) =>
    quoteDetailMap.get(detail.dsy_cpqfields.dsy_packageglobalid!) ?? null
  const memberGroupOrderByGlobalId = assignGroupOrders(
    memberItems,
    (detail) => `pkg:${newPackageGlobalId(detail) ?? ''}`,
    (detail) => ({
      roomId,
      systemId: detail.dsy_cpqfields.dsy_system,
      scopeTag: detail.dsy_cpqfields.dsy_scopetag,
      packageGlobalId: newPackageGlobalId(detail),
      parentGlobalId: null,
    })
  )

  const memberResults = await Promise.all(
    memberItems.map((detail) =>
      createTopLevelRow(
        detail,
        memberGroupOrderByGlobalId.get(detail.dsy_cpqfields.dsy_globalid) ?? null,
        newPackageGlobalId(detail)
      )
    )
  )

  // Package-free paste: no members, so skip the readback rather than issuing an
  // empty one on every ordinary paste.
  if (memberItems.length) {
    const newGlobalIdByMemberQuoteDetailId = await getCreatedGlobalIdsByQuoteDetailId(memberResults)
    memberItems.forEach((detail, index) => {
      const newGlobalId = newGlobalIdByMemberQuoteDetailId.get(memberResults[index])
      if (detail.dsy_cpqfields.dsy_globalid && newGlobalId) {
        quoteDetailMap.set(detail.dsy_cpqfields.dsy_globalid, newGlobalId)
      }
    })
  }

  // Wave 3 — accessories. Bucket includes the new parent global id (which may be
  // a wave-1 or a wave-2 row) since every pasted row gets a fresh id.
  const newParentGlobalId = (detail: QuoteDetailData) => {
    const parentAccessoryId = detail.dsy_cpqfields.dsy_parentglobalid
    return (parentAccessoryId ? quoteDetailMap.get(parentAccessoryId) : undefined) ?? null
  }
  const childGroupOrderByGlobalId = assignGroupOrders(
    childItems,
    (detail) => `${detail.dsy_cpqfields.dsy_system}:${detail.dsy_cpqfields.dsy_scopetag}:${newParentGlobalId(detail) ?? ''}`,
    (detail) => ({
      roomId,
      systemId: detail.dsy_cpqfields.dsy_system,
      scopeTag: detail.dsy_cpqfields.dsy_scopetag,
      packageGlobalId: null,
      parentGlobalId: newParentGlobalId(detail),
    })
  )

  const childResults = await Promise.all(
    childItems.map(async (detail) => {
      const parentAccessoryId = detail.dsy_cpqfields.dsy_parentglobalid
      const newParentAccessoryId = parentAccessoryId ? quoteDetailMap.get(parentAccessoryId) : undefined

      const cpqFieldsBody: UpsertCPQFieldsBody = {
        dsy_system: detail.dsy_cpqfields.dsy_system,
        dsy_phase: detail.dsy_cpqfields.dsy_phase,
        dsy_scopetag: detail.dsy_cpqfields.dsy_scopetag,
        dsy_grouporder: childGroupOrderByGlobalId.get(detail.dsy_cpqfields.dsy_globalid) ?? null,
        dsy_location: detail.dsy_cpqfields.dsy_location,
        dsy_engineeringlabor: detail.dsy_cpqfields.dsy_engineeringlabor,
        dsy_installationlabor: detail.dsy_cpqfields.dsy_installationlabor,
        dsy_roughinlabor: detail.dsy_cpqfields.dsy_roughinlabor,
        dsy_programminglabor: detail.dsy_cpqfields.dsy_programminglabor,
        dsy_projectmanagementlabor: detail.dsy_cpqfields.dsy_projectmanagementlabor,
        dsy_servicelabor: detail.dsy_cpqfields.dsy_servicelabor,
        dsy_laborfactorpercent: detail.dsy_cpqfields.dsy_laborfactorpercent,
        'dsy_room@odata.bind': `/dsy_accountrooms(${roomId})`,
        dsy_parentglobalid: newParentAccessoryId,
      }

      const quantityDecimal = new Decimal(detail.quantity)
      const unitPriceDecimal = new Decimal(detail.priceperunit ?? 0)
      const unitCostDecimal = new Decimal(detail.msdyn_costpriceperunit ?? 0)

      const quoteDetailBody: UpsertQuoteDetailBody = {
        quantity: quantityDecimal.toDecimalPlaces(0).toNumber(),
        msdyn_costpriceperunit: unitCostDecimal.toDecimalPlaces(2).toNumber(),
        msdyn_costamount: quantityDecimal.mul(unitCostDecimal).toDecimalPlaces(2).toNumber(),
        priceperunit: unitPriceDecimal.toDecimalPlaces(2).toNumber(),
        baseamount: quantityDecimal.mul(unitPriceDecimal).toDecimalPlaces(2).toNumber(),
        quotedetailname: isPackageHeader(detail.productid) ? detail.quotedetailname : detail.productid.name,
        description: detail.description,
        tax: detail.tax,
        msdyn_taxable: detail.msdyn_taxable,
        'quoteid@odata.bind': `/quotes(${quoteId})`,
        'productid@odata.bind': `/products(${detail.productid.productid})`,
        'uomid@odata.bind': `/uoms(${detail.productid._defaultuomid_value})`,
      }

      return createQuoteDetail({
        productName: detail.productid.name,
        cpqFieldsBody,
        quoteDetailBody,
      })
    })
  )

  return parentResults.concat(memberResults, childResults)
}
